import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, HttpError, FetchTimeoutError, setAuthToken, clearAuthToken } from "./api";
import { parsePauseReasonInput } from "./pauseReason";
import { Icon, Modal, Panel, Empty, Tabs, fmtTokenCount } from "./components";
import {
  AccountsPanel,
  ActivityFeed,
  AgentsPanel,
  AgentTypesPanel,
  BacklogIntegrityPanel,
  BacklogSummary,
  BlockedPanel,
  categoryTypesCsv,
  COLLISION_EVENT_REFUSED,
  COLLISION_EVENT_REMINTED,
  criticalHealth,
  DONE_ACTIVITY_TYPES_CSV,
  DONE_FAILED_TYPES,
  activityLabel,
  activitySummary,
  EscalationsPanel,
  PrerequisitesPanel,
  HealthStrip,
  KpiRow,
  LayoutResizeHandle,
  RolesPanel,
  ScheduledDispatchPanel,
  ScheduledJobsPanel,
  setGitRedSustainMs,
  SlotCards,
  SlotTable,
  TopBar,
  TWEAK_DEFAULTS,
  agentWorkBySlot,
  fleetSlots,
  latestDoneOutcomeBySlot,
  planningBoxProfile,
  resolveCentralHostId,
  slotKey,
  slotLabel,
  summarise,
  unresolvedBacklogCollisions,
  useLayoutColumnWidths,
  type ActivityFilter,
  type SlotAction,
  type Tweaks,
} from "./layout";
import { filterSlotsByStage, STAGE_FILTER_OPTIONS, type StageFilter } from "./slotStage";
import { Landing } from "./Landing";
import { PlanningBox } from "./PlanningBox";
import { ClaudeWalletPanel } from "./ClaudeWalletPanel";
import { ConcurrencyCapKnobsPanel } from "./ConcurrencyCapKnobsPanel";
import { ConcurrencyCapsPanel } from "./ConcurrencyCapsPanel";
import { FlatRateBoostPanel } from "./FlatRateBoostPanel";
import { GeminiCapacityPanel } from "./GeminiCapacityPanel";
import { MuseCapacityPanel } from "./MuseCapacityPanel";
import { NvidiaCapacityPanel } from "./NvidiaCapacityPanel";
import { DeepSeekWalletPanel } from "./DeepSeekWalletPanel";
import { Login } from "./Login";
import { EscalationDetail } from "./EscalationDetail";
import { BatchingEfficiencyPanel } from "./BatchingEfficiencyPanel";
import { TaskUsageWindowsPanel, fmtTokenCountOrDash, fmtUsdOrDash, totalTokens } from "./TaskUsageWindows";
import { RoutingShadowComparisonPanel } from "./RoutingShadowComparisonPanel";
import { HostsResourcesTable, VmResourcesStaleBanner, useVmResources } from "./VmResources";
import { ResourceWatchdog } from "./ResourceWatchdog";
import { clampLoopSeconds, durationBetweenMs, fmtAgo, fmtClock, fmtDurationBetween, planBasename } from "./utils";
import type {
  AccountView,
  ActivityEntry,
  AgentRole,
  AgentView,
  AuthedUser,
  BackendConfig,
  BacklogCounts,
  BacklogTaskView,
  BlockedRuling,
  BlockedView,
  ChatMessage,
  DispatchPauseStatusView,
  EscalationView,
  FleetFreezeStatusView,
  FromRole,
  ModeInfo,
  ParkedTaskView,
  RoleView,
  ScheduledDispatchStatusView,
  ScheduledJobRunView,
  Session,
  SlotView,
  StateResponse,
  TaskUsageView,
} from "./types";
import { heldBadgeFromBlockedReason } from "./dispatchHolds";

// Route-level code splitting (ao_control_plane_read_models_and_request_path_2026_09_04
// [UI] P0): these 8 pages are reached only via explicit navigation off Landing/Dashboard
// (never the default "/" or "/vm/<id>" paths), so their code — and, for CiHost/FleetKpis,
// the recharts they pull in — stays out of the initial bundle until the operator actually
// visits one. Named exports, so the dynamic import is adapted to the { default } shape
// React.lazy requires; see Router() below for the matching Suspense boundaries.
const CiGates = React.lazy(() => import("./CiGates").then((m) => ({ default: m.CiGates })));
const CiHost = React.lazy(() => import("./CiHost").then((m) => ({ default: m.CiHost })));
const OpsControls = React.lazy(() => import("./OpsControls").then((m) => ({ default: m.OpsControls })));
const FleetGit = React.lazy(() => import("./FleetGit").then((m) => ({ default: m.FleetGit })));
const FleetKpis = React.lazy(() => import("./FleetKpis").then((m) => ({ default: m.FleetKpis })));
const DocGraph = React.lazy(() => import("./DocGraph").then((m) => ({ default: m.DocGraph })));
const HumanFleet = React.lazy(() => import("./HumanFleet").then((m) => ({ default: m.HumanFleet })));
const BacklogRelations = React.lazy(() => import("./BacklogRelations").then((m) => ({ default: m.BacklogRelations })));
// Not a route: the worker chat modal (with the transcript-tail merge it drives) loads the first time an operator opens
// it, so parity work on it never grows the initial bundle (the gzip ratchet in scripts/check-bundle-size.mjs).
// Dispatch-hold UI (banner + epic hold view, 2026-10-05) loads in its own chunk so the hold feature never grows the initial
// bundle (gzip ratchet); the banner renders nothing until its chunk and first poll arrive, which is fine for a status strip.
const DispatchHoldBanner = React.lazy(() => import("./HoldsUi").then((m) => ({ default: m.DispatchHoldBanner })));
const EpicHoldView = React.lazy(() => import("./HoldsUi").then((m) => ({ default: m.EpicHoldView })));
// Deploy-dashboard modal (with its published-releases dropdown) loads only when opened -- keeps it off the initial bundle.
const DeployDashboardModal = React.lazy(() =>
  import("./DeployDashboardModal").then((m) => ({ default: m.DeployDashboardModal })),
);
// Log viewer modal loads in its own chunk (2026-10-05 offset so the dispatch-hold / deploy-dropdown work nets to zero on the initial bundle).
const LogViewerModal = React.lazy(() => import("./LogViewerModal").then((m) => ({ default: m.LogViewerModal })));
const WorkerChatModal = React.lazy(() => import("./WorkerChatModal").then((m) => ({ default: m.WorkerChatModal })));
// Shared fallback for all 8 — deliberately minimal (these chunks are small and load fast
// off a warm cache; a heavier spinner would flash distractingly for no benefit).
const ROUTE_LOADING_FALLBACK = <div className="task-usage-pending">loading…</div>;

// 1 minute auto-poll. Slower than feels "live" but the state on this
// dashboard mostly settles between worker heartbeats — operator can hit
// the per-panel Refresh buttons (Backlog modal, etc.) when they need a
// fresh read right now.
const POLL_INTERVAL_MS = 60000;
const POLL_INTERVAL_LABEL = "60s";
// How many 60s ticks between the slow safety-net fan-out that runs even while the live
// stream is open (see the stream effect below). 10 => every 10 minutes: panels whose data
// `/api/state` does not carry (accounts, escalations, parked tasks, …) stay honest without
// re-adding the 13-fetch-per-minute fan-out this task exists to remove.
const SAFETY_NET_TICKS = 10;
// Smaller live poll (Plan B): the live feed stays lean — "Load older" pages back
// through history on demand via the `before_id` cursor instead of fetching a big
// page every poll.
const ACTIVITY_LIMIT = 25;
// One server page when paging back through history.
const ACTIVITY_OLDER_PAGE = 50;
// Backlog Integrity panel's own activity pull (see collisionActivity's state
// comment) — generous relative to ACTIVITY_LIMIT since a stuck collision must
// never silently page out of view just because unrelated activity is busy.
const COLLISION_ACTIVITY_LIMIT = 200;
// Fleet "Last done" badge's own activity pull (fleet_wide_deepseek_crash_loop_
// undetected_2026_08_11 follow-up), same reasoning as collisionActivity above:
// latestDoneOutcomeBySlot() used to read the shared, category-filtered `activity`
// state (ACTIVITY_LIMIT=25, fleet-wide, "all" filter by default) — during a busy
// window that 25-row buffer turns over in seconds, so a slot's most recent
// slot_done/rejection/tmux_session_lost aged out of view almost immediately and
// the badge silently blanked for the entire fleet (operator-observed live,
// 2026-08-11). A dedicated, type-filtered pull with real headroom fixes it the
// same way collisionActivity already does for the same class of bug.
const DONE_ACTIVITY_LIMIT = 200;
// ao_dashboard_activity_log_role_vocabulary_gap_2026_08_18 — this list was
// hardcoded to "slot_done" alone (never DONE_PASSED_TYPES), so a slot whose most
// recent completion was an escalation/plan_health job — not a numbered backlog
// task — never fetched that row at all, same class of gap already fixed on the
// main Activity panel's Done tabs (DONE_PASSED_TYPES/DONE_FAILED_TYPES). Fixing
// the fetch alone isn't enough — see latestDoneOutcomeBySlot's own isPassed
// check below for the matching render-layer half.
const DONE_ACTIVITY_TYPES = DONE_ACTIVITY_TYPES_CSV;
// The 13 independent fetches refresh() fans out per tick (audit P13: previously one
// all-or-nothing Promise.all — any single endpoint failing blanked every panel). One key
// per fetch, in the same order refresh() issues them, so a section's last-good data and
// its own error/age can be tracked without the rest of the poll going down with it.
type SectionKey =
  | "state"
  | "activity"
  | "accounts"
  | "agents"
  | "roles"
  | "collisionActivity"
  | "escalations"
  | "scheduledJobRuns"
  | "doneActivity"
  | "scheduledDispatch"
  | "parkedTasks"
  | "dispatchPauseStatus"
  | "fleetFreeze";
interface SectionMeta {
  error: string | null;
  updatedAt: number | null;
}
const SECTION_KEYS: SectionKey[] = [
  "state",
  "activity",
  "accounts",
  "agents",
  "roles",
  "collisionActivity",
  "escalations",
  "scheduledJobRuns",
  "doneActivity",
  "scheduledDispatch",
  "parkedTasks",
  "dispatchPauseStatus",
  "fleetFreeze",
];
const SECTION_LABELS: Record<SectionKey, string> = {
  state: "Fleet state",
  activity: "Activity",
  accounts: "Accounts",
  agents: "Agents",
  roles: "Roles",
  collisionActivity: "Backlog collisions",
  escalations: "Escalations",
  scheduledJobRuns: "Scheduled jobs",
  doneActivity: "Done activity",
  scheduledDispatch: "Scheduled dispatch",
  parkedTasks: "Parked tasks",
  dispatchPauseStatus: "Dispatch pause",
  fleetFreeze: "Fleet freeze",
};
const TWEAKS_KEY = "orch.tweaks";

// The first backend we try when the page loads. Once we have a session, we
// fetch the real backends list from /api/backends and replace this.
//
// Two deploy shapes are supported:
//   - Local dev: vite dev server on :5173, backend uvicorn on :8765 → need
//     :8765 here (the legacy :8026 port is retired — CLAUDE.md § orchestration).
//   - Production: nginx serves dashboard + proxies /api/* to backend on the
//     same origin → use the page origin, no port suffix.
//
// Detection: anything served BY vite dev is dev — `import.meta.env.DEV` (not a
// hardcoded port === "5173" check, which broke the moment anything ran vite on a
// different port: the dashboard's own Playwright e2e suite serves off :5198 so it
// never collides with the operator's live :5173 dev stack, and DEV is Vite's
// standard "am I `vite dev`, not `vite build`" flag — true regardless of which
// port `--port`/`--strictPort` bound). The Firebase-hosted SPA at
// agent-orchestrator[.staging].odum-research.com runs cross-origin from the
// backend EC2 VM at api.agent-orchestrator[.staging].odum-research.com — map the
// SPA hostname to its companion api.* host explicitly. Everything else is assumed
// nginx same-origin behind a reverse proxy (legacy Cloud Run pattern).
const BOOTSTRAP_URL = (() => {
  if (import.meta.env.DEV) {
    // Default to the local dev backend (:8765 — uvicorn's canonical bind;
    // the stale :8026 default sent every fresh local login to a dead port,
    // e2e finding 2026-06-11); override with VITE_BACKEND_PORT to target a
    // separate dev instance without editing source.
    const devPort = (import.meta.env.VITE_BACKEND_PORT as string | undefined) ?? "8765";
    return `${window.location.protocol}//${window.location.hostname}:${devPort}`;
  }
  const host = window.location.hostname;
  if (host === "agent-orchestrator.odum-research.com") {
    return "https://api.agent-orchestrator.odum-research.com";
  }
  if (host === "agent-orchestrator.staging.odum-research.com") {
    return "https://api.agent-orchestrator.staging.odum-research.com";
  }
  return window.location.origin;
})();

// Centralized routing: the central API (BOOTSTRAP_URL) is reached directly;
// every other VM is reached THROUGH the central API's /api/vms/<id>/* proxy.
// So the dashboard never hits a worker VM directly (no mixed-content / CORS) —
// it appends the VM segment to the central origin and the server proxies it
// over the private VPC.
function backendBaseUrl(b: { id: string; url?: string }): string {
  if (!b.url || b.url === BOOTSTRAP_URL || b.id === "self") return BOOTSTRAP_URL;
  return `${BOOTSTRAP_URL}/api/vms/${b.id}`;
}

// ─── localStorage helpers ────────────────────────────────────────────────
//
// The session (JWT + user identity) is deliberately NOT one of these — it lives only
// in React state for the life of the tab (ao_dashboard_jwt_localstorage_ws_query_
// string_unsandboxed_iframe_2026_09_04.md todo 2: a token sitting in localStorage is
// readable by any script that runs same-origin, e.g. via the unsandboxed DocGraph
// iframe this same audit flagged). The dashboard's actual cross-reload session is the
// Secure/HttpOnly/SameSite=Strict cookie `server/auth.py::set_session_cookie` sets on
// login — JS never reads or writes it — so a page reload re-shows the Login screen
// today; a cookie-backed silent restore is a separate, not-yet-built follow-up.

function loadTweaks(): Tweaks {
  try {
    const raw = localStorage.getItem(TWEAKS_KEY);
    if (!raw) return TWEAK_DEFAULTS;
    return { ...TWEAK_DEFAULTS, ...(JSON.parse(raw) as Partial<Tweaks>) };
  } catch {
    return TWEAK_DEFAULTS;
  }
}

function saveTweaks(t: Tweaks): void {
  localStorage.setItem(TWEAKS_KEY, JSON.stringify(t));
}

/** LogViewerModal (scope="agent") only ever reads target.agent_id — every
 * other AgentView field is display-only elsewhere in the app, unused by the
 * modal itself. A ScheduledJobRunView only carries dispatch_agent_id (a bare
 * string, not a full AgentView — the run report never fetches one), so this
 * stub is the minimal, honest way to reuse the SAME log viewer instead of
 * building a second one just for scheduled-job runs. */
function scheduledJobLogStub(agentId: string): AgentView {
  return {
    agent_id: agentId,
    role: "custom",
    label: agentId,
    machine: null,
    model: null,
    rc_url: null,
    rc_session_id: null,
    status: "active",
    online: false,
    last_msg: null,
    context_used_pct: 0,
    registered_at: new Date().toISOString(),
    last_ping: null,
    pending_count: 0,
    needs_operator_count: 0,
    tmux_session: null,
    agent_kind: null,
    lifecycle: null,
    account_id: null,
    provider: null,
    session_input_tokens: null,
    session_output_tokens: null,
    session_cache_creation_input_tokens: null,
    session_cache_read_input_tokens: null,
    session_spend_usd: null,
    session_reasoning_tokens: null,
    finished_at: null,
    exit_reason: null,
    current_task: null,
    source: null,
    claude_session_id: null,
  };
}

// ─── root component ──────────────────────────────────────────────────────

/**
 * Auth shape (centralized model): the operator logs into the ONE central API;
 * the session lives for the tab's lifetime as the Secure/HttpOnly/SameSite=Strict
 * cookie `server/auth.py::set_session_cookie` sets on login (never read or
 * written by this code — the browser attaches it automatically, and api.ts's
 * `http()` sends `credentials: "include"` so that happens on the prod
 * dashboard/api origin split too) PLUS the same JWT held in React state here and
 * attached as `Authorization: Bearer <jwt>` via api.setAuthToken() — belt and
 * braces, and what still authorizes calls proxied to individual VMs via
 * /api/vms/<id>/*. Neither the cookie nor this in-memory copy persists across a
 * reload/remount (ao_dashboard_jwt_localstorage_ws_query_string_unsandboxed_iframe_
 * 2026_09_04.md todo 2 — a token in localStorage is readable by any same-origin
 * script), so `session` always starts `null`. It's re-derived below via
 * `GET /api/auth/session`, which trades the still-valid cookie for a fresh
 * in-memory token — same trust model as login (never written to localStorage),
 * so a reload/deep-link no longer forces the operator back through the Login
 * screen while their cookie is still good (that reload was the actual trigger
 * behind "navigating to the next page asks me to re-auth" — the WS auth
 * carve-out below is unaffected). The server accepts unauthenticated requests
 * on the localhost-direct path today; once ALLOW_ANONYMOUS flips False, one of
 * these two credentials is required.
 */
export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [restoring, setRestoring] = useState(true);

  // One-time silent restore attempt on mount: if the HttpOnly cookie is still
  // valid, this resolves a session with no Login round-trip; a 401 (no/expired
  // cookie) is the ordinary "not logged in" outcome, not an error.
  useEffect(() => {
    let cancelled = false;
    api
      .getSession(BOOTSTRAP_URL)
      .then((res) => {
        if (cancelled) return;
        setSession({
          token: res.token,
          user: res.user,
          backend_id: "self",
          started_at: new Date().toISOString(),
        });
      })
      .catch(() => {
        // No valid cookie — fall through to the Login screen below.
      })
      .finally(() => {
        if (!cancelled) setRestoring(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Sync the api.ts module-global whenever session changes (login, logout, or a
  // future mid-session refresh) — every call in api.ts reads that global, not
  // React state directly.
  useEffect(() => {
    setAuthToken(session?.token ?? null);
  }, [session]);

  if (restoring) {
    return <Empty title="Restoring session…" sub="Checking whether you're still signed in." />;
  }

  if (!session) {
    return (
      <Login
        onAuthed={(s) => {
          setAuthToken(s.token);
          setSession(s);
        }}
        bootstrapUrl={BOOTSTRAP_URL}
      />
    );
  }
  return (
    <Router
      session={session}
      onSignOut={() => {
        clearAuthToken();
        setSession(null);
      }}
    />
  );
}

/**
 * v0.7 Phase 2: light-weight path-based routing between the fleet Landing page
 * and the single-VM Dashboard.
 *   `/`         → Landing (cross-VM overview)
 *   `/vm/<id>`  → Dashboard scoped to that VM
 *   anything else → fall through to Dashboard (back-compat for direct deep links)
 *
 * Uses ``window.history.pushState`` for navigation — react-router-dom would be
 * overkill for two routes. Subscribes to ``popstate`` so the browser back button
 * works.
 */
function Router({ session, onSignOut }: { session: Session; onSignOut: () => void }) {
  // Operator dispatch-hold banner: persistent on EVERY route (ao_epic_hold_respawn_slo_alert_fixes_2026_10_05 § A).
  return (
    <>
      <React.Suspense fallback={null}>
        <DispatchHoldBanner baseUrl={BOOTSTRAP_URL} />
      </React.Suspense>
      <RouterRoutes session={session} onSignOut={onSignOut} />
    </>
  );
}

function RouterRoutes({ session, onSignOut }: { session: Session; onSignOut: () => void }) {
  const [path, setPath] = useState<string>(window.location.pathname);
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback((to: string) => {
    window.history.pushState({}, "", to);
    setPath(to);
  }, []);

  if (path === "/" || path === "") {
    return (
      <Landing
        onNavigateToVm={(vmId) => navigate(`/vm/${vmId}`)}
        onNavigateToFleetGit={() => navigate("/fleet-git")}
        onNavigateToFleetKpis={() => navigate("/fleet-kpis")}
        onNavigateToDocGraph={() => navigate("/doc-graph")}
        onNavigateToHumanFleet={() => navigate("/human-fleet")}
        onNavigateToBacklogRelations={() => navigate("/backlog-relations")}
        onNavigateToCiHost={() => navigate("/ci-host")}
        onNavigateToCiGates={() => navigate("/ci-gates")}
        onNavigateToOpsControls={() => navigate("/ops-controls")}
        defaultBaseUrl={BOOTSTRAP_URL}
        authToken={session.token}
      />
    );
  }
  // `/vm/ci-host` + `/vm/ci-gates` are aliases of the canonical `/ci-host` +
  // `/ci-gates` routes. The CI box IS a host, so `/vm/ci-host` is the URL an
  // operator reaches for — measured 2026-08-31, the "the /ci-host page renders
  // NOTHING" report was against `/vm/ci-host`. Without the alias that path
  // falls through to the `/vm/<id>` deep-link branch at the bottom of this
  // function and renders <Dashboard initialVmId="ci-host">, i.e. the fleet
  // dashboard pointed at a backend id that does not exist — a blank page, no
  // error. Nothing is shadowed by the alias: the CI runner box is not an
  // orchestrator backend (`planning` is the only VM).
  if (path === "/ci-host" || path === "/vm/ci-host") {
    // Self-hosted CI runner host (IONOS Cube) — box-layer resource view
    // (ci_host_saturation_remediation_and_observability_2026_08_30.md Phase 1).
    return (
      <React.Suspense fallback={ROUTE_LOADING_FALLBACK}>
        <CiHost baseUrl={BOOTSTRAP_URL} authToken={session.token} onBack={() => navigate("/")} />
      </React.Suspense>
    );
  }
  if (path === "/ci-gates" || path === "/vm/ci-gates") {
    // Remote-gate canary + fleet-wide pause state per repo — the work-layer companion
    // to /ci-host (ci_host_saturation_remediation_and_observability_2026_08_30.md Phase 1).
    return (
      <React.Suspense fallback={ROUTE_LOADING_FALLBACK}>
        <CiGates baseUrl={BOOTSTRAP_URL} authToken={session.token} onBack={() => navigate("/")} />
      </React.Suspense>
    );
  }
  if (path === "/ops-controls") {
    // Truthful per-surface operations control matrix (ao_process_topology_wave2_
    // role_runners_executor_controls_2026_09_04.md [UI] P0).
    return (
      <React.Suspense fallback={ROUTE_LOADING_FALLBACK}>
        <OpsControls baseUrl={BOOTSTRAP_URL} authToken={session.token} onBack={() => navigate("/")} />
      </React.Suspense>
    );
  }
  if (path === "/fleet-git") {
    // Fleet git-health page (fleet_git_health_orchestrator_2026_06_10.md).
    return (
      <React.Suspense fallback={ROUTE_LOADING_FALLBACK}>
        <FleetGit baseUrl={BOOTSTRAP_URL} authToken={session.token} onBack={() => navigate("/")} />
      </React.Suspense>
    );
  }
  if (path === "/fleet-kpis") {
    // Fleet-efficiency KPIs page (AF-5, ao_fleet_observability_kpis_2026_07_20.md).
    return (
      <React.Suspense fallback={ROUTE_LOADING_FALLBACK}>
        <FleetKpis baseUrl={BOOTSTRAP_URL} authToken={session.token} onBack={() => navigate("/")} />
      </React.Suspense>
    );
  }
  if (path === "/doc-graph") {
    // L0 doc-graph page (infra_satellite_ao_dispatch_batch1_2026_07_26.md [BACKEND] P2).
    return (
      <React.Suspense fallback={ROUTE_LOADING_FALLBACK}>
        <DocGraph baseUrl={BOOTSTRAP_URL} authToken={session.token} onBack={() => navigate("/")} />
      </React.Suspense>
    );
  }
  if (path === "/human-fleet") {
    // Human Fleet page (ao_human_fleet_integration_2026_08_15.md Phase 3).
    return (
      <React.Suspense fallback={ROUTE_LOADING_FALLBACK}>
        <HumanFleet baseUrl={BOOTSTRAP_URL} authToken={session.token} onBack={() => navigate("/")} />
      </React.Suspense>
    );
  }
  if (path === "/backlog-relations") {
    // Backlog-relations view (docs/BACKLOG_RELATIONS_UX_BRIEF.md;
    // ao_residuals_after_dispatch_hardening_2026_07_17.md "Backlog-relations view").
    return (
      <React.Suspense fallback={ROUTE_LOADING_FALLBACK}>
        <BacklogRelations baseUrl={BOOTSTRAP_URL} authToken={session.token} onBack={() => navigate("/")} />
      </React.Suspense>
    );
  }
  if (path.startsWith("/escalation/")) {
    // Scoped single-escalation view (ao_escalation_pipeline_e1_e2_build_2026_10_13.md,
    // todo "Scoped Slack link"): the Slack alert for ONE escalation opens THAT
    // record, not the whole queue. Deliberately singular `/escalation/{id}` (the
    // API's per-id read stays namespaced-plural `/api/escalations/{id}`).
    const escalationId = decodeURIComponent(path.slice("/escalation/".length));
    return (
      <React.Suspense fallback={ROUTE_LOADING_FALLBACK}>
        <EscalationDetail
          baseUrl={BOOTSTRAP_URL}
          authToken={session.token}
          escalationId={escalationId}
          onBack={() => navigate("/")}
        />
      </React.Suspense>
    );
  }
  // /vm/<id> → seed Dashboard's active backend from the URL segment (falls
  // through to session.backend_id when path isn't a /vm/<id> deep link).
  const vmIdFromPath = path.startsWith("/vm/") ? decodeURIComponent(path.slice("/vm/".length)) : undefined;
  return <Dashboard session={session} onSignOut={onSignOut} initialVmId={vmIdFromPath} />;
}

// ─── dashboard ───────────────────────────────────────────────────────────

interface DashboardProps {
  session: Session;
  onSignOut: () => void;
  // Seeds the active backend from a /vm/<id> deep link (App's Router). Falls
  // back to session.backend_id when absent (e.g. legacy deep links / "/").
  initialVmId?: string;
}

function Dashboard({ session, onSignOut, initialVmId }: DashboardProps) {
  // Tweaks (settings)
  const [tweaks, setTweaks] = useState<Tweaks>(loadTweaks);
  const setTweak = useCallback(<K extends keyof Tweaks>(k: K, v: Tweaks[K]) => {
    setTweaks((prev) => {
      const next = { ...prev, [k]: v };
      saveTweaks(next);
      return next;
    });
  }, []);
  useEffect(() => {
    document.documentElement.style.setProperty("--accent", tweaks.accent);
  }, [tweaks.accent]);
  useEffect(() => {
    // Drives body[data-palette="..."] selectors in styles.css, which
    // remap the --tone-<role>-color CSS vars for every panel at once.
    document.body.dataset.palette = tweaks.palette;
  }, [tweaks.palette]);

  // Backends
  const [backends, setBackends] = useState<BackendConfig[]>([
    { id: session.backend_id || "self", label: "this", url: BOOTSTRAP_URL, default: true, status: "ok" },
  ]);
  // Start on the backend the operator actually logged into (session.backend_id).
  // If that id isn't in the loaded list (or it's the legacy "self" sentinel), the
  // backends-load effect below falls back to the `default: true` entry. This is
  // what makes logging into a NON-default backend (e.g. a node host) land THERE
  // instead of jumping to the default (GCP) — which holds no token for this
  // session → 401 → bounce back to login. Operator can still switch backends via
  // the pill dropdown mid-session.
  const [activeId, setActiveId] = useState<string>(initialVmId || session.backend_id || "self");

  const activeBackend = backends.find((b) => b.id === activeId) ?? backends[0]!;
  // Central backend → direct; any other VM → through the central /api/vms/<id> proxy.
  const baseUrl = backendBaseUrl(activeBackend);

  // Live state
  const [state, setState] = useState<StateResponse | null>(null);
  // This backend's own host-resource sampler staleness (2026-09-03, resource_history_sampler_
  // wedged_active_but_not_writing_2026_09_02.md UI todo) — a second, independent
  // /ws/vm-resources socket from the one <VmResources> itself opens inside DesktopLayout,
  // deliberately: it drives a page-level banner (rendered here, alongside ErrorBanner/
  // ActionErrorToast) rather than the tile's own buried-inside-the-panel line, and getting
  // that signal up to THIS level without a second connection would mean threading it through
  // DesktopLayoutProps' ~60-field prop list (and MobileTriage) for one boolean. The endpoint
  // is documented (server/routes/vms.py) to tolerate N concurrent dashboard connections fine.
  // `enabled: state !== null` defers connecting until the dashboard has actually loaded,
  // matching the tile's own effective mount lifecycle.
  const selfVmResources = useVmResources(baseUrl, "/ws/vm-resources", state !== null);
  const [accounts, setAccounts] = useState<AccountView[]>([]);
  const [agents, setAgents] = useState<AgentView[]>([]);
  const [escalations, setEscalations] = useState<EscalationView[]>([]);
  const [scheduledJobRuns, setScheduledJobRuns] = useState<ScheduledJobRunView[]>([]);
  const [scheduledDispatch, setScheduledDispatch] = useState<ScheduledDispatchStatusView | null>(null);
  const [dispatchPauseStatus, setDispatchPauseStatus] = useState<DispatchPauseStatusView | null>(null);
  // Scheduled fleet-wide commit freeze (sit_promote_lease_scheduled_freeze_window_2026_08_29)
  // — HealthStrip banner only, no toggle: armed/cleared by the hourly workflow, not this UI.
  const [fleetFreeze, setFleetFreeze] = useState<FleetFreezeStatusView | null>(null);
  const [parkedTasks, setParkedTasks] = useState<ParkedTaskView[] | null>(null);
  // Read-only role registry (Registry tab). Static-ish — fetched once per backend
  // (and refreshed alongside the main poll); no polling cadence of its own needed.
  const [roles, setRoles] = useState<RoleView[]>([]);
  const [activeRole, setActiveRole] = useState<AgentRole | null>(null);
  const [agentHistory, setAgentHistory] = useState<ChatMessage[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [spawnAgentOpen, setSpawnAgentOpen] = useState(false);
  const [pendingDeleteAgent, setPendingDeleteAgent] = useState<AgentView | null>(null);
  const [pendingEditAgent, setPendingEditAgent] = useState<AgentView | null>(null);
  const [pendingDeleteSlot, setPendingDeleteSlot] = useState<SlotView | null>(null);
  const [logViewerSlot, setLogViewerSlot] = useState<SlotView | null>(null);
  const [logViewerAgent, setLogViewerAgent] = useState<AgentView | null>(null);
  const [chatSlot, setChatSlot] = useState<SlotView | null>(null);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  // Activity feed (Plan B): server-driven category filter + cursor pagination.
  // `olderActivity` accumulates pages fetched via "Load older" (decoupled from
  // the live poll, which only ever replaces `activity`).
  const [activityFilter, setActivityFilter] = useState<ActivityFilter>("all");
  // Latest selected tab, readable from an in-flight refresh() closure: a refresh started under
  // a previous tab must not overwrite the page fetched for the current one (stale-response race).
  const activityFilterRef = useRef<ActivityFilter>(activityFilter);
  activityFilterRef.current = activityFilter;
  const [olderActivity, setOlderActivity] = useState<ActivityEntry[]>([]);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [activityHasMore, setActivityHasMore] = useState(true);
  // Backlog Integrity panel (ao_backlog_collision_alert_and_remediation_ui_2026_07_26
  // todo 4): a DEDICATED fetch, independent of the category-filtered `activity`
  // feed above — the category tabs (all/alerts/ops) can page the collision
  // events out of view entirely, so the panel needs its own always-both-types
  // pull to stay accurate regardless of what the operator has the feed set to.
  const [collisionActivity, setCollisionActivity] = useState<ActivityEntry[]>([]);
  const [fixingCollisionId, setFixingCollisionId] = useState<string | null>(null);
  const [collisionFixError, setCollisionFixError] = useState<string | null>(null);
  // Fleet "Last done" badge — see DONE_ACTIVITY_LIMIT's comment above for why this is a
  // dedicated pull rather than reusing the category-filtered `activity` state.
  const [doneActivity, setDoneActivity] = useState<ActivityEntry[]>([]);
  // AgentTypesPanel (Plan B): show-finished toggle pulls retained terminal rows
  // from a SEPARATE fetch so the live roster + role chat stay clean.
  const [typesShowFinished, setTypesShowFinished] = useState(false);
  const [typesFinishedAgents, setTypesFinishedAgents] = useState<AgentView[]>([]);
  const [modeInfo, setModeInfo] = useState<ModeInfo | null>(null);
  const [connected, setConnected] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Per-section last-good state (audit P13 / ao_control_plane_read_models UI todo): refresh()'s
  // 13-call fan-out now runs under Promise.allSettled, so one endpoint failing no longer blanks
  // every panel -- each section keeps rendering its last successful payload and this tracks, per
  // section, the failure (if any) and when it last actually refreshed, so a stale badge can say
  // WHICH section is behind rather than nuking the whole dashboard to a single ErrorBanner.
  const [sectionMeta, setSectionMeta] = useState<Record<SectionKey, SectionMeta>>(
    () =>
      Object.fromEntries(SECTION_KEYS.map((k) => [k, { error: null, updatedAt: null }])) as Record<
        SectionKey,
        SectionMeta
      >,
  );
  // Action-failure surface (deepseek_account_pause_button_silent_failure_2026_08_05):
  // `error`/setError above feeds the poll-failure ErrorBanner (line ~1025), which is
  // gated on `!connected` by design — it's meant for "we lost the connection", not for
  // "a button click failed while otherwise connected". But every action handler
  // (pause/resume account, clear-rate-limit, pause/resume slot, etc.) reused the SAME
  // setError via the shared setErrorMsg(...) catch-helper, so an action failing while the
  // dashboard stayed connected set `error` and rendered NOTHING — the operator clicked
  // Pause, the request 4xx'd, and the UI looked like a silent no-op. Separate state +
  // its own always-visible toast (ActionErrorToast below) fixes this without touching
  // the connection-loss banner's existing behavior.
  const [actionError, setActionError] = useState<string | null>(null);
  const [resetTyping, setResetTyping] = useState<boolean>(false);
  const [adminBusy, setAdminBusy] = useState<"populate" | "reset" | null>(null);

  // UI state
  // Composite `slotKey(s)` ("<host>:<slot_id>"), not a bare slot_id — slot_id collides
  // across hosts by design (ao_v2_worker_runtime_and_liveness_2026_09_01.md Phase 5).
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [pendingReassign, setPendingReassign] = useState<SlotView | null>(null);
  const [pendingSwitchAccount, setPendingSwitchAccount] = useState<SwitchAccountTarget | null>(null);
  const [pendingSwitchModel, setPendingSwitchModel] = useState<SwitchModelTarget | null>(null);
  const [pendingMessage, setPendingMessage] = useState<SlotView | null>(null);
  const [pendingLoopInterval, setPendingLoopInterval] = useState<SlotView | null>(null);
  const [snapshotOpen, setSnapshotOpen] = useState(false);
  const [deployDashboardOpen, setDeployDashboardOpen] = useState(false);
  // Confirm gate for the TopBar's fleet-wide "Pause All" (operator ask 2026-09-03).
  // PAUSE only — see FleetPauseModal for why Resume All is deliberately NOT gated.
  const [fleetPauseConfirmOpen, setFleetPauseConfirmOpen] = useState(false);
  const [spawnOpen, setSpawnOpen] = useState(false);
  const [reportUsageId, setReportUsageId] = useState<string | null>(null);
  const [refreshingUsageId, setRefreshingUsageId] = useState<string | null>(null);
  const [backlogOpen, setBacklogOpen] = useState(false);
  // ao_fleet_slot_state_reason_tooltips_2026_08_27.md Track H [UI] P2 — a top-blocker
  // chip in the Backlog header tooltip opens Backlog detail pre-targeted at that one
  // task (filter="all" + expanded + scrolled-into-view), instead of just the generic
  // no-target open onOpenBacklog already provides.
  const [pendingDetailTaskId, setPendingDetailTaskId] = useState<string | null>(null);
  const openBacklogForTask = useCallback((taskId: string) => {
    setPendingDetailTaskId(taskId);
    setBacklogOpen(true);
  }, []);
  const [reloadingBacklog, setReloadingBacklog] = useState(false);
  const [parkedOpen, setParkedOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 760);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 760);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Load mode info once at startup; doesn't change without a server restart.
  useEffect(() => {
    api
      .mode(baseUrl)
      .then(setModeInfo)
      .catch(() => setModeInfo(null));
  }, [baseUrl]);

  // Load backends list on mount then repoll each /healthz every 30 s so
  // the offline badge reflects real-time reachability, not just boot state.
  useEffect(() => {
    let cancelled = false;

    const probeAll = async (list: BackendConfig[]) => {
      return Promise.all(
        list.map(async (b) => {
          try {
            // Probe through the central proxy (or direct for the central itself)
            // so a worker VM's reachability is checked over the private VPC.
            await api.healthz(backendBaseUrl(b));
            return { ...b, status: "ok" as const, last_pinged_at: Date.now() };
          } catch {
            // Preserve last_pinged_at so the badge can show "last seen Xmin ago".
            return { ...b, status: "stale" as const };
          }
        }),
      );
    };

    api
      .backends(BOOTSTRAP_URL)
      .then(async (res) => {
        if (cancelled) return;
        // Ping each backend to determine its initial status.
        const probed = await probeAll(res.backends);
        if (cancelled) return;
        setBackends(probed);
        if (!probed.find((b) => b.id === activeId)) {
          const def = probed.find((b) => b.default) ?? probed[0];
          if (def) setActiveId(def.id);
        }
      })
      .catch((e) => {
        setError(`Could not load backends.json: ${e instanceof Error ? e.message : String(e)}`);
      });

    // D19: repoll healthz every 30 s so the offline badge stays fresh.
    const interval = setInterval(async () => {
      if (cancelled) return;
      setBackends((prev) => {
        // Kick off async probes; update state via closure.
        probeAll(prev).then((updated) => {
          if (!cancelled) setBackends(updated);
        });
        return prev; // return unchanged synchronously — real update comes from probeAll
      });
    }, 30_000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll state + activity + accounts + agents from the active backend. The
  // activity fetch carries the selected category's `types=` CSV so the SQL
  // filter runs server-side BEFORE the limit (Plan B).
  const refresh = useCallback(async () => {
    const types = categoryTypesCsv(activityFilter);
    // Promise.allSettled (not Promise.all): one endpoint failing must not blank the
    // other 12 panels (audit P13). Order matches SECTION_KEYS exactly.
    const results = await Promise.allSettled([
      api.state(baseUrl),
      api.activity(baseUrl, { limit: ACTIVITY_LIMIT, types }),
      api.listAccounts(baseUrl),
      api.listAgents(baseUrl),
      api.roles(baseUrl),
      // Both collision event types, unfiltered by the category tab — see the
      // collisionActivity state declaration's comment above for why this must
      // be its own pull rather than reusing `a`.
      api.activity(baseUrl, {
        limit: COLLISION_ACTIVITY_LIMIT,
        types: `${COLLISION_EVENT_REFUSED},${COLLISION_EVENT_REMINTED}`,
      }),
      api.escalations(baseUrl),
      api.scheduledJobRuns(baseUrl),
      api.activity(baseUrl, { limit: DONE_ACTIVITY_LIMIT, types: DONE_ACTIVITY_TYPES }),
      api.scheduledDispatchStatus(baseUrl),
      api.parkedTasks(baseUrl),
      api.dispatchPauseStatus(baseUrl),
      api.fleetFreezeStatus(baseUrl),
    ]);
    const [sR, aR, accsR, agsR, rlsR, collisionsR, escsR, sjRunsR, doneActR, sdStatusR, parkedR, dpStatusR, ffStatusR] =
      results;

    // A 401 on ANY section means the session itself is gone — sign out rather than
    // limping along on 12 "successful" panels against a dead session.
    const unauthorized = results.find(
      (r): r is PromiseRejectedResult =>
        r.status === "rejected" && r.reason instanceof HttpError && r.reason.status === 401,
    );
    if (unauthorized) {
      onSignOut();
      return;
    }

    const now = Date.now();
    setSectionMeta((prev) => {
      const next = { ...prev };
      (
        [
          ["state", sR],
          ["activity", aR],
          ["accounts", accsR],
          ["agents", agsR],
          ["roles", rlsR],
          ["collisionActivity", collisionsR],
          ["escalations", escsR],
          ["scheduledJobRuns", sjRunsR],
          ["doneActivity", doneActR],
          ["scheduledDispatch", sdStatusR],
          ["parkedTasks", parkedR],
          ["dispatchPauseStatus", dpStatusR],
          ["fleetFreeze", ffStatusR],
        ] as [SectionKey, PromiseSettledResult<unknown>][]
      ).forEach(([key, r]) => {
        next[key] =
          r.status === "fulfilled"
            ? { error: null, updatedAt: now }
            : {
                error: r.reason instanceof Error ? r.reason.message : String(r.reason),
                updatedAt: prev[key]?.updatedAt ?? null,
              };
      });
      return next;
    });

    // Each setter fires ONLY on success, so a rejected section's panel keeps rendering
    // exactly the last-good data it already had — the per-section resilience contract.
    if (sR.status === "fulfilled") {
      setState(sR.value);
      setGitRedSustainMs(sR.value.git_red_sustain_secs * 1000);
    }
    // Drop a stale activity page: refresh() captured `activityFilter` before awaiting, and the
    // operator may have switched tabs mid-flight. Applying the old tab's page (e.g. the unfiltered
    // newest-25, which excludes older alert rows) clobbers the new tab's rows until the next poll.
    if (aR.status === "fulfilled" && activityFilterRef.current === activityFilter) {
      setActivity(aR.value);
      // A short live page means there's nothing older to page into.
      setActivityHasMore(aR.value.length >= ACTIVITY_LIMIT);
    }
    if (accsR.status === "fulfilled") setAccounts(accsR.value);
    if (agsR.status === "fulfilled") setAgents(agsR.value);
    if (rlsR.status === "fulfilled") setRoles(rlsR.value);
    if (collisionsR.status === "fulfilled") setCollisionActivity(collisionsR.value);
    if (escsR.status === "fulfilled") setEscalations(escsR.value);
    if (sjRunsR.status === "fulfilled") setScheduledJobRuns(sjRunsR.value);
    if (doneActR.status === "fulfilled") setDoneActivity(doneActR.value);
    if (sdStatusR.status === "fulfilled") setScheduledDispatch(sdStatusR.value);
    if (parkedR.status === "fulfilled") setParkedTasks(parkedR.value);
    if (dpStatusR.status === "fulfilled") setDispatchPauseStatus(dpStatusR.value);
    if (ffStatusR.status === "fulfilled") setFleetFreeze(ffStatusR.value);

    // `connected`/`error` still gate the full-page ErrorBanner/LoadingState — that's a
    // "lost the backend entirely" signal, so it tracks the CORE `state` call only now;
    // any other section failing on its own degrades to a stale badge (below) instead of
    // blanking the whole dashboard.
    if (sR.status === "fulfilled") {
      setConnected(true);
      setError(null);
    } else {
      const reason = sR.reason;
      if (reason instanceof FetchTimeoutError) {
        // Best-effort beacon so a stall is retroactively visible even though this
        // tab never got a chance to report anything otherwise — must not throw or
        // block the error state below.
        api
          .reportStall(baseUrl, { path: reason.path, elapsed_ms: reason.elapsedMs, backend_url: baseUrl })
          .catch(() => {});
      }
      setConnected(false);
      setError(reason instanceof Error ? reason.message : String(reason));
    }
  }, [baseUrl, onSignOut, activityFilter]);

  // ── Live stream (read-models wave 3) ─────────────────────────────────────────
  // ONE `/ws/stream` socket for the whole page drives refreshing; the 60s timer below is
  // the fallback used only while that socket is NOT open. snapshotStream.ts's header
  // explains why an event is a "go ask" prompt rather than a change signal: the snapshot
  // revision is a 30s heartbeat from an unconditional writer tick, not a content digest.
  // Transport state gates the fallback timer only. Whether the socket is up is already
  // surfaced to the operator by the existing `connected`/ErrorBanner path, so it needs no
  // second indicator here.
  const streamOpenRef = useRef(false);
  // The stream module is dynamically imported below so its machinery stays out of the
  // initial bundle (snapshotStream.ts's header). Until it resolves there is no socket and
  // therefore no stream event to act on, so `null` here simply means "nothing to do".
  const streamModRef = useRef<typeof import("./snapshotStream") | null>(null);

  // A stream event means a read model was rebuilt. Ask `/api/state` (conditional GET)
  // whether anything content-bearing actually changed, and only then pay for the
  // 12-section fan-out. This is what takes the steady state from ~53 req/min to a couple.
  const refreshIfChanged = useCallback(async () => {
    const stream = streamModRef.current;
    if (!stream) return;
    try {
      const { changed } = await stream.stateProbe(baseUrl);
      if (!changed) return;
    } catch (e) {
      // A 401 means the session itself is gone — same sign-out contract refresh() honours.
      if (e instanceof HttpError && e.status === 401) onSignOut();
      // Anything else is transient: the next stream event and the fallback timer both retry.
      return;
    }
    await refresh();
  }, [baseUrl, refresh, onSignOut]);

  // Sections currently failing (excluding "state", which is surfaced via the existing
  // full-page ErrorBanner instead of the per-section badge below).
  const staleSections = useMemo(
    () => SECTION_KEYS.filter((k) => k !== "state" && sectionMeta[k].error !== null),
    [sectionMeta],
  );

  // "Load older" — fetch one page strictly before the oldest currently-shown row
  // (cursor pagination), append to olderActivity. Decoupled from the live poll.
  const onLoadOlderActivity = useCallback(
    async (beforeId: number) => {
      setLoadingOlder(true);
      try {
        const types = categoryTypesCsv(activityFilter);
        const page = await api.activity(baseUrl, { limit: ACTIVITY_OLDER_PAGE, types, before_id: beforeId });
        setOlderActivity((prev) => [...prev, ...page]);
        setActivityHasMore(page.length >= ACTIVITY_OLDER_PAGE);
      } catch {
        // transient — the main poll surfaces backend errors
      } finally {
        setLoadingOlder(false);
      }
    },
    [baseUrl, activityFilter],
  );

  // Changing the category tab invalidates the accumulated older pages.
  const onActivityFilter = useCallback(
    (f: ActivityFilter) => {
      activityFilterRef.current = f; // synchronously, so an in-flight refresh() for the old tab is dropped
      setActivityFilter(f);
      setOlderActivity([]);
      setActivityHasMore(true);
      // Fetch the new tab's page on its own, NOT only via refresh(): that is a 13-way fan-out gated on
      // its slowest member (Promise.allSettled), so on a loaded backend the old tab's rows stay on screen
      // (the Alerts tab shows the unfiltered newest page) for seconds after the click.
      void api
        .activity(baseUrl, { limit: ACTIVITY_LIMIT, types: categoryTypesCsv(f) })
        .then((page) => {
          if (activityFilterRef.current !== f) return;
          setActivity(page);
          setActivityHasMore(page.length >= ACTIVITY_LIMIT);
        })
        .catch(() => {
          // transient — the refresh() triggered by the filter change surfaces backend errors
        });
    },
    [baseUrl],
  );

  // Merge the live page + paged-back older rows, de-duped by id (the live page
  // can overlap the first older page after new events arrive), newest-first.
  const mergedActivity = useMemo(() => {
    const seen = new Set<number>();
    const out: ActivityEntry[] = [];
    for (const e of [...activity, ...olderActivity]) {
      if (e.id != null) {
        if (seen.has(e.id)) continue;
        seen.add(e.id);
      }
      out.push(e);
    }
    return out;
  }, [activity, olderActivity]);

  // Backlog Integrity panel's derived, currently-unresolved collision list.
  const backlogCollisions = useMemo(() => unresolvedBacklogCollisions(collisionActivity), [collisionActivity]);

  // Fetch finished agents for the AgentTypesPanel only when the operator opts in;
  // when off, the panel reuses the live `agents` poll (no extra request).
  useEffect(() => {
    if (!typesShowFinished) {
      setTypesFinishedAgents([]);
      return;
    }
    let cancelled = false;
    const load = () =>
      api
        .listAgents(baseUrl, { include_finished: true })
        .then((rows) => {
          if (!cancelled) setTypesFinishedAgents(rows);
        })
        .catch(() => {
          /* main poll surfaces backend errors */
        });
    void load();
    const id = setInterval(load, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [typesShowFinished, baseUrl]);

  const typesAgents = typesShowFinished ? typesFinishedAgents : agents;

  // Poll chat history for the active role.
  const refreshHistory = useCallback(async () => {
    if (!activeRole) return;
    setHistoryLoading(true);
    try {
      const res = await api.agentHistoryByRole(baseUrl, activeRole);
      setAgentHistory(res.messages);
    } catch {
      // ignore — main /api/state polling will surface backend errors
    } finally {
      setHistoryLoading(false);
    }
  }, [baseUrl, activeRole]);

  useEffect(() => {
    void refreshHistory();
    // 10s cadence — slow enough not to spam the backend with multiple tabs,
    // fast enough that an operator reply feels close to real-time. Send-time
    // optimistic append covers the gap for messages the operator just typed.
    const id = setInterval(refreshHistory, 10000);
    return () => clearInterval(id);
  }, [refreshHistory]);

  const onSendToRole = useCallback(
    async (role: string, text: string) => {
      // Optimistic append so the operator sees their message immediately;
      // the next refreshHistory tick reconciles with server-side ids/timestamps.
      const optimistic: ChatMessage = {
        id: -Date.now(),
        direction: "to_agent",
        from_role: "operator",
        content: text,
        created_at: new Date().toISOString(),
      };
      setAgentHistory((prev) => [...prev, optimistic]);
      try {
        await api.sendToRole(baseUrl, role, text, "operator");
      } finally {
        // refresh() updates the agents list (and pending_count badge);
        // refreshHistory() reconciles the optimistic message with the real id.
        await Promise.all([refresh(), refreshHistory()]);
      }
    },
    [baseUrl, refresh, refreshHistory],
  );

  const onPromoteAgent = useCallback(
    async (agentId: string, targetRole: AgentRole) => {
      await api.promoteAgent(baseUrl, agentId, targetRole);
      await refresh();
    },
    [baseUrl],
  );

  const onArchiveAgent = useCallback(
    async (agentId: string) => {
      await api.archiveAgent(baseUrl, agentId);
      await refresh();
    },
    [baseUrl],
  );

  const onForceArchiveAgent = useCallback(
    async (agentId: string) => {
      try {
        await api.forceArchiveAgent(baseUrl, agentId);
        await refresh();
      } catch (e) {
        // A 409 (session genuinely alive / already archived) is the tool WORKING as
        // designed, not a bug — surface it plainly rather than swallowing it.
        setErrorMsg(setActionError)(e);
      }
    },
    [baseUrl],
  );

  const onRestoreAgent = useCallback(
    async (agentId: string) => {
      await api.restoreAgent(baseUrl, agentId);
      await refresh();
    },
    [baseUrl],
  );

  const onRequestDeleteAgent = useCallback((a: AgentView) => {
    setPendingDeleteAgent(a);
  }, []);

  const onConfirmDeleteAgent = useCallback(
    async (agentId: string) => {
      try {
        await api.deleteAgent(baseUrl, agentId);
        setPendingDeleteAgent(null);
        await refresh();
      } catch (e) {
        // Surface the failure (proxy/auth/unreachable) instead of swallowing it
        // as an unhandled rejection — otherwise the button silently "does nothing".
        // Leave the modal open so the operator can read the error and retry.
        setErrorMsg(setActionError)(e);
      }
    },
    [baseUrl, refresh],
  );

  const onRequestEditAgent = useCallback((a: AgentView) => {
    setPendingEditAgent(a);
  }, []);

  const onConfirmEditAgent = useCallback(
    async (agentId: string, fields: { label?: string; rc_url?: string; machine?: string }) => {
      await api.updateAgent(baseUrl, agentId, fields);
      setPendingEditAgent(null);
      await refresh();
    },
    [baseUrl],
  );

  useEffect(() => {
    refresh();
    let fallbackTicks = 0;
    const id = setInterval(() => {
      // Stream down: exactly the old 60s behaviour, so a dead socket degrades to a
      // slower dashboard rather than to a frozen one.
      if (!streamOpenRef.current) {
        void refresh();
        return;
      }
      // Stream up: it already drives refreshes, so this timer adds nothing per tick —
      // except the slow safety-net fan-out for panels `/api/state` doesn't carry.
      fallbackTicks += 1;
      if (fallbackTicks % SAFETY_NET_TICKS === 0) void refresh();
    }, POLL_INTERVAL_MS);
    // ONE socket, shared page-wide (snapshotStream.ts keys the client by backend URL).
    // Dynamically imported so the stream machinery stays out of the initial bundle (the
    // bundle-size ratchet measures only the entry chunk's static import graph); the 60s
    // fallback timer above covers the one-chunk gap before the import resolves.
    let unsubscribe: (() => void) | null = null;
    let disposed = false;
    void import("./snapshotStream").then((m) => {
      if (disposed) return;
      streamModRef.current = m;
      unsubscribe = m.subscribeSnapshotStream(baseUrl, {
        onEvent: () => void refreshIfChanged(),
        onStateChange: (next) => {
          streamOpenRef.current = next === "open";
        },
      });
    });
    return () => {
      disposed = true;
      clearInterval(id);
      unsubscribe?.();
    };
  }, [refresh, refreshIfChanged, baseUrl]);

  const summary = useMemo(() => (state ? summarise(state) : null), [state]);
  // PC-10: the ONE central-host resolution shared by every slot action modal below.
  // Resolved against the FULL `state.slots`, never a filtered list — SlotTable's own note
  // records why: `fleetSlots(...)` drops the "main" row `resolveCentralHostId` keys on, so
  // deriving it from a filtered list silently resolves null and misclassifies every row as
  // central. `state == null` (the pre-load render, where these modals are still mounted)
  // yields null, which `slotLabel` reads as "cannot distinguish hosts" and renders unqualified.
  const centralHostId = state ? resolveCentralHostId(state.slots) : null;
  const parkedCount = parkedTasks?.length ?? 0;
  const parkedNeedsHumanCount = parkedTasks?.filter((t) => t.likely_needs_human_action).length ?? 0;

  // ── operator actions ──

  const onAct = useCallback(
    (kind: SlotAction, slot: SlotView) => {
      if (kind === "reassign") setPendingReassign(slot);
      else if (kind === "message") setPendingMessage(slot);
      else if (kind === "loop-interval") setPendingLoopInterval(slot);
      else if (kind === "delete") setPendingDeleteSlot(slot);
      else if (kind === "log") setLogViewerSlot(slot);
      else if (kind === "chat") setChatSlot(slot);
      else if (kind === "switch-account") {
        setPendingSwitchAccount({
          kind: "slot",
          slotId: slot.slot_id,
          label: `slot ${slotLabel(slot, centralHostId)}`,
          host: slot.host,
        });
      } else if (kind === "switch-model") {
        setPendingSwitchModel({
          kind: "slot",
          slotId: slot.slot_id,
          label: `slot ${slotLabel(slot, centralHostId)}`,
          host: slot.host,
        });
      } else if (kind === "pause") {
        api.pause(baseUrl, slot.slot_id, slot.host).then(refresh).catch(setErrorMsg(setActionError));
      } else if (kind === "resume") {
        api.resume(baseUrl, slot.slot_id, slot.host).then(refresh).catch(setErrorMsg(setActionError));
      }
    },
    [baseUrl, refresh, centralHostId],
  );

  const confirmReassign = useCallback(() => {
    if (!pendingReassign) return;
    api
      .reassign(baseUrl, pendingReassign.slot_id, undefined, pendingReassign.host)
      .then(() => {
        setPendingReassign(null);
        refresh();
      })
      .catch(setErrorMsg(setActionError));
  }, [baseUrl, pendingReassign, refresh]);

  // ao_manual_account_switch_2026_08_07: main is a genuine role-singleton
  // (single persistent AgentRow, fixed tmux session name) — role="main" goes
  // through the by-role endpoint directly. review is per-SLOT (multiple
  // concurrent, no single "the" review agent) and has no dedicated slot_id
  // field on AgentView, so its tmux_session ("orch-slot-N", the same string
  // already shown for `tmux attach`) is parsed to resolve which slot to hit —
  // same underlying mechanism as a worker, just reached from the agent card.
  const onSwitchAccountAgent = useCallback((agent: AgentView) => {
    if (agent.role === "main") {
      setPendingSwitchAccount({ kind: "role", role: "main", label: `main (${agent.agent_id})` });
      return;
    }
    const m = agent.tmux_session?.match(/^orch-slot-(\d+)$/);
    if (!m) {
      setActionError(`${agent.role} agent ${agent.agent_id} has no live slot session to switch (tmux_session unset)`);
      return;
    }
    setPendingSwitchAccount({ kind: "slot", slotId: Number(m[1]), label: `${agent.role} (slot #${m[1]})` });
  }, []);

  const confirmSwitchAccount = useCallback(
    (accountId: string) => {
      if (!pendingSwitchAccount) return;
      const call =
        pendingSwitchAccount.kind === "role"
          ? api.switchRoleAccount(baseUrl, pendingSwitchAccount.role, accountId)
          : api.switchSlotAccount(baseUrl, pendingSwitchAccount.slotId, accountId, pendingSwitchAccount.host);
      call
        .then(() => {
          setPendingSwitchAccount(null);
          refresh();
        })
        .catch(setErrorMsg(setActionError));
    },
    [baseUrl, pendingSwitchAccount, refresh],
  );

  // Same role/slot resolution as onSwitchAccountAgent (main is the role-singleton;
  // review/custom resolve via their live slot's tmux_session).
  const onSwitchModelAgent = useCallback((agent: AgentView) => {
    if (agent.role === "main") {
      setPendingSwitchModel({ kind: "role", role: "main", label: `main (${agent.agent_id})` });
      return;
    }
    const m = agent.tmux_session?.match(/^orch-slot-(\d+)$/);
    if (!m) {
      setActionError(`${agent.role} agent ${agent.agent_id} has no live slot session to switch (tmux_session unset)`);
      return;
    }
    setPendingSwitchModel({ kind: "slot", slotId: Number(m[1]), label: `${agent.role} (slot #${m[1]})` });
  }, []);

  const confirmSwitchModel = useCallback(
    (model: string) => {
      if (!pendingSwitchModel) return;
      const call =
        pendingSwitchModel.kind === "role"
          ? api.switchRoleModel(baseUrl, pendingSwitchModel.role, model)
          : api.switchSlotModel(baseUrl, pendingSwitchModel.slotId, model, pendingSwitchModel.host);
      call
        .then(() => {
          setPendingSwitchModel(null);
          refresh();
        })
        .catch(setErrorMsg(setActionError));
    },
    [baseUrl, pendingSwitchModel, refresh],
  );

  const onConfirmDeleteSlot = useCallback(
    async (slotId: number, host?: string | null) => {
      try {
        await api.deleteSlot(baseUrl, slotId, host);
        setPendingDeleteSlot(null);
        await refresh();
      } catch (e) {
        // Surface the failure instead of swallowing it (see onConfirmDeleteAgent).
        setErrorMsg(setActionError)(e);
      }
    },
    [baseUrl, refresh],
  );

  const onAnswerBlocked = useCallback(
    (q: BlockedView, answer: string, fromRole: FromRole, ruling?: BlockedRuling) => {
      setState((prev) =>
        prev ? { ...prev, blocked_queue: prev.blocked_queue.filter((b) => b.blocked_id !== q.blocked_id) } : prev,
      );
      api
        .answerBlocked(baseUrl, q.blocked_id, answer, fromRole, ruling)
        .then(refresh)
        .catch((err) => {
          refresh();
          setErrorMsg(setActionError)(err);
        });
    },
    [baseUrl, refresh],
  );

  const onTogglePrerequisite = useCallback(
    (name: string, value: boolean) => {
      api
        .setPrerequisite(baseUrl, name, value, session.user.role === "viewer" ? "viewer" : "operator")
        .then(refresh)
        .catch(setErrorMsg(setActionError));
    },
    [baseUrl, refresh, session.user.role],
  );

  const onToggleScheduledDispatch = useCallback(
    (mode: string, paused: boolean) => {
      const action = paused ? api.pauseScheduledDispatch : api.resumeScheduledDispatch;
      action(baseUrl, mode).then(refresh).catch(setErrorMsg(setActionError));
    },
    [baseUrl, refresh],
  );

  // Asymmetric by design: PAUSE routes through FleetPauseModal's confirm gate,
  // RESUME fires straight away. Pausing is the destructive direction (kills every
  // live session fleet-wide); resuming is the recovery direction, and the incident
  // that motivated the gate was a failure to resume — friction there would make
  // recovery slower, not safer.
  const onToggleFleetDispatchPause = useCallback(
    (pause: boolean) => {
      if (pause) {
        setFleetPauseConfirmOpen(true);
        return;
      }
      api.resumeFleetDispatch(baseUrl).then(refresh).catch(setErrorMsg(setActionError));
    },
    [baseUrl, refresh],
  );

  const confirmFleetDispatchPause = useCallback(
    (reason: string) => {
      // Close first so a failure surfaces on the action-error banner rather than
      // behind the modal backdrop.
      setFleetPauseConfirmOpen(false);
      api
        .pauseFleetDispatch(baseUrl, reason.trim() || "operator dashboard toggle")
        .then(refresh)
        .catch(setErrorMsg(setActionError));
    },
    [baseUrl, refresh],
  );

  const onSendMessage = useCallback(
    (slot: SlotView, text: string) => {
      api
        .sendMessage(baseUrl, slot.slot_id, text, "operator", slot.host)
        .then(() => {
          setPendingMessage(null);
          refresh();
        })
        .catch(setErrorMsg(setActionError));
    },
    [baseUrl, refresh],
  );

  const onSetLoopInterval = useCallback(
    (slot: SlotView, seconds: number) => {
      api
        .setLoopInterval(baseUrl, slot.slot_id, clampLoopSeconds(seconds))
        .then(() => {
          setPendingLoopInterval(null);
          refresh();
        })
        .catch(setErrorMsg(setActionError));
    },
    [baseUrl, refresh],
  );

  const onSnapshot = useCallback(() => setSnapshotOpen(true), []);
  const confirmSnapshot = useCallback(() => {
    api
      .snapshot(baseUrl, "manual")
      .then(() => {
        setSnapshotOpen(false);
        refresh();
      })
      .catch(setErrorMsg(setActionError));
  }, [baseUrl, refresh]);

  const onDeployDashboard = useCallback(() => setDeployDashboardOpen(true), []);
  const confirmDeployDashboard = useCallback(
    (version: string, target: "prod" | "uat") => {
      api
        .deployDashboard(baseUrl, version, target)
        .then(() => setDeployDashboardOpen(false))
        .catch(setErrorMsg(setActionError));
    },
    [baseUrl],
  );

  const onClearRateLimit = useCallback(
    (accountId: string) => {
      api.clearRateLimit(baseUrl, accountId).then(refresh).catch(setErrorMsg(setActionError));
    },
    [baseUrl, refresh],
  );

  const onDisableAccount = useCallback(
    (accountId: string) => {
      // Pause is sticky (cleared only by Resume), so ask WHY first: the reason and the signed-in
      // operator's name go into the server's audit trail instead of a fixed placeholder.
      const parsed = parsePauseReasonInput(
        window.prompt(`Why are you pausing ${accountId}? This is recorded against your name.`, ""),
      );
      if (parsed.kind === "cancelled") return;
      if (parsed.kind === "empty") {
        setActionError(`${accountId} was not paused: a reason is required.`);
        return;
      }
      api
        .disableAccount(baseUrl, accountId, parsed.reason, session.user.name)
        .then(refresh)
        .catch(setErrorMsg(setActionError));
    },
    [baseUrl, refresh, session.user.name],
  );

  const onEnableAccount = useCallback(
    (accountId: string) => {
      api.enableAccount(baseUrl, accountId).then(refresh).catch(setErrorMsg(setActionError));
    },
    [baseUrl, refresh],
  );

  // Unlike the other account actions this one AWAITS and rethrows: TierBadge keeps the
  // inline editor open on failure and shows the error next to the field, so it needs the
  // rejection rather than a fire-and-forget banner.
  const onUpdateTier = useCallback(
    async (accountId: string, tier: string, weeklyMsgLimit: number) => {
      await api.updateAccountTier(baseUrl, accountId, tier, weeklyMsgLimit);
      refresh();
    },
    [baseUrl, refresh],
  );

  // Operator edited backlog.yaml on disk → tell the server to re-read it.
  // Auto-polling won't trigger this; server caches the parsed YAML in memory.
  const onReloadBacklog = useCallback(async () => {
    setReloadingBacklog(true);
    try {
      await api.reloadBacklog(baseUrl);
      refresh();
    } catch (e) {
      setErrorMsg(setActionError)(e);
    } finally {
      setReloadingBacklog(false);
    }
  }, [baseUrl, refresh]);

  // Backlog Integrity panel's "Fix" button. A 404 means the collision was
  // already resolved/superseded (remint_backlog_collision's own re-validation,
  // e.g. a peer already fixed it or a later regen tick moved the checkbox) —
  // treated the same as success: just refresh so the now-stale row drops out
  // of `collisions` on the next poll. Any other error surfaces inline.
  const onFixCollision = useCallback(
    async (taskId: string) => {
      setFixingCollisionId(taskId);
      setCollisionFixError(null);
      try {
        await api.remintBacklogCollision(baseUrl, taskId);
        await refresh();
      } catch (e) {
        if (e instanceof HttpError && e.status === 404) {
          await refresh();
        } else {
          setCollisionFixError(e instanceof Error ? e.message : String(e));
        }
      } finally {
        setFixingCollisionId(null);
      }
    },
    [baseUrl, refresh],
  );

  const onReportUsage = useCallback((accountId: string) => {
    setReportUsageId(accountId);
  }, []);

  // Slow (~12-15s): backend spawns `claude` in a pty, sends /usage,
  // parses, updates the account row. Spinner shows on the button.
  const onRefreshUsage = useCallback(
    async (accountId: string) => {
      setRefreshingUsageId(accountId);
      try {
        await api.refreshAccountUsage(baseUrl, accountId);
        refresh();
      } catch (e) {
        setErrorMsg(setActionError)(e);
      } finally {
        setRefreshingUsageId(null);
      }
    },
    [baseUrl, refresh],
  );

  // DeepSeek accounts used to have their own dedicated onRefreshDeepseekBalance
  // handler/route (`POST .../refresh-deepseek-balance`); folded into the shared
  // `/refresh-usage` dispatch server-side 2026-08-26 (accounts.py's
  // `_refresh_deepseek_usage` arm), so the account-card prop below now just
  // points at the same onRefreshUsage handler every other provider's card uses.

  const onSubmitUsage = useCallback(
    async (accountId: string, weekly: number | null, fiveHour: number | null) => {
      await api.reportUsage(baseUrl, accountId, {
        weekly_msgs_used: weekly ?? undefined,
        five_hour_msgs_used: fiveHour ?? undefined,
        source: "operator",
      });
      setReportUsageId(null);
      refresh();
    },
    [baseUrl, refresh],
  );

  const handleSignOut = useCallback(() => {
    api
      .logout(baseUrl)
      .catch(() => {})
      .finally(onSignOut);
  }, [baseUrl, onSignOut]);

  const handleAdminPopulate = useCallback(() => {
    if (!confirm("Run the demo-populate script? Seeds 7 slots, 2 blocked questions, account usage.")) return;
    setAdminBusy("populate");
    api
      .adminPopulate(baseUrl)
      .then(() => refresh())
      .catch(setErrorMsg(setActionError))
      .finally(() => setAdminBusy(null));
  }, [baseUrl, refresh]);

  const handleAdminReset = useCallback(() => {
    setResetTyping(true);
  }, []);

  const confirmAdminReset = useCallback(
    async (confirmText: string) => {
      if (confirmText !== "reset") return;
      setAdminBusy("reset");
      try {
        await api.adminReset(baseUrl);
        await refresh();
      } catch (e) {
        setErrorMsg(setActionError)(e);
      } finally {
        setAdminBusy(null);
        setResetTyping(false);
      }
    },
    [baseUrl, refresh],
  );

  // ── render ──

  const sessionStartedShort = new Date(session.started_at).toISOString().slice(11, 16) + " UTC";

  return (
    <div className="app">
      <TopBar
        backends={backends}
        backendId={activeId}
        onBackend={setActiveId}
        state={state}
        onSnapshot={onSnapshot}
        onDeployDashboard={onDeployDashboard}
        connected={connected}
        mode={modeInfo?.mode ?? null}
        t={tweaks}
        setTweak={setTweak}
        user={session.user}
        sessionStartedShort={sessionStartedShort}
        onSignOut={handleSignOut}
        baseUrl={BOOTSTRAP_URL}
        authToken={session.token}
        dispatchPauseStatus={dispatchPauseStatus}
        onToggleFleetDispatchPause={onToggleFleetDispatchPause}
      />
      <main>
        {modeInfo?.admin_enabled && (
          <div className="admin-strip">
            <span className="lab">mock mode</span>
            <span className="sub">
              State lives in <span className="mono">{modeInfo.db_path.split("/").pop()}</span>. Restart with{" "}
              <span className="mono">ORCHESTRATOR_MODE=live</span> for real data.
            </span>
            <button
              className="btn btn-sm"
              onClick={handleAdminPopulate}
              disabled={adminBusy !== null}
              title="Seed mock slots + blocked Qs + account usage"
            >
              {adminBusy === "populate" ? "Populating…" : "Populate demo"}
            </button>
            <button
              className="btn btn-sm btn-danger"
              onClick={handleAdminReset}
              disabled={adminBusy !== null}
              title="Drop all tables + reload backlog + accounts"
            >
              Reset state
            </button>
          </div>
        )}

        {error && !connected && state && <ErrorBanner backend={activeBackend} message={error} onRetry={refresh} />}
        {actionError && <ActionErrorToast message={actionError} onDismiss={() => setActionError(null)} />}
        <VmResourcesStaleBanner data={selfVmResources.data} />
        {state && staleSections.length > 0 && (
          <div
            data-testid="section-stale-banner"
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              fontSize: 12,
              color: "var(--status-stale)",
              marginBottom: 10,
            }}
          >
            {staleSections.map((k) => {
              const meta = sectionMeta[k];
              const ageS = meta.updatedAt !== null ? Math.round((Date.now() - meta.updatedAt) / 1000) : null;
              return (
                <span
                  key={k}
                  data-testid={`stale-section-${k}`}
                  title={meta.error ?? undefined}
                  style={{ border: "1px solid var(--status-stale)", borderRadius: 4, padding: "1px 6px" }}
                >
                  {SECTION_LABELS[k]} stale{ageS !== null ? ` (last ok ${ageS}s ago)` : " (never loaded)"}
                </span>
              );
            })}
          </div>
        )}

        {state === null ? (
          <LoadingState error={!connected ? error : null} onRetry={refresh} />
        ) : isMobile ? (
          <MobileTriage
            state={state}
            summary={summary!}
            fleetFreeze={fleetFreeze}
            baseUrl={baseUrl}
            activity={mergedActivity}
            doneActivity={doneActivity}
            activityFilter={activityFilter}
            onActivityFilter={onActivityFilter}
            onLoadOlderActivity={onLoadOlderActivity}
            loadingOlderActivity={loadingOlder}
            activityHasMore={activityHasMore}
            typesAgents={typesAgents}
            typesShowFinished={typesShowFinished}
            setTypesShowFinished={setTypesShowFinished}
            accounts={accounts}
            roles={roles}
            onAnswer={onAnswerBlocked}
            onAct={onAct}
            onTogglePrerequisite={onTogglePrerequisite}
            onClearRateLimit={onClearRateLimit}
            onReportUsage={onReportUsage}
            onRefreshUsage={onRefreshUsage}
            onRefreshDeepseekBalance={onRefreshUsage}
            onDisableAccount={onDisableAccount}
            onUpdateTier={onUpdateTier}
            onEnableAccount={onEnableAccount}
            refreshingUsageId={refreshingUsageId}
            collisions={backlogCollisions}
            onFixCollision={onFixCollision}
            fixingCollisionId={fixingCollisionId}
            collisionFixError={collisionFixError}
            escalations={escalations}
            agents={agents}
            scheduledJobRuns={scheduledJobRuns}
            onViewScheduledJobLog={(id) => setLogViewerAgent(scheduledJobLogStub(id))}
            scheduledDispatch={scheduledDispatch}
            onToggleScheduledDispatch={onToggleScheduledDispatch}
            activeRole={activeRole}
            setActiveRole={setActiveRole}
            agentHistory={agentHistory}
            historyLoading={historyLoading}
            showArchived={showArchived}
            setShowArchived={setShowArchived}
            onSendToRole={onSendToRole}
            onPromoteAgent={onPromoteAgent}
            onArchiveAgent={onArchiveAgent}
            onForceArchiveAgent={onForceArchiveAgent}
            onRestoreAgent={onRestoreAgent}
            onRequestDeleteAgent={onRequestDeleteAgent}
            onRequestEditAgent={onRequestEditAgent}
            onShowAgentLog={(a) => setLogViewerAgent(a)}
            onSwitchAccountAgent={onSwitchAccountAgent}
            onSwitchModelAgent={onSwitchModelAgent}
            onSpawnAgent={() => setSpawnAgentOpen(true)}
            onOpenBacklog={() => setBacklogOpen(true)}
            onOpenBlockerTask={openBacklogForTask}
            onReloadBacklog={onReloadBacklog}
            reloadingBacklog={reloadingBacklog}
            parkedCount={parkedCount}
            parkedNeedsHumanCount={parkedNeedsHumanCount}
          />
        ) : (
          <DesktopLayout
            t={tweaks}
            baseUrl={baseUrl}
            state={state}
            summary={summary!}
            fleetFreeze={fleetFreeze}
            activity={mergedActivity}
            doneActivity={doneActivity}
            activityFilter={activityFilter}
            onActivityFilter={onActivityFilter}
            onLoadOlderActivity={onLoadOlderActivity}
            loadingOlderActivity={loadingOlder}
            activityHasMore={activityHasMore}
            typesAgents={typesAgents}
            typesShowFinished={typesShowFinished}
            setTypesShowFinished={setTypesShowFinished}
            accounts={accounts}
            roles={roles}
            agents={agents}
            activeRole={activeRole}
            setActiveRole={setActiveRole}
            agentHistory={agentHistory}
            historyLoading={historyLoading}
            showArchived={showArchived}
            setShowArchived={setShowArchived}
            onSendToRole={onSendToRole}
            onPromoteAgent={onPromoteAgent}
            onArchiveAgent={onArchiveAgent}
            onForceArchiveAgent={onForceArchiveAgent}
            onRestoreAgent={onRestoreAgent}
            onRequestDeleteAgent={onRequestDeleteAgent}
            onRequestEditAgent={onRequestEditAgent}
            onShowAgentLog={(a) => setLogViewerAgent(a)}
            onSwitchAccountAgent={onSwitchAccountAgent}
            onSwitchModelAgent={onSwitchModelAgent}
            onSpawnAgent={() => setSpawnAgentOpen(true)}
            expandedId={expandedId}
            setExpandedId={setExpandedId}
            onAct={onAct}
            onAnswer={onAnswerBlocked}
            onTogglePrerequisite={onTogglePrerequisite}
            onClearRateLimit={onClearRateLimit}
            onReportUsage={onReportUsage}
            onRefreshUsage={onRefreshUsage}
            onRefreshDeepseekBalance={onRefreshUsage}
            onDisableAccount={onDisableAccount}
            onUpdateTier={onUpdateTier}
            onEnableAccount={onEnableAccount}
            refreshingUsageId={refreshingUsageId}
            onSpawn={() => setSpawnOpen(true)}
            onOpenBacklog={() => setBacklogOpen(true)}
            onOpenBlockerTask={openBacklogForTask}
            onOpenParked={() => setParkedOpen(true)}
            onReloadBacklog={onReloadBacklog}
            reloadingBacklog={reloadingBacklog}
            setTweak={setTweak}
            collisions={backlogCollisions}
            onFixCollision={onFixCollision}
            fixingCollisionId={fixingCollisionId}
            collisionFixError={collisionFixError}
            escalations={escalations}
            scheduledJobRuns={scheduledJobRuns}
            onViewScheduledJobLog={(id) => setLogViewerAgent(scheduledJobLogStub(id))}
            scheduledDispatch={scheduledDispatch}
            onToggleScheduledDispatch={onToggleScheduledDispatch}
            parkedCount={parkedCount}
            parkedNeedsHumanCount={parkedNeedsHumanCount}
          />
        )}
      </main>

      <ReassignModal
        slot={pendingReassign}
        centralHostId={centralHostId}
        onCancel={() => setPendingReassign(null)}
        onConfirm={confirmReassign}
      />
      <SwitchAccountModal
        target={pendingSwitchAccount}
        accounts={accounts}
        onCancel={() => setPendingSwitchAccount(null)}
        onConfirm={confirmSwitchAccount}
      />
      <SwitchModelModal
        target={pendingSwitchModel}
        onCancel={() => setPendingSwitchModel(null)}
        onConfirm={confirmSwitchModel}
      />
      <MessageModal
        slot={pendingMessage}
        centralHostId={centralHostId}
        onCancel={() => setPendingMessage(null)}
        onSend={onSendMessage}
      />
      <LoopIntervalModal
        slot={pendingLoopInterval}
        centralHostId={centralHostId}
        onCancel={() => setPendingLoopInterval(null)}
        onSet={onSetLoopInterval}
      />
      <SnapshotModal open={snapshotOpen} onCancel={() => setSnapshotOpen(false)} onConfirm={confirmSnapshot} />
      {deployDashboardOpen && (
        <React.Suspense fallback={null}>
          <DeployDashboardModal
            open={deployDashboardOpen}
            baseUrl={baseUrl}
            onCancel={() => setDeployDashboardOpen(false)}
            onConfirm={confirmDeployDashboard}
            lastDeployedVersion={state?.last_dashboard_deploy?.version}
          />
        </React.Suspense>
      )}
      <FleetPauseModal
        open={fleetPauseConfirmOpen}
        liveSlots={(state?.slots ?? []).filter((s) => s.worker_alive).length}
        totalSlots={state?.slots.length ?? 0}
        onCancel={() => setFleetPauseConfirmOpen(false)}
        onConfirm={confirmFleetDispatchPause}
      />
      <SpawnModal
        open={spawnOpen}
        onCancel={() => setSpawnOpen(false)}
        slots={state?.slots ?? []}
        accounts={accounts}
        backendUrl={baseUrl}
        operator={session.user.name}
        workspaceRoot={modeInfo?.workspace_root ?? null}
      />
      <ReportUsageModal
        accountId={reportUsageId}
        accounts={accounts}
        onCancel={() => setReportUsageId(null)}
        onSubmit={onSubmitUsage}
      />
      <ResetModal
        open={resetTyping}
        busy={adminBusy === "reset"}
        onCancel={() => setResetTyping(false)}
        onConfirm={confirmAdminReset}
      />
      <SpawnAgentModal
        open={spawnAgentOpen}
        onCancel={() => setSpawnAgentOpen(false)}
        backendUrl={baseUrl}
        defaultMachine={session.user.machine}
        accounts={accounts}
      />
      <DeleteAgentModal
        agent={pendingDeleteAgent}
        onCancel={() => setPendingDeleteAgent(null)}
        onConfirm={onConfirmDeleteAgent}
      />
      <EditAgentModal
        agent={pendingEditAgent}
        machineOptions={Array.from(new Set(agents.map((a) => a.machine).filter((m): m is string => !!m))).sort()}
        onCancel={() => setPendingEditAgent(null)}
        onConfirm={onConfirmEditAgent}
        onSwitchModel={(a) => {
          setPendingEditAgent(null);
          onSwitchModelAgent(a);
        }}
      />
      <DeleteSlotModal
        slot={pendingDeleteSlot}
        centralHostId={centralHostId}
        onCancel={() => setPendingDeleteSlot(null)}
        onConfirm={onConfirmDeleteSlot}
      />
      <React.Suspense fallback={null}>
        <LogViewerModal
          scope="slot"
          target={logViewerSlot}
          centralHostId={centralHostId}
          baseUrl={baseUrl}
          onClose={() => setLogViewerSlot(null)}
        />
      </React.Suspense>
      <React.Suspense fallback={null}>
        <LogViewerModal
          scope="agent"
          target={logViewerAgent}
          baseUrl={baseUrl}
          onClose={() => setLogViewerAgent(null)}
        />
      </React.Suspense>
      {chatSlot && (
        <React.Suspense fallback={null}>
          <WorkerChatModal slot={chatSlot} baseUrl={baseUrl} onClose={() => setChatSlot(null)} />
        </React.Suspense>
      )}
      <BacklogDetailModal
        open={backlogOpen}
        onClose={() => {
          setBacklogOpen(false);
          setPendingDetailTaskId(null);
        }}
        baseUrl={baseUrl}
        initialTaskId={pendingDetailTaskId}
      />
      <ParkedTasksModal open={parkedOpen} onClose={() => setParkedOpen(false)} baseUrl={baseUrl} />
    </div>
  );
}

// ─── desktop layout ──────────────────────────────────────────────────────

// Shared wiring threaded into both DesktopLayout + MobileTriage (Plan B).
interface ActivityFeedWiring {
  activity: ActivityEntry[];
  // Dedicated, type-filtered pull feeding latestDoneOutcomeBySlot() — see
  // DONE_ACTIVITY_LIMIT's comment for why this must not reuse `activity` above.
  doneActivity: ActivityEntry[];
  activityFilter: ActivityFilter;
  onActivityFilter: (f: ActivityFilter) => void;
  onLoadOlderActivity: (beforeId: number) => void;
  loadingOlderActivity: boolean;
  activityHasMore: boolean;
}

interface AgentTypesWiring {
  typesAgents: AgentView[];
  typesShowFinished: boolean;
  setTypesShowFinished: (v: boolean) => void;
}

interface DesktopLayoutProps extends ActivityFeedWiring, AgentTypesWiring {
  t: Tweaks;
  baseUrl: string;
  state: StateResponse;
  summary: ReturnType<typeof summarise>;
  accounts: AccountView[];
  roles: RoleView[];
  agents: AgentView[];
  activeRole: AgentRole | null;
  setActiveRole: (r: AgentRole | null) => void;
  agentHistory: ChatMessage[];
  historyLoading: boolean;
  showArchived: boolean;
  setShowArchived: (v: boolean) => void;
  onSendToRole: (role: string, text: string) => Promise<void>;
  onPromoteAgent: (agentId: string, targetRole: AgentRole) => Promise<void>;
  onArchiveAgent: (agentId: string) => Promise<void>;
  onForceArchiveAgent: (agentId: string) => Promise<void>;
  onRestoreAgent: (agentId: string) => Promise<void>;
  onRequestDeleteAgent: (a: AgentView) => void;
  onRequestEditAgent: (a: AgentView) => void;
  onShowAgentLog: (a: AgentView) => void;
  onSwitchAccountAgent: (a: AgentView) => void;
  onSwitchModelAgent: (a: AgentView) => void;
  onSpawnAgent: () => void;
  expandedId: string | null;
  setExpandedId: (id: string | null) => void;
  onAct: (action: SlotAction, slot: SlotView) => void;
  onAnswer: (q: BlockedView, answer: string, fromRole: FromRole, ruling?: BlockedRuling) => void;
  onTogglePrerequisite: (name: string, value: boolean) => void;
  onClearRateLimit: (accountId: string) => void;
  onReportUsage: (accountId: string) => void;
  onRefreshUsage: (accountId: string) => void;
  onRefreshDeepseekBalance: (accountId: string) => void;
  onDisableAccount: (accountId: string) => void;
  onUpdateTier: (accountId: string, tier: string, weeklyMsgLimit: number) => Promise<void>;
  onEnableAccount: (accountId: string) => void;
  refreshingUsageId: string | null;
  onSpawn: () => void;
  onOpenBacklog: () => void;
  onOpenBlockerTask: (taskId: string) => void;
  onOpenParked: () => void;
  onReloadBacklog: () => void;
  reloadingBacklog: boolean;
  setTweak: <K extends keyof Tweaks>(k: K, v: Tweaks[K]) => void;
  collisions: ReturnType<typeof unresolvedBacklogCollisions>;
  onFixCollision: (taskId: string) => void;
  fixingCollisionId: string | null;
  collisionFixError: string | null;
  escalations: EscalationView[];
  scheduledJobRuns: ScheduledJobRunView[];
  onViewScheduledJobLog: (agentId: string) => void;
  scheduledDispatch: ScheduledDispatchStatusView | null;
  onToggleScheduledDispatch: (mode: string, paused: boolean) => void;
  parkedCount?: number;
  parkedNeedsHumanCount?: number;
  fleetFreeze: FleetFreezeStatusView | null;
}

function DesktopLayout({
  t,
  baseUrl,
  state,
  summary,
  activity,
  doneActivity,
  activityFilter,
  onActivityFilter,
  onLoadOlderActivity,
  loadingOlderActivity,
  activityHasMore,
  typesAgents,
  typesShowFinished,
  setTypesShowFinished,
  accounts,
  roles,
  agents,
  activeRole,
  setActiveRole,
  agentHistory,
  historyLoading,
  showArchived,
  setShowArchived,
  onSendToRole,
  onPromoteAgent,
  onArchiveAgent,
  onForceArchiveAgent,
  onRestoreAgent,
  onRequestDeleteAgent,
  onRequestEditAgent,
  onShowAgentLog,
  onSwitchAccountAgent,
  onSwitchModelAgent,
  onSpawnAgent,
  expandedId,
  setExpandedId,
  onAct,
  onAnswer,
  onTogglePrerequisite,
  onClearRateLimit,
  onReportUsage,
  onRefreshUsage,
  onRefreshDeepseekBalance,
  onDisableAccount,
  onUpdateTier,
  onEnableAccount,
  refreshingUsageId,
  onSpawn,
  onOpenBacklog,
  onOpenBlockerTask,
  onOpenParked,
  onReloadBacklog,
  reloadingBacklog,
  setTweak,
  collisions,
  onFixCollision,
  fixingCollisionId,
  collisionFixError,
  escalations,
  scheduledJobRuns,
  onViewScheduledJobLog,
  scheduledDispatch,
  onToggleScheduledDispatch,
  parkedCount,
  parkedNeedsHumanCount,
  fleetFreeze,
}: DesktopLayoutProps) {
  const doneBySlot = useMemo(() => latestDoneOutcomeBySlot(doneActivity), [doneActivity]);
  // What each slot-bound typed agent is actually working on — the Fleet Task cell shows
  // only a bare role badge for a slot with no backlog task, while the agent itself knows
  // ("unified-trading-pm#2709 — sit_failure"). Joined client-side; both datasets are
  // already loaded here.
  const workBySlot = useMemo(() => agentWorkBySlot(agents), [agents]);
  const critical = useMemo(
    () =>
      criticalHealth(
        accounts,
        agents,
        escalations,
        scheduledJobRuns,
        state.slots,
        state.backlog_summary,
        state.pm_clone,
      ),
    [accounts, agents, escalations, scheduledJobRuns, state.slots, state.backlog_summary, state.pm_clone],
  );
  const activityFeedNode = (
    <ActivityFeed
      activity={activity}
      filter={activityFilter}
      onFilterChange={onActivityFilter}
      onLoadOlder={onLoadOlderActivity}
      loadingOlder={loadingOlderActivity}
      hasMore={activityHasMore}
      serverStartedAt={state.server_started}
      centralHostId={resolveCentralHostId(state.slots)}
    />
  );
  const agentTypesNode = (
    <div id="agent-types">
      <AgentTypesPanel agents={typesAgents} showFinished={typesShowFinished} setShowFinished={setTypesShowFinished} />
    </div>
  );
  // The Fleet shows WORKER slots only — main (slot 0) + review agents live in the
  // Agents panel above, not the fleet (2026-06-27). The full state.slots list is
  // still passed to the Spawn modal so it picks a genuinely-free slot id.
  const fleetAll = fleetSlots(state.slots);
  const [stageFilter, setStageFilter] = useState<StageFilter>("all");
  const fleet = filterSlotsByStage(fleetAll, stageFilter);
  // Multi-host grouping (ao_v2_worker_runtime_and_liveness_2026_09_01.md Phase 5 [UI]
  // P1) — MUST be resolved against the FULL `state.slots`, never `fleet` above:
  // fleetSlots() strips the "main" (slot 0) row this needs to find its central host id
  // from. See SlotTable/SlotCards' own `centralHostId` prop docs.
  const centralHostId = resolveCentralHostId(state.slots);
  // R20 (ao_planning_box_profile_and_reserved_slots_first_2026_09_05.md): computed
  // against the FULL state.slots (never `fleet`), same reason as centralHostId above —
  // the planning box's own profile counts main/review too, which fleetSlots() excludes.
  const planningProfile = planningBoxProfile(state.slots, centralHostId);
  const { widths: layoutWidths, startResize: startLayoutResize, resetSide: resetLayoutSide } = useLayoutColumnWidths();
  if (fleetAll.length === 0) {
    return (
      <>
        <div className="top-row">
          <HealthStrip
            summary={summary}
            critical={critical}
            fleetFreeze={fleetFreeze}
            onReviewParked={onOpenParked}
            onOpenBacklog={onOpenBacklog}
          />
          <HostsResourcesTable baseUrl={baseUrl} />
          <ResourceWatchdog baseUrl={baseUrl} />
          <BacklogSummary
            backlog={state.backlog_summary}
            parkedCount={parkedCount}
            parkedNeedsHumanCount={parkedNeedsHumanCount}
            topBlockers={state.idle_fleet_blockers?.top_blockers}
            onOpenDetail={onOpenBacklog}
            onOpenBlockerTask={onOpenBlockerTask}
            onReloadYaml={onReloadBacklog}
            reloading={reloadingBacklog}
          />
        </div>
        <div
          className="layout"
          style={{
            gridTemplateColumns: `${layoutWidths.activity}px 14px minmax(0, 1fr) 14px ${layoutWidths.rail}px`,
          }}
        >
          <div className="activity-col">{activityFeedNode}</div>
          <LayoutResizeHandle side="activity" onStart={startLayoutResize} onReset={resetLayoutSide} />
          <div className="main-col">
            <AgentsPanel
              agents={agents}
              slots={state.slots}
              activeRole={activeRole}
              setActiveRole={setActiveRole}
              history={agentHistory}
              historyLoading={historyLoading}
              showArchived={showArchived}
              setShowArchived={setShowArchived}
              onSendToRole={onSendToRole}
              onPromote={onPromoteAgent}
              onArchive={onArchiveAgent}
              onForceArchive={onForceArchiveAgent}
              onRestore={onRestoreAgent}
              onDelete={onRequestDeleteAgent}
              onEdit={onRequestEditAgent}
              onLog={onShowAgentLog}
              onSwitchAccount={onSwitchAccountAgent}
              onSwitchModel={onSwitchModelAgent}
              onSpawnAgent={onSpawnAgent}
            />
            {agentTypesNode}
            <PlanningBox slots={state.slots} />
            <Panel
              title="Fleet"
              collapsible
              flush
              tone="fleet"
              right={
                <button className="btn btn-sm" onClick={onSpawn}>
                  <Icon.Bolt /> Spawn worker
                </button>
              }
            >
              <Empty
                icon={<Icon.Bolt />}
                title="No workers booted yet"
                sub={
                  <>
                    Click <b>Spawn worker</b> above for the curl command, or start a Claude Code session and run{" "}
                    <span className="mono">/boot</span> manually.
                  </>
                }
              />
            </Panel>
            <div id="accounts">
              <AccountsPanel
                accounts={accounts}
                onClearRateLimit={onClearRateLimit}
                onReportUsage={onReportUsage}
                onRefreshUsage={onRefreshUsage}
                onRefreshDeepseekBalance={onRefreshDeepseekBalance}
                onDisableAccount={onDisableAccount}
                onUpdateTier={onUpdateTier}
                onEnableAccount={onEnableAccount}
                refreshingId={refreshingUsageId}
              />
            </div>
            <TaskUsageWindowsPanel baseUrl={baseUrl} />
            <BatchingEfficiencyPanel baseUrl={baseUrl} />
            <RoutingShadowComparisonPanel baseUrl={baseUrl} />
            <DeepSeekWalletPanel baseUrl={baseUrl} />
            <ClaudeWalletPanel baseUrl={baseUrl} />
            <div id="registry">
              <RolesPanel roles={roles} />
            </div>
          </div>
          <LayoutResizeHandle side="rail" onStart={startLayoutResize} onReset={resetLayoutSide} />
          <div className="rail">
            <div id="backlog-integrity">
              <BacklogIntegrityPanel
                collisions={collisions}
                onFix={onFixCollision}
                fixingId={fixingCollisionId}
                fixError={collisionFixError}
              />
            </div>
            <div id="blocked">
              <BlockedPanel queue={state.blocked_queue} onAnswer={onAnswer} roles={roles} />
            </div>
            <div id="escalations">
              <EscalationsPanel escalations={escalations} agents={agents} />
            </div>
            <div id="scheduled-jobs">
              <ScheduledJobsPanel runs={scheduledJobRuns} onViewLog={onViewScheduledJobLog} />
            </div>
            <div id="scheduled-dispatch">
              <ScheduledDispatchPanel status={scheduledDispatch} onToggle={onToggleScheduledDispatch} />
            </div>
            {t.showPrerequisitesRail && (
              <PrerequisitesPanel prerequisites={state.prerequisites} onToggle={onTogglePrerequisite} />
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="top-row">
        <HealthStrip
          summary={summary}
          critical={critical}
          fleetFreeze={fleetFreeze}
          onReviewParked={onOpenParked}
          onOpenBacklog={onOpenBacklog}
        />
        <HostsResourcesTable baseUrl={baseUrl} />
        <BacklogSummary
          backlog={state.backlog_summary}
          parkedCount={parkedCount}
          parkedNeedsHumanCount={parkedNeedsHumanCount}
          topBlockers={state.idle_fleet_blockers?.top_blockers}
          onOpenDetail={onOpenBacklog}
          onOpenBlockerTask={onOpenBlockerTask}
          onReloadYaml={onReloadBacklog}
          reloading={reloadingBacklog}
        />
      </div>
      <KpiRow summary={summary} backlog={state.backlog_summary} />

      <div
        className="layout"
        id="fleet"
        style={{
          // Same 5-track shape as the empty-fleet render even when the activity rail is
          // toggled off — activity's width collapses to 0 instead of dropping the track,
          // so main-col/rail keep their static grid-column (3/5) with no per-instance
          // override needed.
          gridTemplateColumns: `${t.showActivityRail ? layoutWidths.activity : 0}px 14px minmax(0, 1fr) 14px ${layoutWidths.rail}px`,
        }}
      >
        {t.showActivityRail && (
          <>
            <div className="activity-col">{activityFeedNode}</div>
            <LayoutResizeHandle side="activity" onStart={startLayoutResize} onReset={resetLayoutSide} />
          </>
        )}
        <div className="main-col">
          <AgentsPanel
            agents={agents}
            slots={state.slots}
            activeRole={activeRole}
            setActiveRole={setActiveRole}
            history={agentHistory}
            historyLoading={historyLoading}
            showArchived={showArchived}
            setShowArchived={setShowArchived}
            onSendToRole={onSendToRole}
            onPromote={onPromoteAgent}
            onArchive={onArchiveAgent}
            onForceArchive={onForceArchiveAgent}
            onRestore={onRestoreAgent}
            onDelete={onRequestDeleteAgent}
            onEdit={onRequestEditAgent}
            onLog={onShowAgentLog}
            onSwitchAccount={onSwitchAccountAgent}
            onSwitchModel={onSwitchModelAgent}
            onSpawnAgent={onSpawnAgent}
          />

          {agentTypesNode}

          <PlanningBox slots={state.slots} />

          <Panel
            title="Fleet"
            collapsible
            count={
              stageFilter === "all"
                ? `${fleet.length} slot${fleet.length === 1 ? "" : "s"}`
                : `${fleet.length} of ${fleetAll.length} slots`
            }
            tone="fleet"
            right={
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <select
                  className="btn btn-sm"
                  data-testid="fleet-stage-filter"
                  aria-label="Filter fleet by stage"
                  title="Filter slots by declared stage"
                  value={stageFilter}
                  onChange={(e) => setStageFilter(e.target.value as StageFilter)}
                >
                  {STAGE_FILTER_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.value === "all" ? o.label : `stage = ${o.label}`}
                    </option>
                  ))}
                </select>
                <Tabs
                  value={t.slotLayout}
                  onChange={(v) => setTweak("slotLayout", v)}
                  options={[
                    { value: "table", label: "Table" },
                    { value: "cards", label: "Cards" },
                  ]}
                />
                <button className="btn btn-sm" onClick={onSpawn} title="Spawn a new worker session">
                  <Icon.Bolt /> Spawn worker
                </button>
              </div>
            }
            flush
          >
            {t.slotLayout === "table" ? (
              <SlotTable
                slots={fleet}
                onAct={onAct}
                expandedId={expandedId}
                setExpandedId={setExpandedId}
                doneBySlot={doneBySlot}
                workBySlot={workBySlot}
                centralHostId={centralHostId}
                profile={planningProfile}
                nodes={state.nodes ?? []}
              />
            ) : (
              <SlotCards
                slots={fleet}
                onAct={onAct}
                doneBySlot={doneBySlot}
                centralHostId={centralHostId}
                profile={planningProfile}
                nodes={state.nodes ?? []}
              />
            )}
          </Panel>

          <div id="accounts">
            <AccountsPanel
              accounts={accounts}
              onClearRateLimit={onClearRateLimit}
              onReportUsage={onReportUsage}
              onRefreshUsage={onRefreshUsage}
              onRefreshDeepseekBalance={onRefreshDeepseekBalance}
              onDisableAccount={onDisableAccount}
              onUpdateTier={onUpdateTier}
              onEnableAccount={onEnableAccount}
              refreshingId={refreshingUsageId}
            />
          </div>
          <TaskUsageWindowsPanel baseUrl={baseUrl} />
          <BatchingEfficiencyPanel baseUrl={baseUrl} />
          <RoutingShadowComparisonPanel baseUrl={baseUrl} />
          <DeepSeekWalletPanel baseUrl={baseUrl} />
          <ClaudeWalletPanel baseUrl={baseUrl} />
          <GeminiCapacityPanel baseUrl={baseUrl} />
          <NvidiaCapacityPanel baseUrl={baseUrl} />
          <MuseCapacityPanel baseUrl={baseUrl} />
          <ConcurrencyCapsPanel baseUrl={baseUrl} />
          <ConcurrencyCapKnobsPanel baseUrl={baseUrl} />
          <FlatRateBoostPanel baseUrl={baseUrl} />

          <div id="registry">
            <RolesPanel roles={roles} />
          </div>
        </div>

        <LayoutResizeHandle side="rail" onStart={startLayoutResize} onReset={resetLayoutSide} />
        <div className="rail">
          <div id="backlog-integrity">
            <BacklogIntegrityPanel
              collisions={collisions}
              onFix={onFixCollision}
              fixingId={fixingCollisionId}
              fixError={collisionFixError}
            />
          </div>
          <div id="blocked">
            <BlockedPanel queue={state.blocked_queue} onAnswer={onAnswer} roles={roles} />
          </div>
          <div id="escalations">
            <EscalationsPanel escalations={escalations} agents={agents} />
          </div>
          <div id="scheduled-jobs">
            <ScheduledJobsPanel runs={scheduledJobRuns} onViewLog={onViewScheduledJobLog} />
          </div>
          <div id="scheduled-dispatch">
            <ScheduledDispatchPanel status={scheduledDispatch} onToggle={onToggleScheduledDispatch} />
          </div>
          {t.showPrerequisitesRail && (
            <PrerequisitesPanel prerequisites={state.prerequisites} onToggle={onTogglePrerequisite} />
          )}
        </div>
      </div>

      {!t.showActivityRail && activityFeedNode}
    </>
  );
}

// ─── mobile triage view ──────────────────────────────────────────────────

interface MobileTriageProps extends ActivityFeedWiring, AgentTypesWiring {
  state: StateResponse;
  summary: ReturnType<typeof summarise>;
  baseUrl: string;
  accounts: AccountView[];
  roles: RoleView[];
  onAnswer: (q: BlockedView, answer: string, fromRole: FromRole, ruling?: BlockedRuling) => void;
  onAct: (action: SlotAction, slot: SlotView) => void;
  onTogglePrerequisite: (name: string, value: boolean) => void;
  onClearRateLimit: (accountId: string) => void;
  onReportUsage: (accountId: string) => void;
  onRefreshUsage: (accountId: string) => void;
  onRefreshDeepseekBalance: (accountId: string) => void;
  onDisableAccount: (accountId: string) => void;
  onUpdateTier: (accountId: string, tier: string, weeklyMsgLimit: number) => Promise<void>;
  onEnableAccount: (accountId: string) => void;
  refreshingUsageId: string | null;
  collisions: ReturnType<typeof unresolvedBacklogCollisions>;
  onFixCollision: (taskId: string) => void;
  fixingCollisionId: string | null;
  collisionFixError: string | null;
  escalations: EscalationView[];
  agents: AgentView[];
  scheduledJobRuns: ScheduledJobRunView[];
  onViewScheduledJobLog: (agentId: string) => void;
  scheduledDispatch: ScheduledDispatchStatusView | null;
  onToggleScheduledDispatch: (mode: string, paused: boolean) => void;
  // Track 3 (agent_orchestrator_mobile_and_worker_tmux_chat_2026_07_28): real
  // AgentsPanel/RoleChat wiring for the mobile "Agents" tab — mirrors the subset
  // of DesktopLayoutProps AgentsPanel itself needs, one-for-one.
  activeRole: AgentRole | null;
  setActiveRole: (r: AgentRole | null) => void;
  agentHistory: ChatMessage[];
  historyLoading: boolean;
  showArchived: boolean;
  setShowArchived: (v: boolean) => void;
  onSendToRole: (role: string, text: string) => Promise<void>;
  onPromoteAgent: (agentId: string, targetRole: AgentRole) => Promise<void>;
  onArchiveAgent: (agentId: string) => Promise<void>;
  onForceArchiveAgent: (agentId: string) => Promise<void>;
  onRestoreAgent: (agentId: string) => Promise<void>;
  onRequestDeleteAgent: (a: AgentView) => void;
  onRequestEditAgent: (a: AgentView) => void;
  onShowAgentLog: (a: AgentView) => void;
  onSwitchAccountAgent: (a: AgentView) => void;
  onSwitchModelAgent: (a: AgentView) => void;
  onSpawnAgent: () => void;
  // Track 2: BacklogSummary + BacklogDetailModal mobile wiring.
  onOpenBacklog: () => void;
  onOpenBlockerTask: (taskId: string) => void;
  onReloadBacklog: () => void;
  reloadingBacklog: boolean;
  parkedCount?: number;
  parkedNeedsHumanCount?: number;
  fleetFreeze: FleetFreezeStatusView | null;
}

function MobileTriage({
  state,
  summary,
  baseUrl,
  activity,
  doneActivity,
  activityFilter,
  onActivityFilter,
  onLoadOlderActivity,
  loadingOlderActivity,
  activityHasMore,
  typesAgents,
  typesShowFinished,
  setTypesShowFinished,
  accounts,
  roles,
  onAnswer,
  onAct,
  onTogglePrerequisite,
  onClearRateLimit,
  onReportUsage,
  onRefreshUsage,
  onRefreshDeepseekBalance,
  onDisableAccount,
  onUpdateTier,
  onEnableAccount,
  refreshingUsageId,
  collisions,
  onFixCollision,
  fixingCollisionId,
  collisionFixError,
  escalations,
  agents,
  scheduledJobRuns,
  onViewScheduledJobLog,
  scheduledDispatch,
  onToggleScheduledDispatch,
  activeRole,
  setActiveRole,
  agentHistory,
  historyLoading,
  showArchived,
  setShowArchived,
  onSendToRole,
  onPromoteAgent,
  onArchiveAgent,
  onForceArchiveAgent,
  onRestoreAgent,
  onRequestDeleteAgent,
  onRequestEditAgent,
  onShowAgentLog,
  onSwitchAccountAgent,
  onSwitchModelAgent,
  onSpawnAgent,
  onOpenBacklog,
  onOpenBlockerTask,
  onReloadBacklog,
  reloadingBacklog,
  parkedCount,
  parkedNeedsHumanCount,
  fleetFreeze,
}: MobileTriageProps) {
  const blockedCount = state.blocked_queue.filter((b) => !b.answered_at).length;
  const [tab, setTab] = useState<"alerts" | "fleet" | "agents" | "registry" | "activity">(
    blockedCount > 0 ? "alerts" : "fleet",
  );
  const typesOnline = typesAgents.filter((a) => a.online).length;
  // Fleet = worker slots only; main (slot 0) + review agents are in the Agents
  // tab, not the fleet (2026-06-27).
  const fleet = fleetSlots(state.slots);
  // Multi-host grouping (ao_v2_worker_runtime_and_liveness_2026_09_01.md Phase 5 [UI]
  // P1) — MUST be resolved against the FULL `state.slots`, never `fleet` above:
  // fleetSlots() strips the "main" (slot 0) row this needs to find its central host id
  // from. See SlotTable/SlotCards' own `centralHostId` prop docs.
  const centralHostId = resolveCentralHostId(state.slots);
  // R20 (ao_planning_box_profile_and_reserved_slots_first_2026_09_05.md): see the
  // desktop layout's identical comment on this same computation.
  const planningProfile = planningBoxProfile(state.slots, centralHostId);
  const doneBySlot = useMemo(() => latestDoneOutcomeBySlot(doneActivity), [doneActivity]);
  const critical = useMemo(
    () =>
      criticalHealth(
        accounts,
        agents,
        escalations,
        scheduledJobRuns,
        state.slots,
        state.backlog_summary,
        state.pm_clone,
      ),
    [accounts, agents, escalations, scheduledJobRuns, state.slots, state.backlog_summary, state.pm_clone],
  );

  return (
    <>
      <HealthStrip summary={summary} critical={critical} fleetFreeze={fleetFreeze} onOpenBacklog={onOpenBacklog} />
      {/* Track 2: backlog/done/queued stats — always visible above the tab bar
          (not gated behind a tab click), the same way desktop's top-row +
          KpiRow sit above its two-column layout regardless of anything else on
          the page. KpiRow mirrors DesktopLayout's OWN fleet.length>0 gate
          exactly (a KPI row of slot-status counts is meaningless with zero
          fleet slots) — this is parity with desktop, not a new mobile-only rule. */}
      <BacklogSummary
        backlog={state.backlog_summary}
        parkedCount={parkedCount}
        parkedNeedsHumanCount={parkedNeedsHumanCount}
        topBlockers={state.idle_fleet_blockers?.top_blockers}
        onOpenDetail={onOpenBacklog}
        onOpenBlockerTask={onOpenBlockerTask}
        onReloadYaml={onReloadBacklog}
        reloading={reloadingBacklog}
      />
      {fleet.length > 0 && <KpiRow summary={summary} backlog={state.backlog_summary} />}
      <div className="mobile-tabs">
        <button className={tab === "alerts" ? "active" : ""} onClick={() => setTab("alerts")}>
          Triage
          {blockedCount > 0 && <span className="count-pill alert">{blockedCount}</span>}
        </button>
        <button className={tab === "fleet" ? "active" : ""} onClick={() => setTab("fleet")}>
          Fleet
          <span className="count-pill">{fleet.length}</span>
        </button>
        <button className={tab === "agents" ? "active" : ""} onClick={() => setTab("agents")}>
          Agents
          <span className="count-pill">{typesOnline}</span>
        </button>
        <button className={tab === "registry" ? "active" : ""} onClick={() => setTab("registry")}>
          Registry
          <span className="count-pill">{roles.length}</span>
        </button>
        <button className={tab === "activity" ? "active" : ""} onClick={() => setTab("activity")}>
          Activity
        </button>
      </div>

      {tab === "alerts" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <BacklogIntegrityPanel
            collisions={collisions}
            onFix={onFixCollision}
            fixingId={fixingCollisionId}
            fixError={collisionFixError}
          />
          <BlockedPanel queue={state.blocked_queue} onAnswer={onAnswer} roles={roles} />
          <EscalationsPanel escalations={escalations} agents={agents} />
          <ScheduledJobsPanel runs={scheduledJobRuns} onViewLog={onViewScheduledJobLog} />
          <ScheduledDispatchPanel status={scheduledDispatch} onToggle={onToggleScheduledDispatch} />
          {summary.counts.stale > 0 && (
            <Panel title="Stale slots" count={summary.counts.stale} flush>
              <div className="slot-cards">
                {/* Bare `s.slot_id` here was a cross-host collision: `fleet` is
                    `fleetSlots(state.slots)`, and the server merges every node's slots into
                    that list (server/routes/state.py::_node_slots_for_state, each carrying its
                    real `host`), so two hosts can both run slot 7 — a bare key duplicates and
                    React reconciles the two cards as one. `slotKey(s)` is the composite
                    "<host>:<slot_id>" the corrected Fleet table already keys by
                    (ao_v2_worker_runtime_and_liveness_2026_09_01.md Phase 5). */}
                {fleet
                  .filter((s) => s.status === "stale")
                  .map((s) => (
                    <div key={slotKey(s)} className={`slot-card ${s.status}`}>
                      <div className="row-1">
                        <span className="id mono">#{s.slot_id}</span>
                        <span className="ago-cell stale" style={{ fontSize: 11.5 }}>
                          {fmtAgo(s.last_ping)}
                        </span>
                      </div>
                      <div style={{ fontSize: 12.5, color: "var(--fg-1)" }}>
                        {s.current_task && (
                          <span className="task-id mono" style={{ marginRight: 6 }}>
                            {s.current_task}
                          </span>
                        )}
                        {s.last_msg}
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button className="btn btn-sm btn-danger" onClick={() => onAct("reassign", s)}>
                          <Icon.Refresh /> Reassign
                        </button>
                        <button className="btn btn-sm" onClick={() => onAct("message", s)}>
                          <Icon.Send /> Message
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            </Panel>
          )}
          <PrerequisitesPanel prerequisites={state.prerequisites} onToggle={onTogglePrerequisite} />
        </div>
      )}

      {tab === "fleet" && (
        <>
          <PlanningBox slots={state.slots} />
          <Panel title="Fleet" count={fleet.length} flush tone="fleet">
            <SlotCards
              slots={fleet}
              onAct={onAct}
              doneBySlot={doneBySlot}
              centralHostId={centralHostId}
              profile={planningProfile}
              nodes={state.nodes ?? []}
            />
          </Panel>
          <div id="accounts">
            <AccountsPanel
              accounts={accounts}
              onClearRateLimit={onClearRateLimit}
              onReportUsage={onReportUsage}
              onRefreshUsage={onRefreshUsage}
              onRefreshDeepseekBalance={onRefreshDeepseekBalance}
              onDisableAccount={onDisableAccount}
              onUpdateTier={onUpdateTier}
              onEnableAccount={onEnableAccount}
              refreshingId={refreshingUsageId}
            />
          </div>
          <TaskUsageWindowsPanel baseUrl={baseUrl} />
          <BatchingEfficiencyPanel baseUrl={baseUrl} />
          <RoutingShadowComparisonPanel baseUrl={baseUrl} />
          <DeepSeekWalletPanel baseUrl={baseUrl} />
          <ClaudeWalletPanel baseUrl={baseUrl} />
          <GeminiCapacityPanel baseUrl={baseUrl} />
          <NvidiaCapacityPanel baseUrl={baseUrl} />
          <MuseCapacityPanel baseUrl={baseUrl} />
          <ConcurrencyCapsPanel baseUrl={baseUrl} />
          <ConcurrencyCapKnobsPanel baseUrl={baseUrl} />
          <FlatRateBoostPanel baseUrl={baseUrl} />
        </>
      )}

      {tab === "agents" && (
        <>
          {/* Track 3: the real chat surface (main/review/plan_reconciler),
              matching desktop's main-col ordering (AgentsPanel, then
              AgentTypesPanel) exactly — full parity, not a stripped-down mobile
              variant of the roster. */}
          <AgentsPanel
            agents={agents}
            slots={state.slots}
            activeRole={activeRole}
            setActiveRole={setActiveRole}
            history={agentHistory}
            historyLoading={historyLoading}
            showArchived={showArchived}
            setShowArchived={setShowArchived}
            onSendToRole={onSendToRole}
            onPromote={onPromoteAgent}
            onArchive={onArchiveAgent}
            onForceArchive={onForceArchiveAgent}
            onRestore={onRestoreAgent}
            onDelete={onRequestDeleteAgent}
            onEdit={onRequestEditAgent}
            onLog={onShowAgentLog}
            onSwitchAccount={onSwitchAccountAgent}
            onSwitchModel={onSwitchModelAgent}
            onSpawnAgent={onSpawnAgent}
          />
          <div id="agent-types">
            <AgentTypesPanel
              agents={typesAgents}
              showFinished={typesShowFinished}
              setShowFinished={setTypesShowFinished}
            />
          </div>
        </>
      )}

      {tab === "registry" && <RolesPanel roles={roles} />}

      {tab === "activity" && (
        <ActivityFeed
          activity={activity}
          filter={activityFilter}
          onFilterChange={onActivityFilter}
          onLoadOlder={onLoadOlderActivity}
          loadingOlder={loadingOlderActivity}
          hasMore={activityHasMore}
          serverStartedAt={state.server_started}
          centralHostId={resolveCentralHostId(state.slots)}
        />
      )}
    </>
  );
}

// ─── modals ──────────────────────────────────────────────────────────────

function ReassignModal({
  slot,
  centralHostId,
  onCancel,
  onConfirm,
}: {
  slot: SlotView | null;
  centralHostId: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!slot) return null;
  return (
    <Modal open onClose={onCancel} title={`Reassign task — ${slotLabel(slot, centralHostId)}`} danger>
      <div className="modal-body">
        Return <b style={{ color: "var(--fg-0)" }}>{slot.current_task}</b> to the queue? The next idle slot will pick it
        up on its <span className="mono">/boot</span> or <span className="mono">/done</span>. Slot{" "}
        <b style={{ color: "var(--fg-0)" }}>{slotLabel(slot, centralHostId)}</b> goes back to <b>idle</b>.
        <div className="mono" style={{ fontSize: 12 }}>
          POST /api/slots/{slot.slot_id}/reassign
        </div>
        <div style={{ color: "var(--fg-2)", fontSize: 12 }}>
          Use this when a slot is silent / stuck / wrong-track. Anything the worker had in flight will be lost unless it
          committed first.
        </div>
      </div>
      <div className="modal-foot">
        <button className="btn btn-sm" onClick={onCancel}>
          Cancel <span className="kbd">esc</span>
        </button>
        <button
          className="btn btn-sm btn-primary"
          style={{ background: "var(--status-stale)", borderColor: "var(--status-stale)" }}
          onClick={onConfirm}
        >
          Reassign {slotLabel(slot, centralHostId)}
        </button>
      </div>
    </Modal>
  );
}

// ao_manual_account_switch_2026_08_07: operator-emergency lever, reachable
// from a phone with no laptop/SSH access. `target` is pre-resolved by the
// caller (role="main" is a genuine singleton; review/worker are per-SLOT —
// there's no single "the" review agent — so the caller resolves a review
// AgentView down to its slot_id via tmux_session before opening this modal).
export type SwitchAccountTarget =
  { kind: "role"; role: string; label: string } | { kind: "slot"; slotId: number; label: string; host?: string | null };

function SwitchAccountModal({
  target,
  accounts,
  onCancel,
  onConfirm,
}: {
  target: SwitchAccountTarget | null;
  accounts: AccountView[];
  onCancel: () => void;
  onConfirm: (accountId: string) => void;
}) {
  const [accountId, setAccountId] = useState("");
  useEffect(() => {
    if (target && accounts.length > 0 && !accountId) setAccountId(accounts[0].account_id);
  }, [target, accounts, accountId]);
  if (!target) return null;
  const endpoint =
    target.kind === "role"
      ? `/api/agents/by-role/${target.role}/switch-account`
      : `/api/slots/${target.slotId}/switch-account`;
  return (
    <Modal open onClose={onCancel} title="Switch account" danger>
      <div className="modal-body">
        Force <b style={{ color: "var(--fg-0)" }}>{target.label}</b> onto a specific account — kills the current session
        and resumes it (context intact) on the new one, or freshly respawns if there's nothing to resume.
        <div className="spawn-field" style={{ marginTop: 10 }}>
          <span>Account</span>
          <select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
            {accounts.map((a) => (
              <option key={a.account_id} value={a.account_id}>
                {a.label} ({a.tier}
                {a.status !== "healthy" ? ` — ${a.status.replace("_", " ")}` : ""}
                {a.weekly_pct != null ? `, ${a.weekly_pct}% weekly` : ""})
              </option>
            ))}
          </select>
        </div>
        <div className="mono" style={{ fontSize: 12, marginTop: 10 }}>
          POST {endpoint}
        </div>
        <div style={{ color: "var(--fg-2)", fontSize: 12 }}>
          Not gated on the account's tracked status — you may know better than what's cached (a fix that hasn't
          propagated yet, a stale flag). Use when the automatic failover hasn't caught up.
        </div>
      </div>
      <div className="modal-foot">
        <button className="btn btn-sm" onClick={onCancel}>
          Cancel <span className="kbd">esc</span>
        </button>
        <button
          className="btn btn-sm btn-primary"
          style={{ background: "var(--status-stale)", borderColor: "var(--status-stale)" }}
          disabled={!accountId}
          onClick={() => onConfirm(accountId)}
        >
          Switch {target.label}
        </button>
      </div>
    </Modal>
  );
}

// The concrete snapshots server.model_tier.CONCRETE_MODELS accepts — kept in
// lockstep by hand (mirrors the Python constant; a mismatch just 400s at the
// API boundary rather than silently mis-resolving).
const MODEL_OPTIONS: { value: string; label: string }[] = [
  { value: "claude-opus-5", label: "Opus 5 — highest capability, most expensive" },
  { value: "claude-sonnet-5", label: "Sonnet 5 — the default for all sonnet-tier work" },
  { value: "claude-sonnet-4-6", label: "Sonnet 4.6 — light, explicit opt-in only" },
  { value: "claude-haiku-4-5", label: "Haiku 4.5 — cheapest, mechanical work" },
  { value: "claude-fable-5", label: "Fable 5" },
];

// Same target shape as SwitchAccountTarget, same role/slot resolution — see
// onSwitchModelAgent. Real switch: kills + resumes the live session on the new
// model (context intact); for role="main" it also durably rewrites
// agents/main.md so the choice survives every OTHER main respawn path, not
// just this one action (server.main_agent_keeper.switch_main_model).
export type SwitchModelTarget =
  { kind: "role"; role: string; label: string } | { kind: "slot"; slotId: number; label: string; host?: string | null };

function SwitchModelModal({
  target,
  onCancel,
  onConfirm,
}: {
  target: SwitchModelTarget | null;
  onCancel: () => void;
  onConfirm: (model: string) => void;
}) {
  const [model, setModel] = useState("");
  useEffect(() => {
    if (target && !model) setModel(MODEL_OPTIONS[0]!.value);
  }, [target, model]);
  if (!target) return null;
  const endpoint =
    target.kind === "role"
      ? `/api/agents/by-role/${target.role}/switch-model`
      : `/api/slots/${target.slotId}/switch-model`;
  return (
    <Modal open onClose={onCancel} title="Switch model" danger>
      <div className="modal-body">
        Switch <b style={{ color: "var(--fg-0)" }}>{target.label}</b> onto a specific model — kills the current session
        and resumes it (context intact) on the new one, or freshly respawns if there's nothing to resume. This is the
        REAL lever (unlike Edit's Model field, which is display-only).
        <div className="spawn-field" style={{ marginTop: 10 }}>
          <span>Model</span>
          <select value={model} onChange={(e) => setModel(e.target.value)}>
            {MODEL_OPTIONS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
        <div className="mono" style={{ fontSize: 12, marginTop: 10 }}>
          POST {endpoint}
        </div>
      </div>
      <div className="modal-foot">
        <button className="btn btn-sm" onClick={onCancel}>
          Cancel <span className="kbd">esc</span>
        </button>
        <button
          className="btn btn-sm btn-primary"
          style={{ background: "var(--status-stale)", borderColor: "var(--status-stale)" }}
          disabled={!model}
          onClick={() => onConfirm(model)}
        >
          Switch {target.label}
        </button>
      </div>
    </Modal>
  );
}

function SnapshotModal({ open, onCancel, onConfirm }: { open: boolean; onCancel: () => void; onConfirm: () => void }) {
  if (!open) return null;
  return (
    <Modal open onClose={onCancel} title="Take a snapshot">
      <div className="modal-body">
        Writes <span className="mono">state.json</span> to disk, uploads to GCS if configured, and creates a git commit.
        Idempotent — produces no commit if nothing changed.
        <div className="mono" style={{ fontSize: 12 }}>
          POST /api/snapshot?reason=manual
        </div>
      </div>
      <div className="modal-foot">
        <button className="btn btn-sm" onClick={onCancel}>
          Cancel
        </button>
        <button className="btn btn-sm btn-primary" onClick={onConfirm}>
          <Icon.Camera /> Snapshot now
        </button>
      </div>
    </Modal>
  );
}

/**
 * Confirm gate for the TopBar's fleet-wide "Pause All" (operator ask 2026-09-03,
 * ao_fleet_pause_dispatch_ui_button_2026_08_30.md). The button sits one click away
 * from Snapshot in the TopBar, but its blast radius is the entire fleet — it blocks
 * every slot's pick_next_task AND kills each live tmux session in one action — so a
 * mis-click is expensive: that doc's 2026-08-31 Progress Log measured boots 142→4
 * and dispatches 169→4 (a 97% collapse) from a single pause left armed 13.5h.
 *
 * Only the PAUSE direction is gated. Resume All stays a single click on purpose:
 * the same incident was a failure to RESUME, so adding friction to recovery would
 * make the failure mode worse, not better.
 */
function FleetPauseModal({
  open,
  liveSlots,
  totalSlots,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  liveSlots: number;
  totalSlots: number;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (!open) setReason("");
  }, [open]);
  if (!open) return null;
  return (
    <Modal open onClose={onCancel} title="Pause ALL fleet dispatch?" danger>
      <div className="modal-body">
        <p style={{ margin: "0 0 10px 0" }}>
          Blocks <b>every</b> slot fleet-wide from claiming new work and <b>kills each live session immediately</b>.
          In-flight work on {liveSlots} of {totalSlots} slot{totalSlots === 1 ? "" : "s"} with a live worker is
          interrupted, not drained.
        </p>
        <p style={{ color: "var(--fg-2)", fontSize: 12, margin: "0 0 10px 0" }}>
          The fleet stays dark until someone clicks <span className="mono">Resume All</span> — nothing un-pauses it on a
          timer. A pause left armed 13.5h on 2026-08-31 collapsed boots 142→4 and dispatches 169→4.
        </p>
        <label
          htmlFor="fleet-dispatch-pause-reason"
          style={{ display: "block", color: "var(--fg-2)", fontSize: 12, marginBottom: 4 }}
        >
          Reason — surfaced in the button&apos;s tooltip, so whoever finds the fleet paused knows why and who owns the
          resume.
        </label>
        <input
          id="fleet-dispatch-pause-reason"
          autoFocus
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="operator dashboard toggle"
          style={{ width: "100%" }}
        />
        <div className="mono" style={{ fontSize: 12, marginTop: 10, color: "var(--fg-2)" }}>
          POST /api/fleet/pause-dispatch
        </div>
      </div>
      <div className="modal-foot">
        <button className="btn btn-sm" onClick={onCancel}>
          Cancel
        </button>
        <button id="fleet-dispatch-pause-confirm" className="btn btn-sm btn-danger" onClick={() => onConfirm(reason)}>
          <Icon.Pause /> Pause all {totalSlots} slots
        </button>
      </div>
    </Modal>
  );
}

function MessageModal({
  slot,
  centralHostId,
  onCancel,
  onSend,
}: {
  slot: SlotView | null;
  centralHostId: string | null;
  onCancel: () => void;
  onSend: (slot: SlotView, text: string) => void;
}) {
  const [text, setText] = useState("");
  if (!slot) return null;
  const suggestions = [
    "wind down after current QG",
    "commit current diff and quit — fresh session will continue",
    "skip and grab next task",
  ];
  return (
    <Modal open onClose={onCancel} title={`Send message → ${slotLabel(slot, centralHostId)}`}>
      <div className="modal-body">
        <div style={{ color: "var(--fg-2)", fontSize: 12, marginBottom: 8 }}>
          The worker sees this on its next <span className="mono">/progress</span> call (≤5s).
        </div>
        <textarea
          rows={4}
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="e.g. context pressure high — commit, quit, fresh session"
        />
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
          {suggestions.map((s) => (
            <button key={s} className="btn btn-xs" onClick={() => setText(s)}>
              {s}
            </button>
          ))}
        </div>
      </div>
      <div className="modal-foot">
        <button className="btn btn-sm" onClick={onCancel}>
          Cancel
        </button>
        <button className="btn btn-sm btn-primary" disabled={!text.trim()} onClick={() => onSend(slot, text)}>
          <Icon.Send /> Send to {slotLabel(slot, centralHostId)}
        </button>
      </div>
    </Modal>
  );
}

function LoopIntervalModal({
  slot,
  centralHostId,
  onCancel,
  onSet,
}: {
  slot: SlotView | null;
  centralHostId: string | null;
  onCancel: () => void;
  onSet: (slot: SlotView, seconds: number) => void;
}) {
  const [seconds, setSeconds] = useState(60);
  if (!slot) return null;
  const presets = [60, 300, 900, 1800];
  const clamped = clampLoopSeconds(seconds);
  return (
    <Modal open onClose={onCancel} title={`Set /loop cadence → ${slotLabel(slot, centralHostId)}`}>
      <div className="modal-body">
        <div style={{ color: "var(--fg-2)", fontSize: 12, marginBottom: 8 }}>
          Re-issues the agent&apos;s <span className="mono">/loop</span> at the new cadence (live nudge — no respawn,
          keeps its task + context). Bounded to 30–3600s.
        </div>
        <input
          type="number"
          min={30}
          max={3600}
          autoFocus
          value={seconds}
          onChange={(e) => setSeconds(Number(e.target.value))}
        />
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
          {presets.map((p) => (
            <button key={p} className="btn btn-xs" onClick={() => setSeconds(p)}>
              {p}s
            </button>
          ))}
        </div>
      </div>
      <div className="modal-foot">
        <button className="btn btn-sm" onClick={onCancel}>
          Cancel
        </button>
        <button className="btn btn-sm btn-primary" onClick={() => onSet(slot, clamped)}>
          <Icon.Bolt /> Set {clamped}s
        </button>
      </div>
    </Modal>
  );
}

// ─── small helpers ───────────────────────────────────────────────────────

// ─── Spawn worker modal ──────────────────────────────────────────────────

interface SpawnModalProps {
  open: boolean;
  onCancel: () => void;
  slots: SlotView[];
  accounts: AccountView[];
  backendUrl: string;
  operator: string;
  // Worktree base of the backend this modal spawns against (from /api/mode).
  // null until /api/mode resolves. Used to build the per-slot worktree default
  // so the VM UI gets the VM's path, not the operator's laptop path.
  workspaceRoot: string | null;
}

function SpawnModal({ open, onCancel, slots, accounts, backendUrl, operator, workspaceRoot }: SpawnModalProps) {
  // Pick the next free integer slot id (skip occupied ones).
  const occupied = new Set(slots.map((s) => s.slot_id));
  let nextSlot = 2;
  while (occupied.has(nextSlot)) nextSlot++;

  const [slotId, setSlotId] = useState<number>(nextSlot);
  const [accountId, setAccountId] = useState<string>(
    accounts.find((a) => a.status === "healthy")?.account_id ?? accounts[0]?.account_id ?? "",
  );
  const [model, setModel] = useState<"sonnet" | "opus" | "haiku">("sonnet");
  const [effort, setEffort] = useState<"low" | "medium" | "high" | "xhigh" | "max">("high");
  const [thinkingOn, setThinkingOn] = useState<boolean>(false);
  // Working directory for the spawned claude subprocess. Auto-fills to the
  // per-slot worktree convention (`<workspace_root>/.tabs/<slot_id>/`) so
  // the worker lands in its own git worktree per the PM-repo convention.
  // Empty = "wherever the backend was started from" (only useful for
  // spawning a worker that operates on the orchestrator repo itself).
  // The workspace_root prefix comes from /api/mode for the backend we're
  // pointed at — so the VM UI gets the VM's path, not this laptop's. Stays
  // empty until /api/mode resolves; operator can edit per-spawn.
  const [cwd, setCwd] = useState<string>("");
  const [cwdEdited, setCwdEdited] = useState<boolean>(false);
  useEffect(() => {
    if (!cwdEdited) {
      // Default to the per-tab worktree convention, rooted at the backend's
      // own workspace root (matches server worktree_setup.slot_dir).
      setCwd(workspaceRoot ? `${workspaceRoot}/.tabs/${slotId}/` : "");
    }
  }, [slotId, cwdEdited, workspaceRoot]);

  // Server-rendered prompt (editable in the textarea before spawn). Fetched
  // from /api/spawn/preview whenever the inputs change. agents/worker.md is the
  // source of truth — operator can also click "Reload prompts" to re-read it.
  const [bootPrompt, setBootPrompt] = useState<string>("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [spawnBusy, setSpawnBusy] = useState(false);
  const [spawnResult, setSpawnResult] = useState<{ tmux_session: string; attach_hint: string } | null>(null);

  // Reset nextSlot whenever slots list changes.
  useEffect(() => {
    if (open) setSlotId(nextSlot);
  }, [open, nextSlot]);

  // Pre-pick the first available (or first overall) account when the accounts
  // list arrives. useState's initializer only fires once on mount; if accounts
  // hadn't loaded by then the boot prompt would render "<set me>" forever
  // unless the operator manually picked from the dropdown.
  useEffect(() => {
    if (!accountId && accounts.length > 0) {
      const fallback = accounts.find((a) => a.status === "healthy")?.account_id ?? accounts[0]!.account_id;
      setAccountId(fallback);
    }
  }, [accounts, accountId]);

  // Refetch the rendered preview whenever inputs change (or modal opens).
  // We don't auto-refetch when the textarea is dirty — operator edits would
  // be silently nuked otherwise.
  const fetchPreview = useCallback(async () => {
    if (!open) return;
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const res = await api.spawnPreview(backendUrl, {
        slot_id: slotId,
        server_url: backendUrl,
        account_id: accountId || undefined,
        operator,
        model,
        effort,
        thinking: thinkingOn ? "on" : "off",
      });
      setBootPrompt(res.prompt);
    } catch (e) {
      setPreviewError(e instanceof Error ? e.message : String(e));
    } finally {
      setPreviewLoading(false);
    }
  }, [open, slotId, accountId, operator, model, effort, thinkingOn, backendUrl]);

  useEffect(() => {
    void fetchPreview();
  }, [fetchPreview]);

  const reloadFromFile = async () => {
    try {
      await api.reloadPrompts(backendUrl);
      await fetchPreview();
    } catch (e) {
      setPreviewError(`Reload failed: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const spawnHere = async () => {
    setSpawnBusy(true);
    setPreviewError(null);
    try {
      const res = await api.spawnSlot(backendUrl, slotId, {
        account_id: accountId || undefined,
        boot_prompt: bootPrompt,
        cwd: cwd || undefined,
      });
      setSpawnResult({ tmux_session: res.tmux_session, attach_hint: res.attach_hint });
    } catch (e) {
      setPreviewError(`Spawn failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSpawnBusy(false);
    }
  };

  if (!open) return null;

  const copyToClipboard = (text: string) => {
    void navigator.clipboard?.writeText(text);
  };

  return (
    <Modal open onClose={onCancel} title="Spawn a new worker">
      <div className="modal-body spawn-modal-body">
        <div className="spawn-section">
          <h4>What to spawn</h4>
          <div className="spawn-fields-grid">
            <div className="spawn-field">
              <span>Slot ID</span>
              <input
                type="number"
                min={2}
                value={slotId}
                onChange={(e) => setSlotId(parseInt(e.target.value, 10) || nextSlot)}
              />
            </div>
            <div className="spawn-field">
              <span>Account</span>
              <select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
                {accounts.map((a) => (
                  <option key={a.account_id} value={a.account_id} disabled={a.status === "rate_limited"}>
                    {a.label} ({a.tier}
                    {a.status === "rate_limited" ? " — rate-limited" : ""})
                  </option>
                ))}
              </select>
            </div>
            <div className="spawn-field">
              <span>Model</span>
              <select value={model} onChange={(e) => setModel(e.target.value as "sonnet" | "opus" | "haiku")}>
                <option value="sonnet">sonnet</option>
                <option value="opus">opus</option>
                <option value="haiku">haiku</option>
              </select>
            </div>
            <div className="spawn-field">
              <span>Effort</span>
              <select
                value={effort}
                onChange={(e) => setEffort(e.target.value as typeof effort)}
                title="Maps to `claude --effort <level>`. Higher = more thinking budget, more cost."
              >
                <option value="low">low</option>
                <option value="medium">medium</option>
                <option value="high">high</option>
                <option value="xhigh">xhigh</option>
                <option value="max">max</option>
              </select>
            </div>
            <div className="spawn-field">
              <span>Thinking</span>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input type="checkbox" checked={thinkingOn} onChange={(e) => setThinkingOn(e.target.checked)} />
                <span style={{ fontSize: 12, color: "var(--fg-2)" }}>
                  {thinkingOn ? "on" : "off"} — extended thinking
                </span>
              </label>
            </div>
            <div className="spawn-field" style={{ gridColumn: "1 / -1" }}>
              <span>Worktree (cwd)</span>
              <input
                type="text"
                placeholder="leave empty to run in backend cwd (orchestrator/)"
                value={cwd}
                onChange={(e) => {
                  setCwd(e.target.value);
                  setCwdEdited(true);
                }}
              />
            </div>
          </div>
        </div>

        <div className="spawn-section">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <h4 style={{ margin: 0 }}>Boot prompt</h4>
            <button
              className="btn btn-xs btn-ghost"
              onClick={() => void reloadFromFile()}
              title="Re-read agents/worker.md from disk + refresh the textarea below"
            >
              ↻ Reload prompts
            </button>
          </div>
          <p style={{ marginTop: 0, marginBottom: 8, color: "var(--fg-2)", fontSize: 12 }}>
            Rendered from <span className="mono">agents/worker.md</span> server-side. Edit inline for one-off tweaks —
            the server pastes whatever's in this box. For a sticky change, edit the file and click <b>Reload prompts</b>
            .
          </p>
          <textarea
            className="spawn-cmd"
            style={{ width: "100%", minHeight: 260, fontFamily: "var(--font-mono)", fontSize: 11 }}
            value={previewLoading && !bootPrompt ? "Loading…" : bootPrompt}
            onChange={(e) => setBootPrompt(e.target.value)}
            spellCheck={false}
          />
          {previewError && (
            <div style={{ color: "var(--status-stale, #b91c1c)", fontSize: 12, marginTop: 6 }}>{previewError}</div>
          )}
          {spawnResult && (
            <div
              style={{
                background: "var(--bg-3)",
                padding: "8px 10px",
                borderRadius: 6,
                fontSize: 12,
                marginTop: 8,
              }}
            >
              ✓ Spawned tmux session <span className="mono">{spawnResult.tmux_session}</span>. Attach with:{" "}
              <span className="mono">{spawnResult.attach_hint}</span>{" "}
              <button
                className="btn btn-xs"
                onClick={() => copyToClipboard(spawnResult.attach_hint)}
                title="Copy attach command"
              >
                Copy
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="modal-foot">
        <button className="btn btn-sm" onClick={onCancel}>
          Close
        </button>
        <button
          className="btn btn-sm"
          onClick={() => copyToClipboard(bootPrompt)}
          disabled={!bootPrompt}
          title="Copy the prompt to paste into a Claude Code session manually"
        >
          Copy prompt
        </button>
        <button
          className="btn btn-sm btn-primary"
          onClick={() => void spawnHere()}
          disabled={spawnBusy || !bootPrompt}
          title="Spawn an interactive Claude session in a tmux pane on this machine and paste the prompt"
        >
          {spawnBusy ? "Spawning…" : "Spawn here →"}
        </button>
      </div>
    </Modal>
  );
}

// ─── Report usage modal ──────────────────────────────────────────────────

interface ReportUsageModalProps {
  accountId: string | null;
  accounts: AccountView[];
  onCancel: () => void;
  onSubmit: (accountId: string, weekly: number | null, fiveHour: number | null) => Promise<void>;
}

function ReportUsageModal({ accountId, accounts, onCancel, onSubmit }: ReportUsageModalProps) {
  const account = accounts.find((a) => a.account_id === accountId);
  const [weekly, setWeekly] = useState<string>("");
  const [fiveHour, setFiveHour] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (account) {
      setWeekly(String(account.weekly_msgs_used));
      setFiveHour(String(account.five_hour_msgs_used));
    }
  }, [account]);

  if (!accountId || !account) return null;

  const submit = async () => {
    setSubmitting(true);
    try {
      await onSubmit(
        accountId,
        weekly.trim() === "" ? null : parseInt(weekly, 10),
        fiveHour.trim() === "" ? null : parseInt(fiveHour, 10),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open onClose={onCancel} title={`Report usage — ${account.label}`}>
      <div className="modal-body">
        <p style={{ marginTop: 0, fontSize: 12, color: "var(--fg-2)" }}>
          Enter the current numbers from <span className="mono">/usage</span> in any Claude Code session running this
          account. Leaves blank values unchanged.
        </p>
        <div className="spawn-field">
          <span>Weekly used</span>
          <input
            type="number"
            min={0}
            max={account.weekly_msg_limit}
            value={weekly}
            onChange={(e) => setWeekly(e.target.value)}
          />
        </div>
        <div className="spawn-field">
          <span>5-hour used</span>
          <input type="number" min={0} value={fiveHour} onChange={(e) => setFiveHour(e.target.value)} />
        </div>
      </div>
      <div className="modal-foot">
        <button className="btn btn-sm" onClick={onCancel}>
          Cancel
        </button>
        <button className="btn btn-sm btn-primary" disabled={submitting} onClick={submit}>
          <Icon.Check /> {submitting ? "Reporting…" : "Save"}
        </button>
      </div>
    </Modal>
  );
}

// ─── Backlog detail modal ────────────────────────────────────────────────

interface BacklogDetailModalProps {
  open: boolean;
  onClose: () => void;
  baseUrl: string;
  /** ao_fleet_slot_state_reason_tooltips_2026_08_27.md Track H [UI] P2 — set when the
   * modal was opened from a top-blocker chip (Backlog header tooltip) rather than the
   * generic Detail button: forces the "all" filter (the target task may not be
   * "queued" any more by the time it's clicked) and expands + scrolls to that one
   * task's row, mirroring BacklogRelations.tsx's own jumpTo/bl-task-${id} convention. */
  initialTaskId?: string | null;
}

// "orphan" is a cross-cutting cut, not a status: it selects rows that exist in
// state.db with no backlog.yaml entry (undispatchable, awaiting the regen prune).
type BacklogFilter = "all" | "queued" | "dispatched" | "done" | "cancelled" | "orphan" | "done_failed";

// Sortable columns (2026-07-25 — operator ask: "sort by column plan name and
// time... which slot actually did the work"). Sorting is orthogonal to the
// filter tabs above — it applies to whatever the current filter produced.
type BacklogSortColumn =
  "id" | "plan" | "effort" | "status" | "queued_at" | "dispatched_at" | "done_at" | "queue_lag" | "duration";
type SortDir = "asc" | "desc";

// low<medium<high<xhigh<max (server/model_tier.py EFFORT_LADDER SSOT) — sort by
// rank, not alphabetically ("high" < "low" < "max" < "medium" < "xhigh" would be
// nonsense). Unset effort sorts as -1, i.e. before "low" ascending / last descending.
const EFFORT_SORT_RANK: Record<string, number> = { low: 0, medium: 1, high: 2, xhigh: 3, max: 4 };

function backlogSortValue(t: BacklogTaskView, col: BacklogSortColumn): string | number | null {
  switch (col) {
    case "id":
      return t.id;
    case "plan":
      return planBasename(t.plan_ref).toLowerCase();
    case "effort":
      return t.effort ? (EFFORT_SORT_RANK[t.effort] ?? -1) : null;
    case "status":
      return t.status;
    case "queued_at":
      return t.queued_at ? new Date(t.queued_at).getTime() : null;
    case "dispatched_at":
      return t.dispatched_at ? new Date(t.dispatched_at).getTime() : null;
    case "done_at":
      return t.done_at ? new Date(t.done_at).getTime() : null;
    // Queue lag / duration are DERIVED spans (not a raw column), so — unlike the
    // three point-in-time columns above — they need both endpoints present.
    // Mirrors fmtDurationBetween's blank rule: a still-queued row (no
    // dispatched_at) has no queue-lag value yet; a still-dispatched row (no
    // done_at) has no duration value yet. null sorts last per the `sorted`
    // useMemo below, same as the raw timestamp columns.
    case "queue_lag":
      return durationBetweenMs(t.queued_at, t.dispatched_at);
    case "duration":
      return durationBetweenMs(t.dispatched_at, t.done_at);
  }
}

interface SortableThProps {
  col: BacklogSortColumn;
  label: string;
  sortCol: BacklogSortColumn | null;
  sortDir: SortDir;
  onSort: (col: BacklogSortColumn) => void;
}

// A separate click target from the row (the row's own onClick still expands
// blockers/details) — clicking the header cell only ever sorts, never expands.
function SortableTh({ col, label, sortCol, sortDir, onSort }: SortableThProps) {
  const active = sortCol === col;
  return (
    <th
      style={{ padding: "6px 4px", cursor: "pointer", userSelect: "none" }}
      onClick={(e) => {
        e.stopPropagation();
        onSort(col);
      }}
      title={`Sort by ${label}`}
      data-testid={`backlog-sort-${col}`}
    >
      {label}
      <span style={{ marginLeft: 3, color: active ? "var(--fg-1)" : "var(--fg-3)", fontSize: 10 }}>
        {active ? (sortDir === "asc" ? "▲" : "▼") : "⇅"}
      </span>
    </th>
  );
}

/** "done failed" tab: lists rejected /done attempt EVENTS, not backlog tasks —
 * these never carry a backlog task status (a rejected attempt doesn't move a
 * task off "dispatched"), so this is a distinct, simpler render rather than
 * forcing the shape onto the main sortable task table. Reuses the SAME column
 * set as the "done" tab where a matching task_id is still in the currently-loaded
 * backlog (title/plan/tier-pri/effort) — falls back to a "not in current
 * backlog.yaml" placeholder for a task since pruned/archived. */
function DoneFailedTab({
  activity,
  tasks,
  loading,
}: {
  activity: ActivityEntry[];
  tasks: BacklogTaskView[];
  loading: boolean;
}) {
  const taskById = useMemo(() => new Map(tasks.map((t) => [t.id, t])), [tasks]);
  if (!loading && activity.length === 0) {
    return (
      <div style={{ color: "var(--fg-2)", fontSize: 13, padding: "20px 0" }}>
        No rejected /done attempts in the loaded window.
      </div>
    );
  }
  return (
    <table className="backlog-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
      <thead>
        <tr style={{ textAlign: "left", color: "var(--fg-2)", borderBottom: "1px solid var(--border)" }}>
          <th style={{ padding: "6px 4px" }}>ID</th>
          <th style={{ padding: "6px 4px" }}>Title</th>
          <th style={{ padding: "6px 4px" }}>Plan</th>
          <th style={{ padding: "6px 4px" }}>Tier · Pri</th>
          <th style={{ padding: "6px 4px" }}>Effort</th>
          <th style={{ padding: "6px 4px" }}>Slot</th>
          <th style={{ padding: "6px 4px" }}>Reason</th>
          <th style={{ padding: "6px 4px" }}>Ago</th>
        </tr>
      </thead>
      <tbody>
        {activity.map((a) => {
          const t = a.task_id ? taskById.get(a.task_id) : undefined;
          return (
            <tr key={a.id} data-testid="done-failed-row" style={{ borderBottom: "1px solid var(--border)" }}>
              <td className="mono" style={{ padding: "6px 4px" }}>
                {a.task_id ?? "—"}
              </td>
              <td style={{ padding: "6px 4px", color: t ? undefined : "var(--fg-3)" }}>
                {t?.title ?? "(task not in current backlog.yaml)"}
              </td>
              <td
                className="mono"
                style={{ padding: "6px 4px", color: "var(--fg-2)", fontSize: 11 }}
                title={t?.plan_ref || ""}
              >
                {t ? planBasename(t.plan_ref) : "—"}
              </td>
              <td className="mono" style={{ padding: "6px 4px", color: "var(--fg-2)" }}>
                {t ? `T${t.tier}·P${t.priority}` : "—"}
              </td>
              <td className="mono" style={{ padding: "6px 4px", color: "var(--fg-2)", fontSize: 11 }}>
                {t?.effort || "—"}
              </td>
              <td className="mono" style={{ padding: "6px 4px", color: "var(--fg-2)" }}>
                {a.slot_id != null ? `#${a.slot_id}` : "—"}
              </td>
              <td
                style={{ padding: "6px 4px", color: "var(--status-blocked)", fontSize: 11 }}
                title={activitySummary(a)}
              >
                {activityLabel(a.event_type).replace(/^done failed · /, "")}
              </td>
              <td className="mono" style={{ padding: "6px 4px", color: "var(--fg-2)" }} title={fmtClock(a.ts)}>
                {fmtAgo(a.ts)}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// The 4 real TaskRow statuses the server's `status=` query param already supports
// (get_backlog_view(status, tier)) -- fetching just one of these is a small, fast
// scoped response. The other 3 tabs (all/orphan/done_failed) need every row: "all"
// by definition, "orphan" cuts across statuses (a DB-vs-YAML property, not a status
// value the server can filter on), and "done_failed" cross-references activity rows
// against tasks of ANY status (DoneFailedTab's taskById lookup).
const SCOPABLE_BACKLOG_FILTERS = new Set<BacklogFilter>(["queued", "dispatched", "done", "cancelled"]);

// Backlog list virtualization (ao_control_plane_read_models_wave2_snapshot_routes_
// and_fixes_2026_09_04 [UI] P0, audit P7 "no cursor pagination/virtualization").
// Below BACKLOG_VIRTUALIZE_THRESHOLD rows, the table renders every row exactly as
// before (no windowing) -- every existing fixture (3-row e2e specs) never crosses
// this, so their DOM shape/assertions are unaffected. ROW_HEIGHT is a fixed
// estimate (the table's real rows all share the same `padding: "6px 4px"` /
// fontSize 12.5 chrome) used only to compute which slice of `sorted` is in view --
// approximate, not pixel-exact, which is fine for a windowing calc.
const BACKLOG_PAGE_SIZE = 200;
const BACKLOG_ROW_HEIGHT = 31;
const BACKLOG_VIRTUALIZE_THRESHOLD = 150;
const BACKLOG_OVERSCAN_ROWS = 20;
const BACKLOG_LOAD_MORE_THRESHOLD_PX = 400;

function BacklogDetailModal({ open, onClose, baseUrl, initialTaskId }: BacklogDetailModalProps) {
  const [tasks, setTasks] = useState<BacklogTaskView[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [counts, setCounts] = useState<BacklogCounts | null>(null);
  const [doneFailedActivity, setDoneFailedActivity] = useState<ActivityEntry[]>([]);
  const [filter, setFilter] = useState<BacklogFilter>("queued");
  // "Group by epic" mode (operator dispatch holds, plan ao_epic_hold_respawn_slo_alert_fixes_2026_10_05 § A).
  const [epicView, setEpicView] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [blockers, setBlockers] = useState<Record<string, string>>({});
  const [taskUsage, setTaskUsage] = useState<Record<string, TaskUsageView[] | "error">>({});
  const [sortCol, setSortCol] = useState<BacklogSortColumn | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  // Virtualization viewport tracking -- a dedicated scroll container wraps just the
  // <table> (below) so its own scrollTop maps directly onto row offsets, with no
  // need to subtract the filter-buttons/error-banner chrome above it.
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const scrollRafRef = useRef<number | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);

  const toggleSort = (col: BacklogSortColumn) => {
    if (sortCol === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
  };

  // Takes the target filter explicitly (never reads `filter` from closure) so the
  // filter buttons can fetch-and-switch in one call without `load` itself depending
  // on `filter` state -- that would make the [open, load] effect below re-fire (and
  // reset expandedId/blockers/taskUsage) on every tab click, not just on open.
  const load = useCallback(
    async (targetFilter: BacklogFilter) => {
      setLoading(true);
      setError(null);
      // Stale cursor guard: a scroll-triggered loadMore() in flight from the
      // PREVIOUS filter/page must not append onto the freshly-reset list once this
      // resolves -- clearing it synchronously (rather than waiting for the fetch
      // below) closes that window.
      setNextCursor(null);
      try {
        const scopedStatus = SCOPABLE_BACKLOG_FILTERS.has(targetFilter) ? targetFilter : undefined;
        // Fetched together (not per-row) so the retry-count/rejection-count badges
        // and the "done failed" tab are one bulk correlation, never an N+1 fetch.
        // `backlogCounts` is a SEPARATE call from `backlog` deliberately: the tab
        // badges must reflect counts across EVERY status regardless of which one
        // scopedStatus narrows `tasks` to (ao_backlog_route_p95_latency_2026_08_26).
        // First page only (BACKLOG_PAGE_SIZE), via the cursor-paginated variant --
        // loadMore() below fetches subsequent pages as the table scrolls, so a
        // large backlog never pays for a full unbounded list up front.
        const [page, countsData, failedActivity] = await Promise.all([
          api.backlogPage(baseUrl, { status: scopedStatus, limit: BACKLOG_PAGE_SIZE }),
          api.backlogCounts(baseUrl),
          api.activity(baseUrl, { types: DONE_FAILED_TYPES.join(","), limit: 500 }),
        ]);
        // page.items comes off the raw generated BacklogTaskView (affinity: string) --
        // narrowed back to the local SlotAffinity-typed alias, same as api.backlog()'s
        // own http<BacklogTaskView[]> return already does for the bare-list path.
        setTasks(page.items as BacklogTaskView[]);
        setNextCursor(page.next_cursor);
        setCounts(countsData);
        setDoneFailedActivity(failedActivity);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    },
    [baseUrl],
  );

  // Appends the next cursor page onto the already-loaded `tasks` -- called from
  // the table scroll container's onScroll handler below once it nears the bottom.
  // Guarded on `nextCursor`/`loadingMore`/`loading` so a fast scroll can't fire it
  // twice concurrently or race a fresh load() from a tab switch.
  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMore || loading) return;
    setLoadingMore(true);
    try {
      const scopedStatus = SCOPABLE_BACKLOG_FILTERS.has(filter) ? filter : undefined;
      const page = await api.backlogPage(baseUrl, {
        status: scopedStatus,
        cursor: nextCursor,
        limit: BACKLOG_PAGE_SIZE,
      });
      const items = page.items as BacklogTaskView[];
      setTasks((prev) => (prev ? [...prev, ...items] : items));
      setNextCursor(page.next_cursor);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingMore(false);
    }
  }, [baseUrl, filter, nextCursor, loadingMore, loading]);

  useEffect(() => {
    if (open) {
      // ao_fleet_slot_state_reason_tooltips_2026_08_27.md Track H [UI] P2: a
      // top-blocker chip forces "all" (the target task may no longer be "queued"
      // by the time it's clicked -- it's a snapshot from the last /api/state poll)
      // and pre-expands its row; the scroll-into-view itself happens in the
      // separate [open, initialTaskId, tasks] effect below once `tasks` has
      // actually loaded (open alone isn't enough -- the fetch is still in flight).
      load(initialTaskId ? "all" : filter);
      setExpandedId(initialTaskId ?? null);
      setBlockers({});
      setTaskUsage({});
    }
    // Deliberately NOT depending on `filter`/`initialTaskId`: this effect fires once
    // per genuine modal open (using whatever filter was last selected, or "all" when
    // a specific task was requested), not on every tab switch -- the filter buttons
    // below call load(f) directly for that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, load]);

  const reload = async () => {
    setReloading(true);
    try {
      await api.reloadBacklog(baseUrl);
      await load(filter);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setReloading(false);
    }
  };

  const fetchBlockers = async (taskId: string) => {
    if (blockers[taskId]) return;
    try {
      const r = await api.taskBlockers(baseUrl, taskId);
      setBlockers((prev) => ({ ...prev, [taskId]: r.explanation }));
    } catch (e) {
      setBlockers((prev) => ({ ...prev, [taskId]: e instanceof Error ? e.message : String(e) }));
    }
  };

  const fetchTaskUsage = async (taskId: string) => {
    if (taskUsage[taskId]) return;
    try {
      const rows = await api.taskUsage(baseUrl, taskId);
      setTaskUsage((prev) => ({ ...prev, [taskId]: rows }));
    } catch {
      setTaskUsage((prev) => ({ ...prev, [taskId]: "error" }));
    }
  };

  // Virtualization viewport measurement -- tracks the dedicated table-scroll
  // container's own height so the windowing calc below knows how many rows fit.
  // Re-measured whenever the container is (re)mounted (tab switch, loading
  // finishing) since ResizeObserver alone misses the very first mount.
  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    setViewportHeight(el.clientHeight);
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) setViewportHeight(entry.contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [open, filter, loading]);

  // Scroll handler for the table container: updates the virtualization window
  // (rAF-throttled -- a bare scrollTop state write per native scroll event would
  // otherwise re-render on every pixel) and, independently, fires loadMore() once
  // the user nears the bottom (BACKLOG_LOAD_MORE_THRESHOLD_PX) so the next cursor
  // page is fetched ahead of actually running out of rows.
  const handleTableScroll = useCallback(() => {
    if (scrollRafRef.current != null) return;
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null;
      const el = tableScrollRef.current;
      if (!el) return;
      setScrollTop(el.scrollTop);
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (distanceFromBottom < BACKLOG_LOAD_MORE_THRESHOLD_PX) void loadMore();
    });
  }, [loadMore]);

  // `tasks` is already server-scoped to `filter` for the 4 real statuses (load()'s
  // scopedStatus) and holds the FULL set for all/done_failed -- only "orphan" still
  // needs a client-side filter, since it cuts across statuses and the server has no
  // "orphan" status value to filter on.
  const filtered = useMemo(() => {
    if (!tasks) return [];
    if (filter === "orphan") return tasks.filter((t) => t.orphan);
    return tasks;
  }, [tasks, filter]);

  // Sorting is orthogonal to filtering: it re-orders whatever `filtered` already
  // produced, never changes which rows are shown. Nulls (never queued/dispatched/
  // done) always sort last regardless of direction, so toggling asc/desc doesn't
  // make blank timestamps jump to the top.
  const sorted = useMemo(() => {
    if (!sortCol) return filtered;
    const col = sortCol;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = backlogSortValue(a, col);
      const bv = backlogSortValue(b, col);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [filtered, sortCol, sortDir]);

  // ao_fleet_slot_state_reason_tooltips_2026_08_27.md Track H [UI] P2 — scrolls to +
  // primes (blockers/usage) the requested task's row once its data has actually
  // loaded (open alone isn't enough: `tasks` may still be mid-fetch the instant the
  // modal mounts). Mirrors BacklogRelations.tsx's own jumpTo (bl-task-${id} +
  // scrollIntoView) — the "existing task-id link convention" this todo cites.
  // Moved below `sorted` (rather than its original position right after the
  // load-triggering effect) so it can find the target's RENDERED index and, when
  // virtualization is active, scroll the window to include it before falling back
  // to scrollIntoView -- otherwise a jump-to target outside the initial window
  // would never be in the DOM for getElementById to find.
  useEffect(() => {
    if (!open || !initialTaskId || !tasks) return;
    const idx = sorted.findIndex((t) => t.id === initialTaskId);
    if (idx === -1) return; // not in this filter's result set (e.g. a stale/foreign id) — nothing to jump to
    const target = sorted[idx];
    if (target.status === "queued" || target.status === "dispatched") void fetchBlockers(target.id);
    if (target.status === "done") void fetchTaskUsage(target.id);
    const el = tableScrollRef.current;
    if (el && sorted.length > BACKLOG_VIRTUALIZE_THRESHOLD) {
      const desiredTop = Math.max(0, idx * BACKLOG_ROW_HEIGHT - el.clientHeight / 2);
      el.scrollTop = desiredTop;
      setScrollTop(desiredTop);
    }
    requestAnimationFrame(() =>
      document.getElementById(`backlog-row-${initialTaskId}`)?.scrollIntoView({ behavior: "smooth", block: "center" }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialTaskId, tasks, sorted]);

  // Tab-badge counts: server-provided (across EVERY status, independent of which
  // subset `tasks` currently holds) plus done_failed, which was never a backlog
  // status to begin with -- it comes from the activity log, same as before.
  const displayCounts = useMemo(
    () => ({
      all: counts?.all ?? 0,
      queued: counts?.queued ?? 0,
      queued_blocked: counts?.queued_blocked ?? 0, // subset of queued with a non-null blocked_reason
      dispatched: counts?.dispatched ?? 0,
      done: counts?.done ?? 0,
      cancelled: counts?.cancelled ?? 0,
      orphan: counts?.orphan ?? 0,
      done_failed: doneFailedActivity.length,
    }),
    [counts, doneFailedActivity],
  );

  // Retry/rejection count per task — one bulk correlation (see load()'s comment),
  // consumed by both the "done" tab's retry-count badge and the "dispatched" tab's
  // stuck-task rejection-count badge.
  const rejectedCountByTaskId = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of doneFailedActivity) {
      if (!a.task_id) continue;
      m.set(a.task_id, (m.get(a.task_id) ?? 0) + 1);
    }
    return m;
  }, [doneFailedActivity]);

  // Windowing calc: below BACKLOG_VIRTUALIZE_THRESHOLD rows (every existing
  // fixture) this is a no-op -- shouldVirtualize is false, so the full `sorted`
  // array renders exactly as before with zero spacer rows. Above it (the 5,000-task
  // fixture this todo's done-when targets), only the rows within the measured
  // viewport (+ overscan) actually mount; two spacer <tr>s (below) hold the
  // scrollbar's total height so scrolling still feels continuous.
  const shouldVirtualize = viewportHeight > 0 && sorted.length > BACKLOG_VIRTUALIZE_THRESHOLD;
  const backlogStartIndex = shouldVirtualize
    ? Math.max(0, Math.floor(scrollTop / BACKLOG_ROW_HEIGHT) - BACKLOG_OVERSCAN_ROWS)
    : 0;
  const backlogEndIndex = shouldVirtualize
    ? Math.min(
        sorted.length,
        backlogStartIndex + Math.ceil(viewportHeight / BACKLOG_ROW_HEIGHT) + BACKLOG_OVERSCAN_ROWS * 2,
      )
    : sorted.length;
  const backlogWindowRows = shouldVirtualize ? sorted.slice(backlogStartIndex, backlogEndIndex) : sorted;
  const backlogTopSpacerPx = shouldVirtualize ? backlogStartIndex * BACKLOG_ROW_HEIGHT : 0;
  const backlogBottomSpacerPx = shouldVirtualize ? (sorted.length - backlogEndIndex) * BACKLOG_ROW_HEIGHT : 0;

  if (!open) return null;

  return (
    <Modal open onClose={onClose} title="Backlog detail">
      <div className="modal-body" style={{ minWidth: "min(900px, 90vw)", maxHeight: "70vh", overflow: "auto" }}>
        <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
          {(["queued", "dispatched", "done", "done_failed", "cancelled", "orphan", "all"] as BacklogFilter[]).map(
            (f) => (
              <button
                key={f}
                className={`btn btn-xs ${filter === f ? "btn-primary" : ""}`}
                onClick={() => {
                  setFilter(f);
                  load(f);
                }}
                title={
                  f === "done_failed"
                    ? "done failed — rejected /done attempts, an event log, not a backlog task status"
                    : f === "queued"
                      ? "queued total · in parens: how many have a non-null Blocked reason right now " +
                        "(no eligible slot idle, or a real prereq/collision gate — see each row's own Blocked column)"
                      : ""
                }
              >
                {f === "done_failed" ? "rejected" : f}{" "}
                <span className="mono" style={{ marginLeft: 4, color: "var(--fg-3)" }}>
                  {displayCounts[f]}
                </span>
                {f === "queued" && displayCounts.queued_blocked > 0 && (
                  <span className="mono" style={{ marginLeft: 4, fontSize: 10, color: "var(--status-blocked)" }}>
                    ({displayCounts.queued_blocked} blocked)
                  </span>
                )}
              </button>
            ),
          )}
          <button
            className={`btn btn-xs ${epicView ? "btn-primary" : ""}`}
            data-testid="backlog-group-by-epic"
            aria-pressed={epicView}
            onClick={() => setEpicView((v) => !v)}
            title="Group the backlog by epic, with a per-epic dispatch Hold / Release toggle"
          >
            group by epic
          </button>
          <span style={{ flex: 1 }} />
          <button className="btn btn-xs" onClick={() => load(filter)} disabled={loading} title="Re-fetch from server">
            <Icon.Refresh /> {loading ? "Loading…" : "Refresh"}
          </button>
          <button
            className="btn btn-xs"
            onClick={reload}
            disabled={reloading}
            title="Re-read backlog.yaml from disk on the server"
          >
            <Icon.Bolt /> {reloading ? "Reloading…" : "Reload YAML"}
          </button>
        </div>

        {error && <div style={{ color: "var(--status-stale)", marginBottom: 10, fontSize: 12 }}>{error}</div>}
        {epicView ? (
          <React.Suspense fallback={null}>
            <EpicHoldView baseUrl={baseUrl} />
          </React.Suspense>
        ) : filter === "done_failed" ? (
          <DoneFailedTab activity={doneFailedActivity} tasks={tasks ?? []} loading={loading} />
        ) : (
          <>
            {!loading && filtered.length === 0 && (
              <div style={{ color: "var(--fg-2)", fontSize: 13, padding: "20px 0" }}>
                No tasks in <b>{filter}</b>.
              </div>
            )}

            <div
              ref={tableScrollRef}
              onScroll={handleTableScroll}
              data-testid="backlog-table-scroll"
              style={{ maxHeight: "50vh", overflow: "auto" }}
            >
              <table className="backlog-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                <thead>
                  <tr style={{ textAlign: "left", color: "var(--fg-2)", borderBottom: "1px solid var(--border)" }}>
                    <SortableTh col="id" label="ID" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                    <th style={{ padding: "6px 4px" }}>Title</th>
                    <SortableTh col="plan" label="Plan" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                    <th style={{ padding: "6px 4px" }}>Tier · Pri</th>
                    <th
                      style={{ padding: "6px 4px" }}
                      title="Roadmap milestone id (plans/milestones.yaml) — the main driver of Pri within a tier"
                    >
                      Milestone
                    </th>
                    <th
                      style={{ padding: "6px 4px" }}
                      title="Pipeline-layer band (docspec.LAYER) — secondary axis of Pri, under the milestone-bucket band"
                    >
                      Layer
                    </th>
                    <SortableTh col="effort" label="Effort" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                    <SortableTh col="status" label="Status" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                    <th
                      style={{ padding: "6px 4px" }}
                      title="Verbose why-not-dispatchable, from the same 9-filter table live dispatch enforces"
                    >
                      Blocked
                    </th>
                    {/* ao_v2_node_and_human_slot_recognition_wave2_routing_ui_and_
                        done_bar_2026_09_04 H4's second half: the incomplete-tasks
                        surface gets the same Host column the Fleet tab already has —
                        slot ids collide across hosts, so "#N" alone is ambiguous. */}
                    <th
                      style={{ padding: "6px 4px" }}
                      title="Host the task was dispatched to (TaskRow.dispatched_to_host) — 'central' = the control-plane host (NULL, the column's own convention); node hosts show their registered host label."
                    >
                      Host
                    </th>
                    <th style={{ padding: "6px 4px" }}>Slot</th>
                    <SortableTh
                      col="queued_at"
                      label="Queued"
                      sortCol={sortCol}
                      sortDir={sortDir}
                      onSort={toggleSort}
                    />
                    <SortableTh
                      col="dispatched_at"
                      label="Dispatched"
                      sortCol={sortCol}
                      sortDir={sortDir}
                      onSort={toggleSort}
                    />
                    <SortableTh col="done_at" label="Done" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                    <SortableTh
                      col="queue_lag"
                      label="Queue lag"
                      sortCol={sortCol}
                      sortDir={sortDir}
                      onSort={toggleSort}
                    />
                    <SortableTh
                      col="duration"
                      label="Duration"
                      sortCol={sortCol}
                      sortDir={sortDir}
                      onSort={toggleSort}
                    />
                    <th style={{ padding: "6px 4px" }}>Est</th>
                  </tr>
                </thead>
                <tbody>
                  {backlogTopSpacerPx > 0 && (
                    <tr aria-hidden="true" data-testid="backlog-row-spacer-top" style={{ height: backlogTopSpacerPx }}>
                      <td colSpan={14} style={{ padding: 0, border: "none" }} />
                    </tr>
                  )}
                  {backlogWindowRows.map((t) => {
                    const isOpen = expandedId === t.id;
                    return (
                      <Fragment key={t.id}>
                        <tr
                          id={`backlog-row-${t.id}`}
                          data-testid="backlog-row"
                          onClick={() => {
                            const next = isOpen ? null : t.id;
                            setExpandedId(next);
                            if (next && (t.status === "queued" || t.status === "dispatched")) {
                              fetchBlockers(t.id);
                            }
                            if (next && t.status === "done") {
                              fetchTaskUsage(t.id);
                            }
                          }}
                          style={{
                            cursor: "pointer",
                            borderBottom: "1px solid var(--border)",
                            background: isOpen ? "var(--bg-2)" : "transparent",
                          }}
                        >
                          <td className="mono" style={{ padding: "6px 4px" }}>
                            {t.id}
                            {t.orphan && (
                              <span
                                className="mono"
                                title="In state.db but not in backlog.yaml — the dispatcher cannot hand this to a worker. Queued orphans clear on the next regen prune; done orphans are audit history."
                                style={{
                                  marginLeft: 5,
                                  fontSize: 9.5,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.04em",
                                  padding: "1px 4px",
                                  borderRadius: 3,
                                  color: "var(--status-stale)",
                                  border: "1px solid var(--status-stale)",
                                }}
                              >
                                orphan
                              </span>
                            )}
                            {heldBadgeFromBlockedReason(t.blocked_reason) && (
                              <span
                                className="mono"
                                data-testid="backlog-held-badge"
                                title={t.blocked_reason ?? ""}
                                style={{
                                  marginLeft: 5,
                                  fontSize: 9.5,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.04em",
                                  padding: "1px 4px",
                                  borderRadius: 3,
                                  color: "var(--status-blocked)",
                                  border: "1px solid var(--status-blocked)",
                                }}
                              >
                                {heldBadgeFromBlockedReason(t.blocked_reason)}
                              </span>
                            )}
                          </td>
                          <td style={{ padding: "6px 4px", color: t.orphan ? "var(--fg-3)" : undefined }}>{t.title}</td>
                          <td
                            className="mono"
                            style={{ padding: "6px 4px", color: "var(--fg-2)", fontSize: 11 }}
                            title={t.plan_ref || "(no plan_ref)"}
                          >
                            {planBasename(t.plan_ref)}
                          </td>
                          <td className="mono" style={{ padding: "6px 4px", color: "var(--fg-2)" }}>
                            {/* Orphans carry sentinel tier/priority (no YAML row to read them from) —
                          showing "T99·P999" would read as real routing data. */}
                            {t.orphan ? "—" : `T${t.tier}·P${t.priority}`}
                          </td>
                          <td className="mono" style={{ padding: "6px 4px", color: "var(--fg-2)", fontSize: 11 }}>
                            {t.milestone || "—"}
                          </td>
                          <td className="mono" style={{ padding: "6px 4px", color: "var(--fg-2)", fontSize: 11 }}>
                            {t.layer || "—"}
                          </td>
                          <td
                            className="mono"
                            style={{ padding: "6px 4px", color: "var(--fg-2)", fontSize: 11 }}
                            title="Reasoning-effort tier (low<medium<high<xhigh<max) — NOT the same as the Slot column's affinity"
                          >
                            {t.effort || "—"}
                          </td>
                          <td style={{ padding: "6px 4px" }}>
                            <span
                              className="mono"
                              style={{
                                fontSize: 10.5,
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                                padding: "2px 5px",
                                borderRadius: 3,
                                color:
                                  t.status === "dispatched"
                                    ? "var(--status-working)"
                                    : t.status === "done"
                                      ? "var(--fg-2)"
                                      : t.status === "cancelled"
                                        ? "var(--status-stale)"
                                        : t.status === "skipped"
                                          ? "var(--status-paused)"
                                          : "var(--fg-1)",
                                background:
                                  t.status === "dispatched"
                                    ? "var(--status-working-bg)"
                                    : t.status === "done"
                                      ? "transparent"
                                      : t.status === "cancelled"
                                        ? "var(--status-stale-bg)"
                                        : t.status === "skipped"
                                          ? "var(--status-paused-bg)"
                                          : "var(--bg-1)",
                              }}
                            >
                              {t.status}
                            </span>
                            {(t.status === "done" || t.status === "dispatched") &&
                              (rejectedCountByTaskId.get(t.id) ?? 0) > 0 && (
                                <span
                                  className="mono"
                                  title={
                                    t.status === "done"
                                      ? "rejected /done attempts before this task's real completion"
                                      : "rejected /done attempts on this still-in-flight task — may be stuck bouncing"
                                  }
                                  style={{
                                    marginLeft: 5,
                                    fontSize: 10,
                                    color: t.status === "done" ? "var(--fg-2)" : "var(--status-blocked)",
                                  }}
                                >
                                  · {rejectedCountByTaskId.get(t.id)} rejected
                                </span>
                              )}
                          </td>
                          <td
                            style={{
                              padding: "6px 4px",
                              color: t.blocked_reason ? "var(--status-blocked)" : "var(--fg-3)",
                              maxWidth: 220,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: 11,
                            }}
                            title={t.blocked_reason || (t.status === "queued" ? "ready (no blockers)" : "")}
                          >
                            {t.blocked_reason || (t.status === "queued" ? "ready" : "—")}
                          </td>
                          <td
                            className="mono"
                            style={{ padding: "6px 4px", color: "var(--fg-2)" }}
                            title="Dispatch host — 'central' = the control-plane host (NULL); per-host slot id namespaces collide, so this is the only unambiguous half of the attribution"
                          >
                            {t.dispatched_to_host ?? "central"}
                          </td>
                          <td className="mono" style={{ padding: "6px 4px", color: "var(--fg-2)" }}>
                            {t.dispatched_to != null
                              ? `#${t.dispatched_to}`
                              : t.done_sha
                                ? t.done_sha.slice(0, 7)
                                : t.target_slot != null && t.affinity !== "none"
                                  ? (() => {
                                      const affColor =
                                        t.affinity === "high"
                                          ? "var(--status-blocked)"
                                          : t.affinity === "medium"
                                            ? "var(--status-paused)"
                                            : "var(--fg-2)";
                                      return (
                                        <span style={{ color: affColor }} title={`affinity=${t.affinity}`}>
                                          →#{t.target_slot} <span style={{ fontSize: 10 }}>({t.affinity})</span>
                                        </span>
                                      );
                                    })()
                                  : "—"}
                          </td>
                          <td
                            className="mono"
                            style={{ padding: "6px 4px", color: "var(--fg-2)" }}
                            title={fmtClock(t.queued_at)}
                          >
                            {fmtAgo(t.queued_at)}
                          </td>
                          <td
                            className="mono"
                            style={{ padding: "6px 4px", color: "var(--fg-2)" }}
                            title={fmtClock(t.dispatched_at)}
                          >
                            {fmtAgo(t.dispatched_at)}
                          </td>
                          <td
                            className="mono"
                            style={{ padding: "6px 4px", color: "var(--fg-2)" }}
                            title={fmtClock(t.done_at)}
                          >
                            {fmtAgo(t.done_at)}
                          </td>
                          <td
                            className="mono"
                            data-testid={`backlog-queue-lag-${t.id}`}
                            style={{ padding: "6px 4px", color: "var(--fg-2)" }}
                            title="Time between queued and dispatched"
                          >
                            {fmtDurationBetween(t.queued_at, t.dispatched_at)}
                          </td>
                          <td
                            className="mono"
                            data-testid={`backlog-duration-${t.id}`}
                            style={{ padding: "6px 4px", color: "var(--fg-2)" }}
                            title={
                              t.done_at
                                ? "Time between dispatched and done"
                                : "Still running — time since dispatched (updates live, not a final duration)"
                            }
                          >
                            {/* A dispatched-but-not-done row used to render blank here (done_at
                            missing) — exactly the case an operator most wants a duration for:
                            "has this been stuck too long?" (2026-08-29 operator ask). Falls back
                            to now() as the open end so it reads as a live, growing span instead
                            of a blank cell; the sort-value fn above deliberately keeps null for
                            this case so ongoing rows don't sort as if scored by a moving target. */}
                            {t.dispatched_at && !t.done_at
                              ? `${fmtDurationBetween(t.dispatched_at, new Date().toISOString())} (ongoing)`
                              : fmtDurationBetween(t.dispatched_at, t.done_at)}
                          </td>
                          <td className="mono" style={{ padding: "6px 4px", color: "var(--fg-2)" }}>
                            {t.est_hours}h
                          </td>
                        </tr>
                        {isOpen && (
                          <tr style={{ background: "var(--bg-2)" }}>
                            <td
                              colSpan={14}
                              style={{ padding: "10px 12px 14px 12px", borderBottom: "1px solid var(--border)" }}
                            >
                              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                                <div>
                                  <div style={{ fontSize: 11, color: "var(--fg-3)", marginBottom: 4 }}>BRIEF</div>
                                  <pre
                                    style={{
                                      margin: 0,
                                      fontFamily: "var(--font-mono)",
                                      fontSize: 12,
                                      whiteSpace: "pre-wrap",
                                      color: "var(--fg-1)",
                                    }}
                                  >
                                    {t.brief || "(no brief)"}
                                  </pre>
                                </div>
                                <div>
                                  <div style={{ fontSize: 11, color: "var(--fg-3)", marginBottom: 4 }}>DONE WHEN</div>
                                  <pre
                                    style={{
                                      margin: 0,
                                      fontFamily: "var(--font-mono)",
                                      fontSize: 12,
                                      whiteSpace: "pre-wrap",
                                      color: "var(--fg-1)",
                                    }}
                                  >
                                    {t.done_definition || "(none)"}
                                  </pre>
                                </div>
                              </div>
                              <div style={{ marginTop: 10, fontSize: 11.5, color: "var(--fg-2)" }}>
                                <span className="mono">plan_ref:</span> {t.plan_ref || "—"}
                                {t.repos.length > 0 && (
                                  <>
                                    {" "}
                                    · <span className="mono">repos:</span> {t.repos.join(", ")}
                                  </>
                                )}
                                {t.collision_group && (
                                  <>
                                    {" "}
                                    · <span className="mono">group:</span> {t.collision_group}
                                  </>
                                )}
                              </div>
                              {(t.status === "queued" || t.status === "dispatched") && (
                                <div style={{ marginTop: 8, fontSize: 11.5, color: "var(--fg-2)" }}>
                                  <span className="mono">why this rank:</span>{" "}
                                  {blockers[t.id] ?? <span style={{ color: "var(--fg-3)" }}>loading…</span>}
                                </div>
                              )}
                              {t.status === "done" && (
                                <div style={{ marginTop: 10 }} data-testid="task-usage-detail">
                                  <div style={{ fontSize: 11, color: "var(--fg-3)", marginBottom: 4 }}>TOKEN USAGE</div>
                                  {taskUsage[t.id] === undefined && (
                                    <div style={{ color: "var(--fg-3)", fontSize: 11.5 }}>loading…</div>
                                  )}
                                  {taskUsage[t.id] === "error" && (
                                    <div style={{ color: "var(--status-stale)", fontSize: 11.5 }}>
                                      Failed to load usage.
                                    </div>
                                  )}
                                  {Array.isArray(taskUsage[t.id]) &&
                                    (taskUsage[t.id] as TaskUsageView[]).length === 0 && (
                                      <div style={{ color: "var(--fg-3)", fontSize: 11.5 }}>
                                        No usable transcript usage for this task.
                                      </div>
                                    )}
                                  {Array.isArray(taskUsage[t.id]) &&
                                    (taskUsage[t.id] as TaskUsageView[]).length > 0 && (
                                      <table
                                        className="mono"
                                        style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}
                                      >
                                        <thead>
                                          <tr style={{ textAlign: "right", color: "var(--fg-3)" }}>
                                            <th style={{ padding: "2px 6px", textAlign: "left" }}>Attempt</th>
                                            <th style={{ padding: "2px 6px" }}>Turns</th>
                                            <th style={{ padding: "2px 6px" }}>Input</th>
                                            <th style={{ padding: "2px 6px" }}>Cache write</th>
                                            <th style={{ padding: "2px 6px" }}>Cache read</th>
                                            <th style={{ padding: "2px 6px" }}>Output</th>
                                            <th
                                              style={{ padding: "2px 6px" }}
                                              title="DeepSeek native-proxy reasoning-token breakdown — a subset WITHIN Output, not additive spend. — for a non-DeepSeek attempt or one that predates the native proxy."
                                            >
                                              Reasoning
                                            </th>
                                            <th style={{ padding: "2px 6px" }}>Total</th>
                                            <th style={{ padding: "2px 6px" }}>Spend</th>
                                            <th style={{ padding: "2px 6px", textAlign: "left" }}>
                                              Session (deep dive)
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {(taskUsage[t.id] as TaskUsageView[]).map((u, i) => (
                                            <tr key={`${u.claude_session_id ?? "attempt"}-${i}`}>
                                              <td style={{ padding: "2px 6px", color: "var(--fg-2)" }}>
                                                #{i + 1} · slot {u.slot_id}
                                                {u.host && ` · ${u.host}`}
                                                {u.provider && ` · ${u.provider}`}
                                                {u.account_id && ` · ${u.account_id}`}
                                                {u.model && ` · ${u.model}`}
                                              </td>
                                              <td
                                                style={{ padding: "2px 6px", textAlign: "right" }}
                                                data-testid="task-usage-turn-count"
                                              >
                                                {u.turn_count}
                                              </td>
                                              <td style={{ padding: "2px 6px", textAlign: "right" }}>
                                                {fmtTokenCount(u.input_tokens)}
                                              </td>
                                              <td style={{ padding: "2px 6px", textAlign: "right" }}>
                                                {fmtTokenCount(u.cache_creation_input_tokens)}
                                              </td>
                                              <td style={{ padding: "2px 6px", textAlign: "right" }}>
                                                {fmtTokenCount(u.cache_read_input_tokens)}
                                              </td>
                                              <td style={{ padding: "2px 6px", textAlign: "right" }}>
                                                {fmtTokenCount(u.output_tokens)}
                                              </td>
                                              <td style={{ padding: "2px 6px", textAlign: "right" }}>
                                                {fmtTokenCountOrDash(u.reasoning_tokens)}
                                              </td>
                                              <td
                                                style={{ padding: "2px 6px", textAlign: "right", color: "var(--fg-1)" }}
                                              >
                                                {fmtTokenCount(totalTokens(u))}
                                              </td>
                                              <td
                                                style={{ padding: "2px 6px", textAlign: "right" }}
                                                data-testid="task-usage-spend"
                                                title={
                                                  u.spend_usd == null && u.unmeasured_reason
                                                    ? u.unmeasured_reason
                                                    : undefined
                                                }
                                              >
                                                {/* ao_v2_fleet_visibility_and_reconciliation_2026_09_03 Phase 5 UI P2
                                            (~197): a null spend_usd used to render an undifferentiated dash
                                            regardless of WHY — the far more common cause (an unpriced provider,
                                            e.g. a plain Anthropic subscription) reads identically to a genuine
                                            measurement failure (a node-spool tombstone, an unresolvable
                                            transcript). Render the real reason, honestly, instead of a zero OR
                                            an unexplained dash, whenever the backend recorded one. */}
                                                {u.spend_usd == null && u.unmeasured_reason ? (
                                                  <span data-testid="task-usage-unmeasured-reason">unmeasured</span>
                                                ) : (
                                                  fmtUsdOrDash(u.spend_usd)
                                                )}
                                              </td>
                                              <td
                                                style={{ padding: "2px 6px", color: "var(--fg-2)" }}
                                                title={u.claude_session_id ?? undefined}
                                              >
                                                {u.claude_session_id ?? "—"}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    )}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                  {backlogBottomSpacerPx > 0 && (
                    <tr
                      aria-hidden="true"
                      data-testid="backlog-row-spacer-bottom"
                      style={{ height: backlogBottomSpacerPx }}
                    >
                      <td colSpan={14} style={{ padding: 0, border: "none" }} />
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {loadingMore && (
              <div style={{ padding: "8px 0", color: "var(--fg-2)", fontSize: 11.5, textAlign: "center" }}>
                Loading more…
              </div>
            )}
          </>
        )}
      </div>
      <div className="modal-foot">
        <button className="btn btn-sm" onClick={onClose}>
          Close
        </button>
      </div>
    </Modal>
  );
}

// ─── Parked tasks modal ──────────────────────────────────────────────────
//
// surface_parked_tasks_2026_07_25: an auto-parked task (a fleet-wide BLOCKED/
// PARKED/GATED decline past the skip threshold — server/auto_park.py) used to be
// invisible outside a direct API query. This is the health-strip "Review N parked
// tasks" quick action's detail panel — mirrors BacklogDetailModal's shape (a
// Panel-flavoured Modal, expand-on-click rows) rather than inventing a new pattern.

interface ParkedTasksModalProps {
  open: boolean;
  onClose: () => void;
  baseUrl: string;
}

function ParkedTasksModal({ open, onClose, baseUrl }: ParkedTasksModalProps) {
  const [tasks, setTasks] = useState<ParkedTaskView[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Per-row busy flag so a slow redispatch/mark-done disables only that row's
  // buttons, not the whole modal.
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.parkedTasks(baseUrl);
      setTasks(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [baseUrl]);

  useEffect(() => {
    if (open) {
      load();
      setExpandedId(null);
      setActionError(null);
    }
  }, [open, load]);

  const redispatch = async (taskId: string) => {
    setBusyId(taskId);
    setActionError(null);
    try {
      await api.redispatchParkedTask(baseUrl, taskId);
      await load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  const markDone = async (taskId: string) => {
    setBusyId(taskId);
    setActionError(null);
    try {
      await api.markParkedTaskDone(baseUrl, taskId);
      await load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  if (!open) return null;

  return (
    <Modal open onClose={onClose} title="Parked tasks">
      <div className="modal-body" style={{ minWidth: "min(760px, 90vw)", maxHeight: "70vh", overflow: "auto" }}>
        <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 12 }}>
          <span style={{ fontSize: 12, color: "var(--fg-2)" }}>
            Every task the fleet has declined (BLOCKED/PARKED/GATED) past the skip threshold — no worker will be
            dispatched to these until the blocker clears.
          </span>
          <span style={{ flex: 1 }} />
          <button className="btn btn-xs" onClick={load} disabled={loading} title="Re-fetch from server">
            <Icon.Refresh /> {loading ? "Loading…" : "Refresh"}
          </button>
        </div>

        {error && <div style={{ color: "var(--status-stale)", marginBottom: 10, fontSize: 12 }}>{error}</div>}
        {actionError && (
          <div style={{ color: "var(--status-stale)", marginBottom: 10, fontSize: 12 }}>{actionError}</div>
        )}

        {!loading && tasks && tasks.length === 0 && (
          <div style={{ color: "var(--fg-2)", fontSize: 13, padding: "20px 0" }}>Nothing is parked right now.</div>
        )}

        {(tasks ?? []).map((t) => {
          const isOpen = expandedId === t.task_id;
          const busy = busyId === t.task_id;
          return (
            <div
              key={t.task_id}
              data-testid="parked-row"
              style={{
                border: "1px solid var(--border)",
                borderRadius: 6,
                padding: "10px 12px",
                marginBottom: 8,
                background: isOpen ? "var(--bg-2)" : "transparent",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}
                onClick={() => setExpandedId(isOpen ? null : t.task_id)}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span className="mono" style={{ fontSize: 12 }}>
                      {t.task_id}
                    </span>
                    <span style={{ fontSize: 13 }}>{t.title}</span>
                    {t.likely_needs_human_action && (
                      <span
                        data-testid="human-action-badge"
                        title="This blocker's brief mentions a delete/VM-launch/credential/permission action — a human likely needs to go DO something before this can be resolved, not just click a button."
                        className="mono"
                        style={{
                          fontSize: 9.5,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                          padding: "1px 5px",
                          borderRadius: 3,
                          color: "var(--status-blocked)",
                          border: "1px solid var(--status-blocked)",
                        }}
                      >
                        needs human action
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11.5, color: "var(--fg-2)", marginTop: 3 }}>
                    <span className="mono">{t.reason_code}</span> · {t.skip_count} skips · parked{" "}
                    {fmtAgo(t.window_started_at)}
                    {t.plan_ref && (
                      <>
                        {" "}
                        · <span className="mono">{planBasename(t.plan_ref)}</span>
                      </>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                  <button
                    className="btn btn-xs"
                    disabled={busy}
                    onClick={() => redispatch(t.task_id)}
                    title="Clear the park and make this task dispatchable again — safe for any parked task; it may simply re-park if the blocker is still unresolved."
                  >
                    {busy ? "…" : "Dispatch now"}
                  </button>
                  {t.operator_gated && (
                    <button
                      className="btn btn-xs btn-danger"
                      disabled={busy}
                      onClick={() => {
                        if (window.confirm(`Mark ${t.task_id} done? Only do this if you already completed it by hand.`))
                          markDone(t.task_id);
                      }}
                      title="Operator-only: marks this task done directly WITHOUT dispatching a worker — use only if you already did the underlying action (e.g. ran the delete/launch by hand) yourself."
                    >
                      {busy ? "…" : "Mark done"}
                    </button>
                  )}
                </div>
              </div>
              {isOpen && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 11, color: "var(--fg-3)", marginBottom: 4 }}>BRIEF</div>
                  <pre
                    style={{
                      margin: 0,
                      fontFamily: "var(--font-mono)",
                      fontSize: 12,
                      whiteSpace: "pre-wrap",
                      color: "var(--fg-1)",
                    }}
                  >
                    {t.brief || "(no brief)"}
                  </pre>
                  <div style={{ marginTop: 8, fontSize: 11.5, color: "var(--fg-2)" }}>
                    <span className="mono">condition:</span> {t.condition}
                    {t.orphan && (
                      <>
                        {" "}
                        · <span style={{ color: "var(--status-stale)" }}>orphan — no longer in backlog.yaml</span>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="modal-foot">
        <button className="btn btn-sm" onClick={onClose}>
          Close
        </button>
      </div>
    </Modal>
  );
}

// ─── Spawn agent modal ───────────────────────────────────────────────────

interface SpawnAgentModalProps {
  open: boolean;
  onCancel: () => void;
  backendUrl: string;
  defaultMachine: string | null;
  accounts: AccountView[];
}

function SpawnAgentModal({ open, onCancel, backendUrl, defaultMachine, accounts }: SpawnAgentModalProps) {
  const [role, setRole] = useState<AgentRole>("main");
  const [label, setLabel] = useState("");
  const [machine, setMachine] = useState(defaultMachine ?? "");
  // Phase 4b-cleanup made account_id mandatory on the backend (every spawned
  // claude session must have a setup-token env file). Default to the first
  // available account; operator can override via the dropdown.
  const [accountId, setAccountId] = useState<string>(
    accounts.find((a) => a.status === "healthy")?.account_id ?? accounts[0]?.account_id ?? "",
  );
  const [model, setModel] = useState<string>("opus");
  const [effort, setEffort] = useState<string>("high");
  const [thinking, setThinking] = useState<boolean>(true);
  const [pollIntervalSeconds, setPollIntervalSeconds] = useState<number | null>(null);
  const [bootPrompt, setBootPrompt] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [spawnBusy, setSpawnBusy] = useState(false);
  const [spawnResult, setSpawnResult] = useState<{
    agent_id: string;
    tmux_session: string;
    attach_hint: string;
    rc_url: string | null;
    rc_url_warning: string | null;
  } | null>(null);

  useEffect(() => {
    setMachine(defaultMachine ?? "");
  }, [defaultMachine]);

  // Pre-pick an account when the accounts list lands after first render.
  // Mirrors SpawnModal's behaviour so the boot prompt renders with a real
  // account_id instead of "<set me>" and the Spawn click doesn't 400.
  useEffect(() => {
    if (!accountId && accounts.length > 0) {
      const fallback = accounts.find((a) => a.status === "healthy")?.account_id ?? accounts[0]!.account_id;
      setAccountId(fallback);
    }
  }, [accounts, accountId]);

  // Default model follows role convention: opus for main, sonnet for review/custom.
  // Thinking defaults to ON for main/review (decision-heavy) and custom — matches
  // Anthropic's tier-selection guidance.
  useEffect(() => {
    if (role === "main") {
      setModel("opus");
      setEffort("high");
      setThinking(true);
    } else if (role === "review") {
      setModel("sonnet");
      setEffort("high");
      setThinking(true);
    } else {
      setModel("sonnet");
      setEffort("high");
      setThinking(true);
    }
  }, [role]);

  const defaultLabel = label || `${role.charAt(0).toUpperCase()}${role.slice(1)} agent`;

  const fetchPreview = useCallback(async () => {
    if (!open) return;
    // custom doesn't map to a template file; reuse main as a starting point.
    const previewRole = role === "custom" ? "main" : role;
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const res = await api.spawnAgentPreview(backendUrl, {
        role: previewRole,
        server_url: backendUrl,
        machine: machine || undefined,
        model,
        effort,
        thinking: thinking ? "on" : "off",
      });
      setBootPrompt(res.prompt);
    } catch (e) {
      setPreviewError(e instanceof Error ? e.message : String(e));
    } finally {
      setPreviewLoading(false);
    }
  }, [open, role, machine, backendUrl, model, effort, thinking]);

  useEffect(() => {
    void fetchPreview();
  }, [fetchPreview]);

  const reloadFromFile = async () => {
    try {
      await api.reloadPrompts(backendUrl);
      await fetchPreview();
    } catch (e) {
      setPreviewError(`Reload failed: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const copy = () => {
    void navigator.clipboard?.writeText(bootPrompt);
  };

  const spawnHere = async () => {
    if (!accountId) {
      setPreviewError("Pick an account before spawning — every claude session needs a setup-token env file.");
      return;
    }
    setSpawnBusy(true);
    setPreviewError(null);
    try {
      const res = await api.spawnAgent(backendUrl, {
        role: role === "custom" ? "main" : role,
        label: defaultLabel,
        account_id: accountId,
        machine: machine || undefined,
        model,
        effort,
        thinking,
        poll_interval_seconds: pollIntervalSeconds ?? undefined,
        boot_prompt: bootPrompt,
      });
      setSpawnResult({
        agent_id: res.agent_id,
        tmux_session: res.tmux_session,
        attach_hint: res.attach_hint,
        rc_url: res.rc_url,
        rc_url_warning: res.rc_url_warning,
      });
    } catch (e) {
      setPreviewError(`Spawn failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSpawnBusy(false);
    }
  };

  if (!open) return null;

  return (
    <Modal open onClose={onCancel} title="Spawn agent">
      <div className="modal-body spawn-modal-body">
        <div className="spawn-section">
          <h4>What to spawn</h4>
          <div className="spawn-fields-grid">
            <div className="spawn-field">
              <span>Role</span>
              <select value={role} onChange={(e) => setRole(e.target.value as AgentRole)}>
                <option value="main">main — orchestration + chat</option>
                <option value="review">review — quality checks + chat</option>
                <option value="custom">custom — operator-defined (uses main.md)</option>
              </select>
            </div>
            <div className="spawn-field">
              <span>Label</span>
              <input type="text" placeholder={defaultLabel} value={label} onChange={(e) => setLabel(e.target.value)} />
            </div>
            <div className="spawn-field">
              <span>Account</span>
              <select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
                {accounts.length === 0 && <option value="">(no accounts loaded)</option>}
                {accounts.map((a) => (
                  <option key={a.account_id} value={a.account_id} disabled={a.status === "rate_limited"}>
                    {a.label} ({a.tier}
                    {a.status === "rate_limited" ? " — rate-limited" : ""})
                  </option>
                ))}
              </select>
            </div>
            <div className="spawn-field">
              <span>Machine</span>
              <input
                type="text"
                placeholder="hostname where the Claude session runs"
                value={machine}
                onChange={(e) => setMachine(e.target.value)}
              />
            </div>
            <div className="spawn-field">
              <span>Model</span>
              <select value={model} onChange={(e) => setModel(e.target.value)}>
                <option value="opus">opus</option>
                <option value="sonnet">sonnet</option>
                <option value="haiku">haiku</option>
              </select>
            </div>
            <div className="spawn-field">
              <span>Effort</span>
              <select value={effort} onChange={(e) => setEffort(e.target.value)}>
                <option value="low">low</option>
                <option value="medium">medium</option>
                <option value="high">high</option>
                <option value="xhigh">xhigh</option>
                <option value="max">max</option>
              </select>
            </div>
            <div className="spawn-field">
              <span>Thinking</span>
              <select value={thinking ? "on" : "off"} onChange={(e) => setThinking(e.target.value === "on")}>
                <option value="off">off</option>
                <option value="on">on (max thinking tokens)</option>
              </select>
            </div>
            <div className="spawn-field">
              <span>Poll interval (s)</span>
              <input
                type="number"
                min={1}
                max={3600}
                placeholder="template default"
                value={pollIntervalSeconds ?? ""}
                onChange={(e) => {
                  const v = e.target.value.trim();
                  setPollIntervalSeconds(v === "" ? null : Math.max(1, Math.min(3600, parseInt(v, 10) || 60)));
                }}
                title="Override the /loop poll cadence in the boot prompt. Leave blank to use the template's default (60s for main, 15 min for review)."
              />
            </div>
          </div>
          <p style={{ marginTop: 8, marginBottom: 0, color: "var(--fg-2)", fontSize: 12 }}>
            <b>Spawn here</b> runs <span className="mono">claude</span> in a tmux pane, pastes the boot prompt, and
            auto-captures the agent's <span className="mono">/remote-control</span> URL so the dashboard's{" "}
            <b>↗ claude.ai/code</b> link works from mobile without any extra steps. <b>Copy prompt</b> is the manual
            path: paste into a Claude session yourself, then attach the RC URL via the agent's <b>Edit</b> button.
          </p>
        </div>

        <div className="spawn-section">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <h4 style={{ margin: 0 }}>Boot prompt</h4>
            <button
              className="btn btn-xs btn-ghost"
              onClick={() => void reloadFromFile()}
              title={`Re-read agents/${role === "custom" ? "main" : role}.md from disk + refresh the textarea below`}
            >
              ↻ Reload prompts
            </button>
          </div>
          <p style={{ marginTop: 0, marginBottom: 8, color: "var(--fg-2)", fontSize: 12 }}>
            Rendered from <span className="mono">agents/{role === "custom" ? "main" : role}.md</span> server-side. Edit
            inline for one-off tweaks. For a sticky change, edit the file and click <b>Reload prompts</b>.
          </p>
          <textarea
            className="spawn-cmd"
            style={{ width: "100%", minHeight: 220, fontFamily: "var(--font-mono)", fontSize: 11 }}
            value={previewLoading && !bootPrompt ? "Loading…" : bootPrompt}
            onChange={(e) => setBootPrompt(e.target.value)}
            spellCheck={false}
          />
          {previewError && (
            <div style={{ color: "var(--status-stale, #b91c1c)", fontSize: 12, marginTop: 6 }}>{previewError}</div>
          )}
          {spawnResult && (
            <div
              style={{
                background: "var(--bg-3)",
                padding: "8px 10px",
                borderRadius: 6,
                fontSize: 12,
                marginTop: 8,
                lineHeight: 1.5,
              }}
            >
              ✓ Spawned agent <span className="mono">{spawnResult.agent_id}</span> in tmux session{" "}
              <span className="mono">{spawnResult.tmux_session}</span>.
              <br />
              {spawnResult.rc_url ? (
                <>
                  ✓ RC URL auto-captured: <span className="mono">{spawnResult.rc_url}</span>
                </>
              ) : (
                <span style={{ color: "var(--status-stale, #b91c1c)" }}>
                  ⚠{" "}
                  {spawnResult.rc_url_warning ??
                    "RC URL capture failed — attach + run /remote-control manually, then paste via Edit."}
                </span>
              )}
              <br />
              Attach: <span className="mono">{spawnResult.attach_hint}</span>{" "}
              <button className="btn btn-xs" onClick={() => copy()} title="Copy attach command">
                Copy
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="modal-foot">
        <button className="btn btn-sm" onClick={onCancel}>
          Close
        </button>
        <button className="btn btn-sm" onClick={copy} disabled={!bootPrompt}>
          Copy prompt
        </button>
        <button
          className="btn btn-sm btn-primary"
          onClick={() => void spawnHere()}
          disabled={spawnBusy || !bootPrompt}
          title="Spawn an interactive Claude session in tmux + auto-register + auto-capture RC URL"
        >
          {spawnBusy ? "Spawning…" : "Spawn here →"}
        </button>
      </div>
    </Modal>
  );
}

// ─── Log viewer modal (tmux capture-pane) ────────────────────────────────

// ─── Delete slot confirmation modal ──────────────────────────────────────

function DeleteSlotModal({
  slot,
  centralHostId,
  onCancel,
  onConfirm,
}: {
  slot: SlotView | null;
  centralHostId: string | null;
  onCancel: () => void;
  onConfirm: (slotId: number, host?: string | null) => Promise<void>;
}) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!slot) setTyped("");
  }, [slot]);
  if (!slot) return null;
  const blocked = slot.current_task != null;
  return (
    <Modal open onClose={onCancel} title={`Delete slot ${slotLabel(slot, centralHostId)}`} danger>
      <div className="modal-body">
        <p style={{ margin: "0 0 8px 0" }}>
          Removes slot <b style={{ color: "var(--fg-0)" }}>{slotLabel(slot, centralHostId)}</b> from the dashboard. If
          the slot has a tmux session (<span className="mono">{slot.tmux_session || "—"}</span>), it will also be
          killed.
        </p>
        {blocked && (
          <p style={{ color: "var(--status-stale)", fontSize: 12 }}>
            ⚠ Slot has current task <span className="mono">{slot.current_task}</span> — reassign first, then delete.
          </p>
        )}
        <p style={{ color: "var(--fg-2)", fontSize: 12 }}>
          Type{" "}
          <span className="mono">
            <b>delete</b>
          </span>{" "}
          to confirm.
        </p>
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder="delete"
          autoFocus
          disabled={busy || blocked}
        />
      </div>
      <div className="modal-foot">
        <button className="btn btn-sm" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          className="btn btn-sm btn-primary"
          style={{ background: "var(--status-stale)", borderColor: "var(--status-stale)" }}
          disabled={busy || blocked || typed !== "delete"}
          onClick={async () => {
            setBusy(true);
            try {
              await onConfirm(slot.slot_id, slot.host);
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? "Deleting…" : "Delete slot"}
        </button>
      </div>
    </Modal>
  );
}

// ─── Edit agent metadata modal ───────────────────────────────────────────

const MACHINE_OTHER = "__other__";

function EditAgentModal({
  agent,
  machineOptions,
  onCancel,
  onConfirm,
  onSwitchModel,
}: {
  agent: AgentView | null;
  machineOptions: string[];
  onCancel: () => void;
  onConfirm: (agentId: string, fields: { label?: string; rc_url?: string; machine?: string }) => Promise<void>;
  onSwitchModel: (agent: AgentView) => void;
}) {
  const [label, setLabel] = useState("");
  const [rcUrl, setRcUrl] = useState("");
  const [machine, setMachine] = useState("");
  const [machineIsOther, setMachineIsOther] = useState(false);
  const [busy, setBusy] = useState(false);
  // Reseed from the agent when the modal opens or the target agent changes — NOT on every poll: a re-render
  // that hands in a fresh `agent`/`machineOptions` object would otherwise reset "Other…" and wipe typed edits.
  const machineOptionsRef = useRef(machineOptions);
  machineOptionsRef.current = machineOptions;
  const agentId = agent?.agent_id;
  useEffect(() => {
    if (!agent) return;
    setLabel(agent.label ?? "");
    setRcUrl(agent.rc_url ?? "");
    const m = agent.machine ?? "";
    setMachine(m);
    setMachineIsOther(m !== "" && !machineOptionsRef.current.includes(m));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);
  if (!agent) return null;
  const submit = async () => {
    setBusy(true);
    try {
      await onConfirm(agent.agent_id, {
        label: label !== agent.label ? label : undefined,
        rc_url: rcUrl !== (agent.rc_url ?? "") ? rcUrl : undefined,
        machine: machine !== (agent.machine ?? "") ? machine : undefined,
      });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open onClose={onCancel} title={`Edit agent ${agent.agent_id}`}>
      <div className="modal-body">
        <p style={{ margin: "0 0 12px 0", color: "var(--fg-2)", fontSize: 12 }}>
          Update the agent metadata. The most common use is pasting the RC URL after running{" "}
          <span className="mono">/remote-control</span> in the agent's Claude session — once set, the dashboard's{" "}
          <b>↗ claude.ai/code</b> link opens that session directly (useful from mobile).
        </p>
        <div className="spawn-field">
          <span>Label</span>
          <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} disabled={busy} />
        </div>
        <div className="spawn-field">
          <span>RC URL</span>
          <input
            type="text"
            placeholder="https://claude.ai/code/session_..."
            value={rcUrl}
            onChange={(e) => setRcUrl(e.target.value)}
            disabled={busy}
          />
        </div>
        <div className="spawn-field">
          <span>Machine</span>
          <select
            value={machineIsOther ? MACHINE_OTHER : machine}
            onChange={(e) => {
              if (e.target.value === MACHINE_OTHER) {
                setMachineIsOther(true);
                setMachine("");
              } else {
                setMachineIsOther(false);
                setMachine(e.target.value);
              }
            }}
            disabled={busy}
          >
            <option value="" disabled>
              (select a machine)
            </option>
            {machineOptions.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
            <option value={MACHINE_OTHER}>Other…</option>
          </select>
          {machineIsOther && (
            <input
              type="text"
              placeholder="laptop hostname"
              value={machine}
              onChange={(e) => setMachine(e.target.value)}
              disabled={busy}
              style={{ marginTop: 6 }}
            />
          )}
        </div>
        <div className="spawn-field">
          <span>Model</span>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className="mono">{agent.model ?? "(unset)"}</span>
            <button className="btn btn-xs" type="button" onClick={() => onSwitchModel(agent)}>
              Switch model →
            </button>
          </div>
          <div style={{ color: "var(--fg-2)", fontSize: 12, marginTop: 4 }}>
            Display-only here — kills + resumes the live session, so it isn't a Save-button edit.
          </div>
        </div>
      </div>
      <div className="modal-foot">
        <button className="btn btn-sm" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button className="btn btn-sm btn-primary" onClick={() => void submit()} disabled={busy}>
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </Modal>
  );
}

// ─── Delete agent confirmation modal ─────────────────────────────────────

function DeleteAgentModal({
  agent,
  onCancel,
  onConfirm,
}: {
  agent: AgentView | null;
  onCancel: () => void;
  onConfirm: (agentId: string) => Promise<void>;
}) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!agent) setTyped("");
  }, [agent]);
  if (!agent) return null;
  return (
    <Modal open onClose={onCancel} title={`Delete agent ${agent.agent_id}`} danger>
      <div className="modal-body">
        <p style={{ margin: "0 0 8px 0" }}>
          Removes <b style={{ color: "var(--fg-0)" }}>{agent.label}</b> ({agent.agent_id}) from the dashboard.
          Conversation history for role <b>{agent.role}</b> is preserved (messages are keyed by role, not by agent).
        </p>
        <p style={{ color: "var(--fg-2)", fontSize: 12 }}>
          Type{" "}
          <span className="mono">
            <b>delete</b>
          </span>{" "}
          to confirm.
        </p>
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder="delete"
          autoFocus
          disabled={busy}
        />
      </div>
      <div className="modal-foot">
        <button className="btn btn-sm" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          className="btn btn-sm btn-primary"
          style={{ background: "var(--status-stale)", borderColor: "var(--status-stale)" }}
          disabled={busy || typed !== "delete"}
          onClick={async () => {
            setBusy(true);
            try {
              await onConfirm(agent.agent_id);
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? "Deleting…" : "Delete agent"}
        </button>
      </div>
    </Modal>
  );
}

// ─── Reset confirmation modal ────────────────────────────────────────────

function ResetModal({
  open,
  busy,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (typed: string) => void;
}) {
  const [typed, setTyped] = useState("");
  useEffect(() => {
    if (!open) setTyped("");
  }, [open]);
  if (!open) return null;
  return (
    <Modal open onClose={onCancel} title="Reset all state" danger>
      <div className="modal-body">
        <p style={{ margin: "0 0 10px 0" }}>
          Drops all SQLite tables (slots, tasks, blocked queue, prerequisites, accounts usage, compactions, activity
          log) and re-loads from <span className="mono">backlog.yaml</span>
          {" + "}
          <span className="mono">accounts.json</span>. Workers currently running will lose their assignments on next
          /boot.
        </p>
        <p style={{ color: "var(--fg-2)", fontSize: 12 }}>
          Type{" "}
          <span className="mono">
            <b>reset</b>
          </span>{" "}
          below to confirm.
        </p>
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder="reset"
          autoFocus
          disabled={busy}
        />
      </div>
      <div className="modal-foot">
        <button className="btn btn-sm" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          className="btn btn-sm btn-primary"
          style={{ background: "var(--status-stale)", borderColor: "var(--status-stale)" }}
          onClick={() => onConfirm(typed)}
          disabled={busy || typed !== "reset"}
        >
          {busy ? "Resetting…" : "Reset everything"}
        </button>
      </div>
    </Modal>
  );
}

/** Always-visible surface for an action failure (Pause/Resume account, clear-rate-limit,
 * pause/resume slot, etc.) — distinct from ErrorBanner (connection-loss only, gated on
 * `!connected`) so a 4xx/5xx on an otherwise-healthy connection actually shows something
 * instead of silently vanishing (deepseek_account_pause_button_silent_failure_2026_08_05).
 * Auto-dismisses after 8s so a stale error doesn't linger indefinitely; the close button
 * lets the operator dismiss it immediately once read. */
function ActionErrorToast({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 8000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-arm the 8s timer on every new message, not on onDismiss identity
  }, [message]);
  return (
    <div className="error-banner" style={{ borderColor: "var(--status-stale)" }}>
      <span className="icon">
        <Icon.Warn />
      </span>
      <div style={{ flex: 1 }}>
        <div className="title">Action failed</div>
        <div className="url">{message}</div>
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <button className="btn btn-sm" onClick={onDismiss}>
          Dismiss
        </button>
      </div>
    </div>
  );
}

function ErrorBanner({ backend, message, onRetry }: { backend: BackendConfig; message: string; onRetry: () => void }) {
  return (
    <div className="error-banner">
      <span className="icon">
        <Icon.Warn />
      </span>
      <div style={{ flex: 1 }}>
        <div className="title">
          Cannot reach <span style={{ color: "var(--status-stale)" }}>{backend.label}</span>
        </div>
        <div className="url">
          {backend.url} · {message}
        </div>
        <div style={{ color: "var(--fg-2)", fontSize: 12, marginTop: 6, lineHeight: 1.45 }}>
          Dashboard will keep retrying every {POLL_INTERVAL_LABEL}. Last known state shown below — may be stale.
        </div>
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <button className="btn btn-sm" onClick={onRetry}>
          <Icon.Refresh /> Retry
        </button>
      </div>
    </div>
  );
}

function LoadingState({ error, onRetry }: { error: string | null; onRetry: () => void }) {
  // dashboard_frontend_state_fetch_hang_2026_07_30: on a COLD load (no prior
  // successful fetch yet) the ordinary ErrorBanner never renders — its own
  // condition requires `state` to already be truthy — so a failed/timed-out first
  // fetch used to leave the operator staring at this generic placeholder forever
  // with zero indication anything had gone wrong, even though refresh() was
  // silently retrying underneath every POLL_INTERVAL_MS. Surface the same failure
  // here instead of only after the first success.
  if (error) {
    return (
      <Panel title="Loading…" flush>
        <Empty
          title="Could not load dashboard state"
          sub={
            <>
              <div>{error}</div>
              <button className="btn btn-sm" style={{ marginTop: 8 }} onClick={onRetry}>
                <Icon.Refresh /> Retry
              </button>
            </>
          }
        />
      </Panel>
    );
  }
  return (
    <Panel title="Loading…" flush>
      <Empty title="Fetching dashboard state" sub="If this hangs, the backend may be down — check the LivePill." />
    </Panel>
  );
}

function setErrorMsg(setter: (msg: string) => void) {
  return (e: unknown) => setter(e instanceof Error ? e.message : String(e));
}

// Re-export for the App.tsx so we don't need a separate file.
export type { AuthedUser };
